import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DndContext, DragOverlay, closestCenter, useSensor, useSensors, PointerSensor } from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove, horizontalListSortingStrategy, rectSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useParams, Link, useNavigate } from 'react-router-dom';
import ProductSwitcherModal from '../components/ProductSwitcherModal';
import ProductSettingsModal from '../components/ProductSettingsModal';
import PlaceAddModal from '../components/PlaceAddModal';
import DetailLinkEmailModal from '../components/DetailLinkEmailModal';
import { getCities, getManagers, getProductTypes, getRepresentativeImages, uploadAllSizeS3, registerUploadedImage, saveRepresentativeImage, saveRepresentativeImageOrder, deleteRepresentativeImage, getProductCity, getCommonImagesByCity, getSupplierCities, downloadCommonImagesToProduct, getProductGallery, getContentMainList, getItineraryType, getItineraryDetail, getSchedulePlaceImages, saveItineraryOrder, saveItineraryContent, deleteItinerarySchedule, addItinerarySchedule, deleteItineraryPlace, saveItineraryVisitKeywords, saveScheduleImageFromGallery, savePlaceImageFromGalleryToSchedule, saveScheduleSelectedImages, deleteScheduleImage, copyScheduleToDay, saveItineraryMeal, saveHotelItineraryEdits, saveProductContentInfo, createNewProductContent, deleteProductContent, getSupplierAirports, saveFlightItinerary, getContentImagesByContentNo, mapImagesToContent, deleteContentImageMapping, getLandboxSimpleSchedules, getLandboxScheduleDetailHtml, saveContentOrder, saveTourMeetingRemark, getTourMeetingSetting, getProductSelectOptions, updateProductSelectOption, deleteProductSelectOption, createProductSelectOption, getPriceStockCalendar, getCompanyPrimaryDomain, uploadContentFile, getContentInfo, getContentAttachedFiles } from '../services/apiService';
import { getUserInfo } from '../utils/tokenManager';
import { getProductDetails, saveProductBasicInfo } from '../services/productService';
import { openTravelProductPrintPreview } from '../utils/pdfExportUtil';

// Inline editor for a single meal item
function MealInlineEditor({ label, initialValue, onSave, onCancel }) {
  const [val, setVal] = useState(initialValue || '');
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border">
      <span className="font-semibold text-gray-800">{label}</span>
      <span className="mx-0.5 text-gray-300">:</span>
      <input
        type="text"
        className="w-36 md:w-44 border rounded px-2 py-0.5 text-[11px]"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onSave?.(val);
          if (e.key === 'Escape') onCancel?.();
        }}
        autoFocus
      />
      <button
        type="button"
        className="ml-1 px-2 py-0.5 text-[11px] rounded bg-blue-600 text-white hover:bg-blue-700"
        onClick={() => onSave?.(val)}
      >저장</button>
      <button
        type="button"
        className="px-2 py-0.5 text-[11px] rounded border bg-white hover:bg-gray-50"
        onClick={onCancel}
      >취소</button>
    </span>
  );
}

// 콘텐츠 카드용 Sortable 컴포넌트 (테이블 행 스타일)
function SortableContentCard({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 30 : 'auto',
  };
  return (
    <li
      ref={setNodeRef}
      style={style}
      className="group border-t border-gray-200 bg-white hover:bg-gray-50 first:border-t-0"
    >
      <div className="flex items-stretch">
        {/* Drag handle: only this area captures drag to avoid blocking clicks */}
        <span
          className="flex items-center justify-center w-8 px-1 text-gray-400 hover:text-gray-700 cursor-grab active:cursor-grabbing select-none border-r border-gray-100"
          title="드래그"
          {...listeners}
          {...attributes}
          onClick={(e) => e.stopPropagation()}
        >
          ☰
        </span>
        <div className="flex-1 px-3 py-2 overflow-hidden">
          {children}
        </div>
      </div>
    </li>
  );
}

// 키워드 칩을 dnd-kit Sortable로 구성
function SortableKeyword({ id, label, onClick, onDelete, disabled }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 'auto',
  };
  return (
    <span
      ref={setNodeRef}
      className="relative pl-2 pr-5 py-0.5 rounded border bg-white text-gray-800 hover:bg-gray-50 cursor-pointer"
      style={style}
      title="드래그하여 순서를 변경하거나 클릭하여 편집하세요"
      onClick={onClick}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (onClick) onClick();
        }
      }}
    >
      <span
        className="inline-flex items-center mr-1 text-gray-400 hover:text-gray-600 align-middle select-none cursor-grab active:cursor-grabbing"
        {...(!disabled ? listeners : {})}
        {...attributes}
        title="드래그"
        onClick={(e) => e.stopPropagation()}
      >
        ☰
      </span>
      {label}
      <button
        type="button"
        className="absolute right-0.5 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] text-gray-500 hover:text-red-600"
        title="삭제"
        onPointerDown={(e) => { e.stopPropagation(); }}
        onMouseDown={(e) => { e.stopPropagation(); }}
        onTouchStart={(e) => { e.stopPropagation(); }}
        onClick={(e) => { e.stopPropagation(); if (onDelete) onDelete(); }}
      >
        ✕
      </button>
    </span>
  );
}

// 대표이미지 타일용 Sortable 컴포넌트
function SortableRepItem({ id, index, img, onDelete, onMakeCover }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 40 : 'auto',
  };
  return (
    <li ref={setNodeRef} style={style} {...attributes} {...listeners} className="flex items-center justify-center select-none">
      <div className="group relative w-full pt-[56.25%] rounded-lg overflow-hidden border bg-gray-50 hover:shadow-md transition-shadow cursor-grab active:cursor-grabbing">
        {index === 0 && (
          <span className="absolute top-1 left-1 z-10 inline-flex items-center justify-center px-1.5 h-5 rounded bg-black/70 text-white text-[10px]">
            표지
          </span>
        )}
        <button
          type="button"
          className="absolute top-1 right-1 z-10 inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/90 text-gray-600 border border-gray-300 hover:text-gray-800 hover:bg-white shadow transition-colors opacity-0 group-hover:opacity-100"
          title="이 대표이미지 삭제"
          onPointerDown={(e) => { e.stopPropagation(); }}
          onMouseDown={(e) => { e.stopPropagation(); }}
          onTouchStart={(e) => { e.stopPropagation(); }}
          onClick={(e) => { e.stopPropagation(); if (onDelete) onDelete(e); }}
        >
          ✕
        </button>
        <img
          src={img.이미지경로}
          alt={img.이미지명 || '대표이미지'}
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
          draggable={false}
          referrerPolicy="no-referrer"
        />
     
      </div>
    </li>
  );
}

// --- Utility: 초성 추출 (Korean initial consonants) ---
const CHO_LIST = ['ㄱ', 'ㄲ', 'ㄴ', 'ㄷ', 'ㄸ', 'ㄹ', 'ㅁ', 'ㅂ', 'ㅃ', 'ㅅ', 'ㅆ', 'ㅇ', 'ㅈ', 'ㅉ', 'ㅊ', 'ㅋ', 'ㅌ', 'ㅍ', 'ㅎ'];
function getChosung(str) {
  if (!str) return '';
  let out = '';
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    // Hangul Syllables range
    if (ch >= 0xAC00 && ch <= 0xD7A3) {
      const base = ch - 0xAC00;
      const choIndex = Math.floor(base / (21 * 28));
      out += CHO_LIST[choIndex] || '';
    } else {
      const c = str[i];
      // keep Hangul consonants or alphanumerics for searching
      if (/^[ㄱ-ㅎa-z0-9]$/i.test(c)) out += c.toLowerCase();
    }
  }
  return out;
}

// --- AirportSelect: improved dropdown with 초성 검색 ---
function AirportSelect({ value, onChange, airports, placeholder = '검색해서 선택', loading = false }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);

  // Close when clicking outside
  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const list = Array.isArray(airports) ? airports : [];
  const q = (query || value || '').toString().trim().toLowerCase();
  const qChosung = getChosung((query || value || '').toString().replace(/\s+/g, ''));

  const filtered = useMemo(() => {
    if (!q && !qChosung) return list.slice(0, 100);
    return list.filter((a) => {
      const code = (a?.코드 || '').toString().toLowerCase();
      const name = (a?.코드명 || '').toString();
      const nameLower = name.toLowerCase();
      const nameChosung = getChosung(name.replace(/\s+/g, ''));
      return (
        code.includes(q) ||
        nameLower.includes(q) ||
        (!!qChosung && nameChosung.includes(qChosung))
      );
    }).slice(0, 200);
  }, [list, q, qChosung]);

  const handlePick = (a) => {
    const picked = a?.코드명 || '';
    onChange?.(picked);
    setQuery('');
    setOpen(false);
  };

  return (
    <div className="relative" ref={wrapRef}>
      <div className="flex items-center gap-1">
        <input
          type="text"
          className="w-full px-2 py-1 border rounded text-[12px] pr-7"
          placeholder={placeholder}
          value={value}
          onChange={(e) => { onChange?.(e.target.value); setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
        />
        <button
          type="button"
          className="absolute right-1 top-1/2 -translate-y-1/2 px-1.5 py-0.5 text-[11px] rounded border bg-white hover:bg-gray-50"
          onClick={() => setOpen((o) => !o)}
          tabIndex={-1}
          aria-label="드롭다운 열기"
        >▾</button>
      </div>
      {open && (
        <div className="absolute z-50 mt-1 w-full max-h-60 overflow-auto rounded border bg-white shadow-lg">
          {loading ? (
            <div className="px-2 py-2 text-[12px] text-gray-500">불러오는 중...</div>
          ) : filtered.length === 0 ? (
            <div className="px-2 py-2 text-[12px] text-gray-500">검색 결과 없음</div>
          ) : (
            <ul className="divide-y">
              {filtered.map((a, idx) => (
                <li key={idx}>
                  <button
                    type="button"
                    className="w-full text-left px-2 py-1.5 text-[12px] hover:bg-blue-50"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handlePick(a)}
                    title={a?.코드명 || ''}
                  >
                    <span className="text-blue-600 font-semibold">({a?.코드 || ''})</span>
                    <span className="ml-1">{a?.코드명 || ''}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// --- AirportDropdownNew: matches the new-schedule modal dropdown style ---
function AirportDropdownNew({ value, onChange, airports, loading = false, placeholder = '' }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);

  const list = Array.isArray(airports) ? airports : [];

  const filtered = useMemo(() => {
    const q = (query || '').trim();
    if (!q) return list.slice(0, 100);
    const qLower = q.toLowerCase();
    const qCho = getChosung(q);
    const scored = list.map((a) => {
      const code = String(a?.코드 || '').trim();
      const name = String(a?.코드명 || '').trim();
      const codeLower = code.toLowerCase();
      const nameLower = name.toLowerCase();
      const nameCho = getChosung(name);
      let score = -1e9;
      if (codeLower === qLower) score = 1200;
      else if (nameLower === qLower) score = 1100;
      else if (codeLower.startsWith(qLower)) score = 1000;
      else if (nameLower.startsWith(qLower)) score = 900;
      else if (codeLower.includes(qLower)) score = 800;
      else if (nameLower.includes(qLower)) score = 700;
      else if (nameCho === qCho) score = 650;
      else if (nameCho.startsWith(qCho)) score = 600;
      else if (nameCho.includes(qCho)) score = 550;
      return { a, score };
    })
      .filter(x => x.score > -1e9)
      .sort((x, y) => y.score - x.score || String(x.a?.코드명 || '').length - String(y.a?.코드명 || '').length)
      .map(x => x.a);
    return scored;
  }, [list, query]);

  const displayValue = query !== '' ? query : (value || '');

  return (
    <div className="relative">
      <input
        type="text"
        className="w-full px-2 py-1 pr-8 border rounded text-[12px]"
        value={displayValue}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onChange={(e) => {
          const v = e.target.value;
          setQuery(v);
          onChange?.(v);
        }}
        placeholder={placeholder}
      />
      {(displayValue) && (
        <button
          type="button"
          className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 px-1"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => { setQuery(''); onChange?.(''); }}
          aria-label="clear"
        >×</button>
      )}
      {open && (
        <div className="absolute z-20 mt-1 w-full max-h-56 overflow-auto border rounded bg-white shadow">
          {loading ? (
            <div className="px-2 py-2 text-xs text-gray-500">불러오는 중...</div>
          ) : (filtered.length > 0 ? (
            filtered.slice(0, 100).map((a) => (
              <div
                key={String(a.코드)}
                className="px-2 py-1 text-sm hover:bg-blue-50 cursor-pointer"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  const formatted = formatAirportOption(a);
                  setQuery(formatted);
                  onChange?.(formatted);
                  setOpen(false);
                }}
              >{String(a.코드명 || '')}</div>
            ))
          ) : (
            <div className="px-2 py-2 text-xs text-gray-500">검색 결과가 없습니다.</div>
          ))}
        </div>
      )}
    </div>
  );
}

const GeneralDetailPageSettings = () => {
  const { productId } = useParams();

  // 투어 전용 페이지 확인용 로그
const [companyDomain, setCompanyDomain] = useState('');
  const baseUrl = useMemo(() => `https://${companyDomain || 'total.tripbox.kr'}/detail`, [companyDomain]);
  const [previewUrl, setPreviewUrl] = useState('');
  const [detailLinkEmailOpen, setDetailLinkEmailOpen] = useState(false);
  // helper date formatters
  const pad2 = useCallback((n) => String(n).padStart(2, '0'), []);
  const formatYMD = useCallback((d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`, [pad2]);
  // Build preview URL from parts
  const buildPreviewUrl = useCallback((dateStr) => {
    const params = new URLSearchParams();
    params.set('productID', String(productId || ''));
    params.set('productType', '001002');
    params.set('defaultSearchDate', dateStr || '');
    params.set('defaultDays', '1');
    params.set('defaultPAdult', '1');
    params.set('defaultChildAges', '');
    params.set('UseCache', 'false');
    return `${baseUrl}?${params.toString()}`;
  }, [baseUrl, productId]);
  // Load representative domain
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const domain = await getCompanyPrimaryDomain({});
        if (alive) setCompanyDomain(domain || '');
      } catch (_) {
        if (alive) setCompanyDomain('');
      }
    })();
    return () => { alive = false; };
  }, []);
  // Find earliest available date with price and positive remaining stock (not closed)
  useEffect(() => {
    let alive = true;
    const run = async () => {
      try {
        if (!productId) { setPreviewUrl(buildPreviewUrl('')); return; }
        const { companyNo } = getUserInfo() || {};
        if (!companyNo) { setPreviewUrl(buildPreviewUrl('')); return; }
        const today = new Date();
        const todayDateOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        // search up to next 6 months including current
        let found = '';
        for (let m = 0; m < 6 && !found; m++) {
          const start = new Date(today.getFullYear(), today.getMonth() + m, 1);
          const end = new Date(today.getFullYear(), today.getMonth() + m + 1, 0);
          const resp = await getPriceStockCalendar({
            productTypeName: '투어',
            productNo: Number(productId),
            companyNo: String(companyNo),
            startDate: formatYMD(start),
            endDate: formatYMD(end),
            판매여부1: true,
            판매여부2: true,
            판매된리스트만보기: false,
            키워드: '',
            구분: '3',
            플랫폼수수료반영: false,
            결제수수료반영: false,
          });
          const rows = (resp && (resp.table || resp.table1 || resp.Table1 || resp.Table || resp.Table0 || resp.empt0 || [])) || [];
          // map of date string -> earliest that satisfies criteria
          const bestByDate = new Set();
          for (const r of rows) {
            const rawDate = r?.행사시작일시 || r?.일자 || r?.날짜 || r?.판매일 || r?.date || r?.Date;
            if (!rawDate) continue;
            const d = new Date(String(rawDate).slice(0, 10) + 'T00:00:00');
            if (Number.isNaN(d.getTime())) continue;
            // Only consider dates on or after the query date (today)
            if (d < todayDateOnly) continue;
            const key = formatYMD(d);
            // parse prices
            const totalPriceStr = r?.총판매가 || r?.TotalPrice || '';
            let priceAdult = '';
            if (totalPriceStr) {
              priceAdult = String(totalPriceStr).split('/')[0]?.trim() || '';
            } else {
              priceAdult = r?.판매가성인 ?? r?.판매가A ?? r?.AdultPrice ?? r?.priceAdult ?? '';
            }
            // parse stock
            const remainRaw = r?.잔여석 ?? r?.잔여 ?? r?.RemainingStock ?? '';
            const remain = Number(String(remainRaw).replace(/[^0-9.-]/g, ''));
            const closedRaw = (typeof r?.마감여부 !== 'undefined') ? r?.마감여부 : (typeof r?.Closed !== 'undefined' ? r?.Closed : undefined);
            const toBool = (x) => {
              if (typeof x === 'boolean') return x;
              if (x == null) return false;
              const num = Number(String(x).replace(/[^0-9.-]/g, ''));
              if (!Number.isNaN(num)) return num > 0;
              const s = String(x).trim().toLowerCase();
              return new Set(['true', 't', 'y', 'yes', '1', 'closed', 'close', 'done', '마감', '마감됨', '판매마감', '종료', '닫힘']).has(s);
            };
            const closed = toBool(closedRaw);
            // criteria: has price string and remain > 0 and not closed
            if (String(priceAdult || '').trim() && !Number.isNaN(remain) && remain > 0 && !closed) {
              bestByDate.add(key);
            }
          }
          if (bestByDate.size) {
            // choose minimum date
            found = Array.from(bestByDate).sort()[0];
          }
        }
        const dateStr = found || formatYMD(today);
        if (alive) setPreviewUrl(buildPreviewUrl(dateStr));
      } catch (_) {
        if (alive) setPreviewUrl(buildPreviewUrl(''));
      }
    };
    run();
    return () => { alive = false; };
  }, [productId, buildPreviewUrl, formatYMD]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const navigate = useNavigate();
  const [switcherOpen, setSwitcherOpen] = useState(false);
  // Ref to detect clicks outside the currently editing schedule item
  const editingContainerRef = useRef(null);
  // 방문지 키워드 편집 활성화 직후 발생하는 초기 blur 무시용 플래그
  const visitKwEditingJustActivatedRef = useRef(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [productName, setProductName] = useState('');
  const [productTypeCode, setProductTypeCode] = useState(''); // 예: 투어 038001, 호텔 038002
  const [tempScheduleSeq, setTempScheduleSeq] = useState(-1); // 임시 일정번호 시퀀스(-1, -2, ...)
  const [tempAnchors, setTempAnchors] = useState({}); // 임시 일정번호 -> 앵커(선택) 일정번호 매핑
  const [iframeKey, setIframeKey] = useState(0);
  const [repImages, setRepImages] = useState([]);
  const [repLoading, setRepLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // { current, total }
  const [savingOrder, setSavingOrder] = useState(false);
  const [repActiveId, setRepActiveId] = useState(null);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const fileInputRef = useRef(null);
  const addMenuRef = useRef(null);
  // 대표이미지 - 상품 갤러리 선택 모달 상태
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [galleryImgs, setGalleryImgs] = useState([]);
  const [gallerySelected, setGallerySelected] = useState([]);

  // 일정표 드래그 상태
  const [scheduleDrag, setScheduleDrag] = useState({ day: null, from: null, over: null });
  const [savingScheduleOrder, setSavingScheduleOrder] = useState(false);
  // 일정 인라인 편집 상태: { [scheduleNo]: { name: string, content: string, saving?: boolean } }
  const [editingSchedules, setEditingSchedules] = useState({});
  // 호텔 인라인 편집 상태: { [hotelKey]: { name: string, content: string, saving?: boolean } }
  const [editingHotels, setEditingHotels] = useState({});
  // 식사 편집 상태: { [dayNo]: { breakfast?: string, lunch?: string, dinner?: string, target?: 'breakfast'|'lunch'|'dinner'|null, saving?: boolean } }
  const [editingMealsByDay, setEditingMealsByDay] = useState({});

  // 콘텐츠 메인 리스트 상태
  const [contentMain, setContentMain] = useState([]);
  const [contentLoading, setContentLoading] = useState(false);
  const [contentError, setContentError] = useState('');
  const [contentReorderSaving, setContentReorderSaving] = useState(false);
  // 선택옵션 노출 상태
  const [selectOptions, setSelectOptions] = useState([]);
  const [selectOptionsLoading, setSelectOptionsLoading] = useState(false);
  const [selectOptionsError, setSelectOptionsError] = useState('');
  // 선택옵션 편집 모달 상태
  const [selectEditOpen, setSelectEditOpen] = useState(false);
  const [selectEditSaving, setSelectEditSaving] = useState(false);
  const [selectEditError, setSelectEditError] = useState('');
  const [selectEditForm, setSelectEditForm] = useState({
    serviceNo: null,
    name: '',
    desc: '',
    chargeModeCode: '066001', // 수량
    adultFeeStr: '',
    childFeeStr: '',
    infantFeeStr: '',
  });
  // dnd sensors to prevent accidental drags from simple clicks
  const contentDnDSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
  );
  // 콘텐츠 미리보기 모달 상태
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewModalTitle, setPreviewModalTitle] = useState('');
  const [previewModalTitleEdit, setPreviewModalTitleEdit] = useState('');
  const [previewModalHtml, setPreviewModalHtml] = useState('');
  const [previewModalContentNo, setPreviewModalContentNo] = useState(null);
  const [previewModalMode, setPreviewModalMode] = useState('edit'); // 'edit' | 'create'
  const [previewModalContentTypeCode, setPreviewModalContentTypeCode] = useState('');
  const titleInputRef = useRef(null);

  // 파일 첨부 관련 상태
  const [contentFileUploading, setContentFileUploading] = useState(false);
  const [contentFileDragOver, setContentFileDragOver] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  // 편집 모달 열기 (콘텐츠 정보 및 첨부파일 조회 포함)
  const openEditModal = async ({ contentNo, title, html, contentTypeCode }) => {
    setPreviewModalMode('edit');
    setPreviewModalContentTypeCode(String(contentTypeCode || ''));
    setPreviewModalTitle(String(title || '콘텐츠'));
    setPreviewModalTitleEdit(String(title || '콘텐츠'));
    setPreviewModalHtml(String(html || ''));
    setPreviewModalContentNo(Number.isFinite(contentNo) && contentNo > 0 ? contentNo : null);
    setUploadedFiles([]); // 초기화
    setPreviewModalOpen(true);

    // 콘텐츠번호가 있으면 콘텐츠 정보 조회하여 파일첨부여부 확인
    if (Number.isFinite(contentNo) && contentNo > 0) {
      try {
        const { companyNo, userNo } = getUserInfo() || {};
        const contentInfoRes = await getContentInfo({
          contentNo: Number(contentNo),
          productNo: productId,
          companyNo: companyNo || '1',
          userNo: userNo || '1',
        });
        const contentInfo = contentInfoRes?.table?.[0] || null;
        
        // 파일첨부여부가 true이면 첨부파일 목록 조회
        if (contentInfo?.파일첨부여부 === true) {
          const filesRes = await getContentAttachedFiles({ contentNo: Number(contentNo) });
          const files = filesRes?.table || [];
          if (files.length > 0) {
            setUploadedFiles(files.map(f => ({
              파일번호: f.파일번호,
              파일명: f.파일명,
              파일경로: f.파일경로,
            })));
          }
        }
      } catch (err) {
        console.error('콘텐츠 정보/첨부파일 조회 실패:', err);
      }
    }
  };

  // Landbox 일정표 가져오기 모달 상태
  const [landboxOpen, setLandboxOpen] = useState(false);
  const [landboxLoading, setLandboxLoading] = useState(false);
  const [landboxError, setLandboxError] = useState('');
  const [landboxList, setLandboxList] = useState([]);
  // 컬럼별 필터 상태 (상단 툴바 제거, 컬럼 필터만 사용)
  const [landboxColFilter, setLandboxColFilter] = useState({ num: '', name: '', price: '', city: '' });
  const filteredLandboxList = useMemo(() => {
    const normalize = (v) => (v == null ? '' : String(v)).toLowerCase();
    // Column filters only
    const numF = landboxColFilter.num.trim();
    const nameF = normalize(landboxColFilter.name);
    const priceF = normalize(landboxColFilter.price);
    const cityF = normalize(landboxColFilter.city);
    return (Array.isArray(landboxList) ? landboxList : []).filter((r) => {
      const nameOkCol = !nameF || normalize(r.일정명).includes(nameF);
      const numOkCol = !numF || String(r.일정번호).includes(numF);
      const priceOkCol = !priceF || normalize(r.상품가).includes(priceF);
      const cityOkCol = !cityF || normalize(r.방문도시).includes(cityF);
      return nameOkCol && numOkCol && priceOkCol && cityOkCol;
    });
  }, [landboxList, landboxColFilter]);

  const openLandboxModal = async () => {
    try {
      setLandboxOpen(true);
      setLandboxLoading(true);
      setLandboxError('');
      const list = await getLandboxSimpleSchedules({ favorite: 2 });
      setLandboxList(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
      setLandboxError('일정 목록을 불러오지 못했습니다.');
    } finally {
      setLandboxLoading(false);
    }
  };

  const handlePickLandboxSchedule = async (scheduleNo) => {
    try {
      setLandboxLoading(true);
      const row = await getLandboxScheduleDetailHtml({ scheduleNo, shared: 0 });
      const htmlRaw = String(row?.내용 || '');
      // HTML 파싱: body 안쪽만 사용 (body가 없으면 원본 사용)
      let parsed = htmlRaw;
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlRaw, 'text/html');
        parsed = doc?.body ? doc.body.innerHTML : htmlRaw;
      } catch { }
      // 랜드박스 전용 스타일/메타를 상단에 주입 (중복 방지 마커 포함)
      const LANDBOX_MARK = '/*__LANDBOX_STYLE_MARK__*/';
      const landboxHead = `
<style>${LANDBOX_MARK}
    @font-face {
        font-family: 'Material Icons';
        src: url('/LDItaoyNOAY6Uewc665JcIzCKsKc_M9flwmP.woff2') format('woff2');
    }
    .material-icons-round { font-family: 'Material Icons'; }
</style>
<style>
    /* MUI Container 및 테이블 width 강제 오버라이드 */
    * {
        max-width: 100% !important;
    }
    
    [class*="MuiContainer"] {
        width: 100% !important;
        max-width: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
        transform: none !important;
        left: auto !important;
        position: static !important;
    }
    
    table, .info-table, .__se_tbl {
        width: 100% !important;
        max-width: 100% !important;
        table-layout: auto !important;
        margin: 0 !important;
    }
    
    td, th {
        width: auto !important;
        max-width: none !important;
        word-break: break-word !important;
        overflow-wrap: break-word !important;
    }
    
    .product-info {
        width: 100% !important;
        max-width: 100% !important;
        overflow-x: visible !important;
    }
</style>
<meta charset="utf-8">
<meta content="width=device-width, initial-scale=1.0" name="viewport">
<title>일정표</title>
<link href="https://fonts.googleapis.com/icon?family=Material+Icons+Round"
    rel="stylesheet">
<meta content="복붙 한번이 만드는 변화, 랜드사와 여행사를 위한 스마트한 일정 공유 플랫폼 랜드박스"
    name="description">
<style>
    body {
        font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, 'Apple SD Gothic Neo', sans-serif;
        line-height: 1.6;
        color: #222222;
        background-color: #ffffff;
        overflow-x: hidden;
    }
        
    .material-icons-round {
        margin-top: 4px;
        font-size: 20px;
    }
        
    .detail-text {
        font-size: 14px;
    }
        
    /* 일정 섹션 스타일 */
    .day-section {
        margin-bottom: 30px;
        background: #fff;
        border-radius: 5px;
        overflow: hidden;
        border: 1px solid #cccccc;
    }
        
    .day-title {
        display: flex;
        justify-content: space-between;
        align-items: center;
        cursor: pointer;
        padding: 12px 18px;
        background: rgb(14, 37, 59);
        color: white;
        border-bottom: 1px solid #cccccc;
        transition: background-color 0.3s ease;
        font-size: 16px;
        font-weight: 600;
    }
        
    .day-content {
        padding: 18px;
        background: #fff;
        transition: all 0.3s ease;
    }
        
    .day-content.collapsed {
        display: none;
    }
        
    /* 아이콘 관련 스타일 */
    .toggle-icon {
        color: #000000;
        transition: transform 0.3s ease;
    }
        
    .toggle-icon.collapsed {
        transform: rotate(-180deg);
    }
        
    .icon {
        width: 24px;
        height: 24px;
        margin-right: 8px;
        flex-shrink: 0;
        background: #fff;
        z-index: 1;
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #000000;
    }
        
    /* 스케줄 아이템 스타일 */
    .schedule-item {
        margin: 15px 0;
        display: flex;
        align-items: flex-start;
        padding: 8px;
        border-radius: 8px;
        transition: background-color 0.3s ease;
        position: relative;
    }
        
    .schedule-item:hover {
        background-color: #f7f7f7;
    }
        
    .schedule-item:not(:last-child)::after {
        content: '';
        position: absolute;
        left: 18px;
        top: 24px;
        bottom: -15px;
        width: 1px;
        border-left: 1px solid #cccccc;
    }
        
    .location {
        font-size: 14px;
    }
        
    /* 식사 정보 스타일 */
    .meals {
        margin-top: 18px;
        padding: 8px 15px;
        background: #f7f7f7;
        border-radius: 8px;
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
    }
        
    .meal-info {
        display: flex;
        gap: 8px;
        white-space: nowrap;
        min-width: min-content;
    }
        
    .meal-item {
        background: #fff;
        padding: 4px 18px;
        border-radius: 15px;
        font-size: 13px;
        color: #222222;
        flex-shrink: 0;
        border: 1px solid #cccccc;
    }
        
    /* 정보 섹션 스타일 */
    .product-info {
        margin-bottom: 40px;
    }
        
    .info-section {
        background: #fff;
        border-radius: 12px;
        padding: 18px;
        font-size: 13px;
        margin-bottom: 18px;
        border: 1px solid #cccccc;
    }
        
    @media screen and (max-width: 768px) {
        body {
            padding: 0;
            margin: 0;
            max-width: 100%;
            overflow-x: hidden;
            background-color: white;
        }
        
        /* 일차별 일정 섹션 */
        .day-title {
            font-size: 14px;
            padding: 12px 15px;
            background-color: rgb(14, 37, 59) !important;
            color: white;
        }
        
        .day-section {
            padding: 0;
            border: none;
            margin-bottom: 18px;
        }
        
        .day-content {
            padding: 0;
        }
        
        .schedule-item {
            padding: 0;
        }
        
        .schedule-item:not(:last-child)::after {
            left: 8px;
        }
    
       
        .meals {
            margin-top: 15px;
            padding: 8px;
        }
        
        .meal-info {
            font-size: 12px;
            gap: 8px;
        }
        
        .meal-item {
            padding: 4px 8px;
            font-size: 11px;
        }
        
        /* 정보 섹션 모바일 최적화 */
        .info-section {
            padding: 12px;
            background-color: #fff;
            border: 1px solid #cccccc;
            margin-bottom: 12px;
        }
        
        /* 상세 텍스트 모바일 최적화 */
        .detail-text {
            font-size: 13px;
            line-height: 1.4;
            word-break: keep-all;
            overflow-wrap: break-word;
        }
        
        .location {
            font-size: 13px;
            font-weight: 600;
        }
        
        /* 아이콘 크기 조정 */
        .material-icons-round {
            font-size: 18px;
        }
        
        .icon {
            width: 20px;
            height: 20px;
            margin-right: 8px;
        }
        
        /* 테이블 가로 스크롤 방지 */
        .product-info {
            overflow-x: auto;
            margin-bottom: 18px;
        }
        
        /* 이미지 반응형 처리 */
        .product-info img {
            max-width: 100% !important;
            width: auto !important;
            height: auto !important;
        }
    }
</style>`;
      const combined = parsed.includes(LANDBOX_MARK) ? parsed : `${landboxHead}\n${parsed}`;
      setPreviewModalHtml(combined || '');
      setLandboxOpen(false);
    } catch (e) {
      console.error(e);
      alert('일정표를 불러오지 못했습니다.');
    } finally {
      setLandboxLoading(false);
    }
  };
  const [saveContentLoading, setSaveContentLoading] = useState(false);
  const [deleteContentLoading, setDeleteContentLoading] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  // 콘텐츠 상세 모달 - 컨텐츠 이미지 상태
  const [contentImages, setContentImages] = useState([]);
  const [contentImagesLoading, setContentImagesLoading] = useState(false);
  const [contentImagesError, setContentImagesError] = useState('');
  // 콘텐츠 이미지 추가 UI 상태
  const [contentAddMenuOpen, setContentAddMenuOpen] = useState(false);
  // 콘텐츠 이미지 연결 해제 진행중인 이미지번호
  const [contentUnlinkingId, setContentUnlinkingId] = useState(null);
  const contentAddMenuRef = useRef(null);
  const contentFileInputRef = useRef(null);
  // 공용 이미지 선택 모달 (컨텐츠용)
  const [contentCommonOpen, setContentCommonOpen] = useState(false);
  const [contentCommonLoading, setContentCommonLoading] = useState(false);
  const [contentCommonImgs, setContentCommonImgs] = useState([]);
  const [contentCommonSelected, setContentCommonSelected] = useState([]);
  const [contentCommonCityName, setContentCommonCityName] = useState('');
  // 갤러리 선택 모달 (컨텐츠용)
  const [contentGalleryOpen, setContentGalleryOpen] = useState(false);
  const [contentGalleryLoading, setContentGalleryLoading] = useState(false);
  const [contentGalleryImgs, setContentGalleryImgs] = useState([]);
  const [contentGallerySelected, setContentGallerySelected] = useState([]);
  // 미리보기 표시/숨김 상태
  const [showPreview, setShowPreview] = useState(true);
  // 미리보기 모드: 'pc' | 'mobile'
  const [previewMode, setPreviewMode] = useState('pc');
  // 디바이스 프리셋 및 사용자 지정 사이즈 상태
  const devicePresets = useMemo(() => ([
    { id: 'iphone-13', name: 'iPhone 13 (390 x 844)', w: 390, h: 844, notch: true, radius: 24 },
    { id: 'iphone-se', name: 'iPhone SE (375 x 667)', w: 375, h: 667, notch: false, radius: 20 },
    { id: 'pixel-7', name: 'Pixel 7 (412 x 915)', w: 412, h: 915, notch: true, radius: 24 },
    { id: 'galaxy-s22', name: 'Galaxy S22 (360 x 780)', w: 360, h: 780, notch: true, radius: 22 },
    { id: 'ipad-mini', name: 'iPad Mini (768 x 1024)', w: 768, h: 1024, notch: false, radius: 18 },
    { id: 'custom', name: '사용자 지정', w: 375, h: 700, notch: false, radius: 16 },
  ]), []);
  const [previewDeviceId, setPreviewDeviceId] = useState('iphone-13');
  const [customDevice, setCustomDevice] = useState({ width: 375, height: 700, notch: false, radius: 16 });
  // SmartEditor iframe refs/states
  const seIframeRef = useRef(null);
  const [seReady, setSeReady] = useState(false);
  const [convertingItinerary, setConvertingItinerary] = useState(false);

  // 장소 등록 모달 상태
  const [placeModalOpen, setPlaceModalOpen] = useState(false);
  const [placeModalScheduleNo, setPlaceModalScheduleNo] = useState(null);
  const GOOGLE_PLACES_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyAJZldkS8iQk8VD9vb4NXkHvshYk4B_Q70';

  // 이미지 설정 모달 상태
  const [imageStatusOpen, setImageStatusOpen] = useState(false);
  const [imageStatusScheduleNo, setImageStatusScheduleNo] = useState(null);
  const [imageStatusLoading, setImageStatusLoading] = useState(false);
  const [imageStatusPlaceGroups, setImageStatusPlaceGroups] = useState({}); // { 장소명: rows[] }
  const [imageStatusScheduleRows, setImageStatusScheduleRows] = useState([]); // table1 rows
  const [imageStatusSelected, setImageStatusSelected] = useState([]); // 이미지번호 배열
  const [imageStatusError, setImageStatusError] = useState('');
  const [imageStatusUploading, setImageStatusUploading] = useState(false);
  const [imageStatusUploadProgress, setImageStatusUploadProgress] = useState(null); // { current, total }
  const [imageStatusTargetPlace, setImageStatusTargetPlace] = useState({ placeName: '', placeNo: 0 });
  const [imageStatusSaving, setImageStatusSaving] = useState(false);
  const [imageStatusDeleting, setImageStatusDeleting] = useState(false);
  const imageStatusFileInputRef = useRef(null);

  // 이미지 설정 모달 - 서버 이미지 선택 상태
  const [imageStatusServerImgOpen, setImageStatusServerImgOpen] = useState(false);
  const [imageStatusServerCityNo, setImageStatusServerCityNo] = useState('');
  const [imageStatusServerCityName, setImageStatusServerCityName] = useState('');
  const [imageStatusAllCities, setImageStatusAllCities] = useState([]);
  const [imageStatusServerImgs, setImageStatusServerImgs] = useState([]);
  const [imageStatusServerLoading, setImageStatusServerLoading] = useState(false);
  const [imageStatusServerSelected, setImageStatusServerSelected] = useState([]);
  const [imageStatusServerCityQuery, setImageStatusServerCityQuery] = useState('');
  const [imageStatusServerCityOpen, setImageStatusServerCityOpen] = useState(false);

  // 이미지 업로드 메뉴 상태
  const [imageUploadMenuOpen, setImageUploadMenuOpen] = useState(false);
  const [imageUploadMenuPosition, setImageUploadMenuPosition] = useState({ x: 0, y: 0 });
  const [imageUploadMenuTarget, setImageUploadMenuTarget] = useState({ type: '', placeNo: 0, placeName: '' });

  // 이미지 업로드 메뉴 외부 클릭 감지를 위한 ref
  const imageUploadMenuRef = useRef(null);

  // 이미지 설정 모달 - 상품 갤러리 선택 상태
  const [imageStatusGalleryOpen, setImageStatusGalleryOpen] = useState(false);
  const [imageStatusGalleryLoading, setImageStatusGalleryLoading] = useState(false);
  const [imageStatusGalleryImgs, setImageStatusGalleryImgs] = useState([]);
  const [imageStatusGallerySelected, setImageStatusGallerySelected] = useState([]);

  // 이미지 업로드 메뉴 외부 클릭 감지 이벤트 핸들러
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (imageUploadMenuOpen && imageUploadMenuRef.current && !imageUploadMenuRef.current.contains(event.target)) {
        setImageUploadMenuOpen(false);
      }
    };

    // 이벤트 리스너 등록
    document.addEventListener('mousedown', handleClickOutside);

    // 컴포넌트 언마운트 시 이벤트 리스너 제거
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [imageUploadMenuOpen]);

  // SmartEditor postMessage bridge: listen for readiness and SE_GET_RESULT
  useEffect(() => {
    const onMessage = (e) => {
      const data = e?.data;
      if (!data || typeof data !== 'object') return;
      if (data.type === 'SE_READY') {
        setSeReady(true);
        return;
      }
      if (data.type === 'SE_GET_RESULT') {
        const resolver = seGetResolverRef.current;
        seGetResolverRef.current = null;
        if (resolver) resolver({ html: String(data.html || '') });
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // When editor becomes ready or html changes while modal is open, push initial content
  useEffect(() => {
    if (!previewModalOpen) return;
    if (!seReady) return;
    try {
      const w = seIframeRef.current?.contentWindow;
      if (w) {
        w.postMessage({ type: 'SE_SET', html: previewModalHtml || '' }, '*');
      }
    } catch { }
  }, [seReady, previewModalHtml, previewModalOpen]);

  // 콘텐츠 상세 모달 열림/컨텐츠번호 변경 시 컨텐츠 이미지 조회
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!previewModalOpen) return;
      if (!previewModalContentNo) {
        setContentImages([]);
        setContentImagesError('');
        return;
      }
      try {
        setContentImagesError('');
        setContentImagesLoading(true);
        const rows = await getContentImagesByContentNo({ contentNo: previewModalContentNo });
        if (cancelled) return;
        setContentImages(Array.isArray(rows) ? rows : []);
      } catch (err) {
        if (cancelled) return;
        console.error('컨텐츠 이미지 조회 실패', err);
        setContentImagesError('이미지를 불러오지 못했습니다.');
        setContentImages([]);
      } finally {
        if (!cancelled) setContentImagesLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [previewModalOpen, previewModalContentNo]);

  // Helper: 콘텐츠 이미지 새로고침
  const refreshContentImages = useCallback(async () => {
    if (!previewModalOpen || !previewModalContentNo) return;
    try {
      setContentImagesLoading(true);
      const rows = await getContentImagesByContentNo({ contentNo: previewModalContentNo });
      setContentImages(Array.isArray(rows) ? rows : []);
    } catch (e) {
      console.error(e);
    } finally {
      setContentImagesLoading(false);
    }
  }, [previewModalOpen, previewModalContentNo]);

  // 콘텐츠: 이미지 연결 해제 핸들러
  const handleUnlinkContentImage = useCallback(async (imageNo) => {
    if (!previewModalContentNo || !imageNo) return;
    const ok = window.confirm('이 이미지를 컨텐츠와의 연결에서 해제하시겠습니까?');
    if (!ok) return;
    try {
      setContentUnlinkingId(imageNo);
      const { userNo } = getUserInfo() || {};
      await deleteContentImageMapping({ contentNo: previewModalContentNo, userNo, imageNos: [imageNo] });
      await refreshContentImages();
    } catch (err) {
      console.error(err);
      alert('이미지 연결 해제에 실패했습니다.');
    } finally {
      setContentUnlinkingId(null);
    }
  }, [previewModalContentNo, refreshContentImages]);

  // 콘텐츠: 이미지 추가 메뉴 밖 클릭 닫기
  useEffect(() => {
    const onClick = (e) => {
      if (contentAddMenuOpen && contentAddMenuRef.current && !contentAddMenuRef.current.contains(e.target)) {
        setContentAddMenuOpen(false);
      }
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [contentAddMenuOpen]);

  // 콘텐츠: PC 업로드 선택 핸들러
  const handleContentUploadFromPC = useCallback(() => {
    contentFileInputRef.current?.click();
    setContentAddMenuOpen(false);
  }, []);

  // 콘텐츠: 컨텐츠번호가 없으면 생성한 뒤 이미지 추가 메뉴 토글
  const ensureContentExistsAndToggleAddMenu = useCallback(async () => {
    try {
      if (!previewModalContentNo) {
        const { companyNo, userNo } = getUserInfo() || {};
        const created = await createNewProductContent({
          productNo: productId,
          companyNo,
          userNo,
          contentTypeCode: previewModalContentTypeCode || '',
        });
        const newNo = created?.table?.[0]?.컨텐츠번호 || created?.contentNo || created?.Table?.[0]?.컨텐츠번호;
        if (newNo) {
          setPreviewModalContentNo(Number(newNo));
        } else {
          throw new Error('컨텐츠번호 생성 실패');
        }
      }
      setContentAddMenuOpen((v) => !v);
    } catch (e) {
      console.error('이미지 추가 메뉴 준비 실패', e);
      alert('컨텐츠를 생성하지 못했습니다. 잠시 후 다시 시도해주세요.');
    }
  }, [previewModalContentNo, productId, previewModalContentTypeCode]);

  // 콘텐츠: 파일 업로드 input 변경 처리
  const onContentFileChange = useCallback(async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length || !previewModalContentNo) return;
    try {
      setContentImagesLoading(true);
      const { companyNo, userNo } = getUserInfo() || {};
      const filePathBase = `${companyNo}/Product/${productId}/Content/${previewModalContentNo}/`;
      const newImageNos = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const fileName = file.name;
        const fileBase64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result || '';
            const base64 = String(result).includes(',') ? String(result).split(',')[1] : String(result);
            resolve(base64);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        await uploadAllSizeS3({ fileBase64, fileName, filePath: filePathBase });
        const imagePath = `${filePathBase}${fileName}`;
        const regRes = await registerUploadedImage({ companyNo, userNo, productNo: productId, fileName, imagePath });
        const imageNo = regRes?.table && regRes.table[0]?.이미지번호;
        if (imageNo) newImageNos.push(Number(imageNo));
      }
      if (newImageNos.length > 0) {
        await mapImagesToContent({ contentNo: previewModalContentNo, productNo: productId, userNo, imageNos: newImageNos });
        await refreshContentImages();
      }
    } catch (err) {
      console.error(err);
      alert('이미지 업로드에 실패했습니다.');
    } finally {
      setContentImagesLoading(false);
    }
  }, [previewModalContentNo, productId, refreshContentImages]);

  // 파일 첨부 핸들러
  const handleContentFileUpload = useCallback(async (files) => {
    if (!files || files.length === 0) return;
    
    // 컨텐츠번호가 없으면 먼저 생성
    let contentNo = previewModalContentNo;
    if (!contentNo) {
      try {
        const { companyNo, userNo } = getUserInfo() || {};
        const created = await createNewProductContent({
          productNo: productId,
          companyNo,
          userNo,
          contentTypeCode: previewModalContentTypeCode || '',
        });
        const newNo = created?.table?.[0]?.컨텐츠번호 || created?.contentNo || created?.Table?.[0]?.컨텐츠번호;
        if (newNo) {
          contentNo = Number(newNo);
          setPreviewModalContentNo(contentNo);
        } else {
          throw new Error('컨텐츠번호 생성 실패');
        }
      } catch (e) {
        console.error('컨텐츠 생성 실패', e);
        alert('컨텐츠를 생성하지 못했습니다. 잠시 후 다시 시도해주세요.');
        return;
      }
    }

    setContentFileUploading(true);
    const newUploadedFiles = [];
    
    for (const file of Array.from(files)) {
      try {
        console.log('[파일첨부] 파일 업로드 시작:', file.name);
        const result = await uploadContentFile({
          file,
          productNo: productId,
          contentNo,
        });
        console.log('[파일첨부] 업로드 결과:', result);
        
        if (result && !result.error) {
          // S3 업로드 결과에서 파일 경로 추출
          const s3Url = result.uploadResult?.url || result.uploadResult?.Url || result.filePath;
          newUploadedFiles.push({
            파일번호: Date.now(),
            파일명: result.fileName || file.name,
            파일경로: s3Url ? `https://kr.object.iwinv.kr/orgin/1/${result.filePath}` : '',
          });
          console.log('[파일첨부] 파일 추가됨:', result.fileName);
        } else {
          console.error('[파일첨부] 업로드 실패 결과:', result);
          alert(`파일 업로드 실패: ${file.name}`);
        }
      } catch (err) {
        console.error('파일 업로드 실패:', err);
        alert(`파일 업로드 실패: ${file.name}`);
      }
    }
    
    if (newUploadedFiles.length > 0) {
      setUploadedFiles(prev => [...prev, ...newUploadedFiles]);
    }
    
    setContentFileUploading(false);
  }, [previewModalContentNo, productId, previewModalContentTypeCode]);

  const onContentFileInputChange = useCallback((e) => {
    console.log('[파일첨부] onContentFileInputChange 호출됨, e.target.files:', e.target.files);
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length > 0) {
      handleContentFileUpload(files);
    }
  }, [handleContentFileUpload]);

  const handleContentFileDrop = useCallback((e) => {
    console.log('[파일첨부] handleContentFileDrop 호출됨');
    e.preventDefault();
    e.stopPropagation();
    setContentFileDragOver(false);
    const files = Array.from(e.dataTransfer?.files || []);
    console.log('[파일첨부] 드롭된 파일:', files);
    if (files.length > 0) {
      handleContentFileUpload(files);
    }
  }, [handleContentFileUpload]);

  const handleContentFileDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setContentFileDragOver(true);
  }, []);

  const handleContentFileDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setContentFileDragOver(false);
  }, []);

  // 콘텐츠: 공용 이미지에서 선택
  const openContentCommonModal = useCallback(async () => {
    try {
      setContentAddMenuOpen(false);
      setContentCommonOpen(true);
      setContentCommonLoading(true);
      setContentCommonSelected([]);
      // 상품의 대표 도시 조회 후 해당 도시 공용이미지 로드
      const prodCity = await getProductCity({ productNo: productId });
      const cityNo = prodCity?.도시번호 || prodCity?.코드;
      setContentCommonCityName(prodCity?.도시명 || prodCity?.코드명 || '');
      if (cityNo) {
        const imgs = await getCommonImagesByCity({ cityNo });
        setContentCommonImgs(Array.isArray(imgs) ? imgs : []);
      } else {
        setContentCommonImgs([]);
      }
    } catch (err) {
      console.error(err);
      alert('공용 이미지를 불러오지 못했습니다.');
    } finally {
      setContentCommonLoading(false);
    }
  }, [productId]);

  // 상품 선택옵션 불러오기
  useEffect(() => {
    let alive = true;
    const run = async () => {
      if (!productId) return;
      try {
        setSelectOptionsError('');
        setSelectOptionsLoading(true);
        const rows = await getProductSelectOptions({ productNo: productId });
        if (!alive) return;
        setSelectOptions(Array.isArray(rows) ? rows : []);
      } catch (e) {
        console.error(e);
        if (alive) setSelectOptionsError('선택옵션 정보를 불러오지 못했습니다.');
      } finally {
        if (alive) setSelectOptionsLoading(false);
      }
    };
    run();
    return () => { alive = false; };
  }, [productId]);

  // --- 선택옵션 편집 도우미/핸들러 ---
  const parseFees = (feeStr) => {
    try {
      if (typeof feeStr !== 'string') return [0, 0, 0];
      const toInt = (s) => {
        const digits = String(s ?? '').replace(/[^0-9]/g, '');
        return parseInt(digits || '0', 10) || 0;
      };
      const parts = feeStr.split('/').map((s) => toInt(s));
      return [parts[0] || 0, parts[1] || 0, parts[2] || 0];
    } catch { return [0, 0, 0]; }
  };
  const formatMoney = (n) => {
    const num = Number(n) || 0;
    return num.toLocaleString();
  };
  const sanitizeMoneyInput = (val) => {
    const digits = String(val || '').replace(/[^0-9]/g, '');
    if (!digits) return '';
    return Number(digits).toLocaleString();
  };
  const chargeTextToCode = (txt) => {
    const t = String(txt || '').trim();
    if (t === '요금 * 선택한 날짜' || t === '요금 * 1회만') return '066001'; // 수량
    if (t === '요금 * 숙박일수') return '066003'; // 인원 전체
    return '066001';
  };
  const chargeCodeToDisplay = (code) => (code === '066003' ? '인원 전체' : '수량');
  const splitDescToTags = (desc) => {
    try {
      if (!desc) return [];
      const parts = String(desc)
        .split(/[\n,]/)
        .flatMap((s) => String(s).split(/\s+/))
        .map((s) => String(s).trim())
        .filter(Boolean);
      const uniq = [];
      for (const p of parts) {
        if (!uniq.includes(p)) uniq.push(p);
      }
      return uniq.slice(0, 6);
    } catch { return []; }
  };

  const refreshSelectOptions = useCallback(async () => {
    try {
      setSelectOptionsLoading(true);
      const rows = await getProductSelectOptions({ productNo: productId });
      setSelectOptions(Array.isArray(rows) ? rows : []);
      setSelectOptionsError('');
    } catch (e) {
      console.error(e);
      setSelectOptionsError('선택옵션 정보를 불러오지 못했습니다.');
    } finally {
      setSelectOptionsLoading(false);
    }
  }, [productId]);

  const openSelectEdit = (row) => {
    try {
      const serviceNo = row?.서비스번호 ?? row?.번호 ?? row?.id ?? null;
      const name = row?.한글명 ?? row?.서비스명 ?? row?.명 ?? '';
      const desc = row?.설명 ?? '';
      const [adultFee, childFee, infantFee] = parseFees(row?.이용요금 ?? '0/0/0');
      const chargeModeCode = chargeTextToCode(row?.요금부과방식);
      setSelectEditForm({
        serviceNo,
        name,
        desc,
        adultFeeStr: formatMoney(adultFee),
        childFeeStr: formatMoney(childFee),
        infantFeeStr: formatMoney(infantFee),
        chargeModeCode,
      });
      setSelectEditError('');
      setSelectEditOpen(true);
    } catch (e) {
      console.error(e);
      try { alert('편집 창을 열 수 없습니다.'); } catch { }
    }
  };

  // 선택옵션 생성 (메인 컴포넌트 스코프)
  const openSelectCreate = () => {
    try {
      setSelectEditForm({
        serviceNo: null,
        name: '',
        desc: '',
        chargeModeCode: '066001',
        adultFeeStr: '',
        childFeeStr: '',
        infantFeeStr: '',
      });
      setSelectEditError('');
      setSelectEditOpen(true);
    } catch (e) {
      console.error(e);
    }
  };

  const saveSelectEdit = async () => {
    try {
      setSelectEditSaving(true);
      setSelectEditError('');
      const adultFee = parseInt(String(selectEditForm.adultFeeStr || '').replace(/,/g, ''), 10) || 0;
      const childFee = parseInt(String(selectEditForm.childFeeStr || '').replace(/,/g, ''), 10) || 0;
      const infantFee = parseInt(String(selectEditForm.infantFeeStr || '').replace(/,/g, ''), 10) || 0;

      if (selectEditForm.serviceNo) {
        await updateProductSelectOption({
          serviceNo: selectEditForm.serviceNo,
          productNo: productId,
          name: selectEditForm.name,
          desc: selectEditForm.desc,
          chargeModeCode: selectEditForm.chargeModeCode,
          adultFee,
          childFee,
          infantFee,
        });
      } else {
        await createProductSelectOption({
          productNo: productId,
          name: selectEditForm.name,
          desc: selectEditForm.desc,
          chargeModeCode: selectEditForm.chargeModeCode,
          adultFee,
          childFee,
          infantFee,
        });
      }
      setSelectEditOpen(false);
      await refreshSelectOptions();
      // 미리보기 아이프레임 새로고침
      setIframeKey((k) => k + 1);
    } catch (e) {
      console.error(e);
      setSelectEditError('저장에 실패했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setSelectEditSaving(false);
    }
  };

  // 선택옵션 삭제 (메인 컴포넌트 스코프)
  const deleteSelectOption = async () => {
    try {
      if (!selectEditForm?.serviceNo) {
        setSelectEditError('서비스번호가 없습니다.');
        return;
      }
      const ok = window.confirm('정말 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.');
      if (!ok) return;
      setSelectEditSaving(true);
      await deleteProductSelectOption({ serviceNo: selectEditForm.serviceNo });
      try { alert('삭제되었습니다.'); } catch { }
      setSelectEditOpen(false);
      await refreshSelectOptions();
      // 미리보기 아이프레임 새로고침
      setIframeKey((k) => k + 1);
    } catch (e) {
      console.error(e);
      setSelectEditError('삭제에 실패했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setSelectEditSaving(false);
    }
  };

  // 콘텐츠: 상품 갤러리에서 선택
  const openContentGalleryModal = useCallback(async () => {
    try {
      setContentAddMenuOpen(false);
      setContentGalleryOpen(true);
      setContentGalleryLoading(true);
      setContentGallerySelected([]);
      const { companyNo } = getUserInfo() || {};
      const gallery = await getProductGallery({ companyNo, productNo: productId });
      setContentGalleryImgs(Array.isArray(gallery) ? gallery : []);
    } catch (err) {
      console.error(err);
      alert('상품 갤러리를 불러오지 못했습니다.');
    } finally {
      setContentGalleryLoading(false);
    }
  }, [productId]);

  // Helper: request current HTML from SmartEditor iframe and return promise
  const seGetResolverRef = useRef(null);
  const getEditorHtml = useCallback(() => {
    return new Promise((resolve) => {
      const w = seIframeRef.current?.contentWindow;
      if (!w) return resolve({ html: '' });
      seGetResolverRef.current = resolve;
      try { w.postMessage({ type: 'SE_GET' }, '*'); } catch { }
      // safety timeout
      setTimeout(() => {
        if (seGetResolverRef.current === resolve) {
          seGetResolverRef.current = null;
          resolve({ html: '' });
        }
      }, 2000);
    });
  }, []);

  // Helper: extract plain text with basic line breaks from HTML
  const htmlToPlainText = (html) => {
    try {
      const tmp = document.createElement('div');
      const normalized = String(html || '')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/(p|div|li|h[1-6])>/gi, '\n');
      tmp.innerHTML = normalized;
      const text = (tmp.textContent || '').replace(/\u00A0/g, ' ').replace(/[\n\r]+/g, '\n').trim();
      return text;
    } catch {
      return '';
    }
  };

  // 제품 변경 시 콘텐츠 메인 리스트 로딩 (저장 후 재사용)
  const loadContentMain = useCallback(async () => {
    if (!productId) return;
    let cancelled = false;
    try {
      setContentError('');
      setContentLoading(true);
      const rows = await getContentMainList({ productNo: productId });
      if (cancelled) return;
      const list = Array.isArray(rows) ? rows : [];
      setContentMain(list);
    } catch (e) {
      if (cancelled) return;
      console.error(e);
      setContentError('불러오기 실패');
      setContentMain([]);
    } finally {
      if (!cancelled) setContentLoading(false);
    }
    return () => { cancelled = true; };
  }, [productId]);

  useEffect(() => {
    let cleanup;
    (async () => {
      cleanup = await loadContentMain();
    })();
    return () => { if (typeof cleanup === 'function') cleanup(); };
  }, [loadContentMain]);

  // Delete button handler - opens confirmation dialog
  const handleDeleteContent = useCallback(() => {
    if (!previewModalContentNo) {
      alert('삭제할 콘텐츠가 없습니다.');
      return;
    }
    setDeleteConfirmOpen(true);
  }, [previewModalContentNo]);

  // Confirm delete handler - calls API to delete content
  const confirmDeleteContent = useCallback(async () => {
    if (!previewModalContentNo) {
      setDeleteConfirmOpen(false);
      return;
    }

    setDeleteContentLoading(true);
    try {
      const { userNo } = getUserInfo() || {};

      await deleteProductContent({
        contentNo: previewModalContentNo,
        userNo,
      });

      // Close dialogs and refresh content list
      setDeleteConfirmOpen(false);
      setPreviewModalOpen(false);
      loadContentMain();
      setIframeKey((k) => k + 1);

      // Show success message
      alert('콘텐츠가 삭제되었습니다.');
    } catch (error) {
      console.error('Content deletion error:', error);
      alert(`콘텐츠 삭제 중 오류가 발생했습니다: ${error.message || '알 수 없는 오류'}`);
    } finally {
      setDeleteContentLoading(false);
    }
  }, [previewModalContentNo, loadContentMain]);

  // Save handler for SmartEditor content
  const handleSaveSmartEditor = useCallback(async () => {
    if (!productId) {
      alert('상품 정보가 없습니다.');
      return;
    }
    if (!seReady) {
      alert('에디터가 아직 준비되지 않았습니다.');
      return;
    }
    setSaveContentLoading(true);
    try {
      const { companyNo, userNo } = getUserInfo() || {};
      const { html } = await getEditorHtml();
      const text = htmlToPlainText(html);
      const currentTitleInput = (titleInputRef.current && typeof titleInputRef.current.value === 'string') ? titleInputRef.current.value : previewModalTitleEdit;
      const effectiveTitle = currentTitleInput || previewModalTitle || '콘텐츠';
      // 신규 생성 모드이면 먼저 컨텐츠번호 확보
      let contentNo = (Number.isFinite(previewModalContentNo) && previewModalContentNo > 0) ? previewModalContentNo : null;
      if (!contentNo && previewModalMode === 'create') {
        const created = await createNewProductContent({
          productNo: productId,
          companyNo,
          userNo,
          contentTypeCode: String(previewModalContentTypeCode || ''),
          title: effectiveTitle,
        });
        // 다양한 형태 대응: created.table?.[0]?.컨텐츠번호 또는 DataSet/Result 구조
        let newNo = null;
        try {
          if (created && Array.isArray(created.table) && created.table[0] && created.table[0].컨텐츠번호) {
            newNo = Number(created.table[0].컨텐츠번호);
          } else if (created && created.Table && Array.isArray(created.Table) && created.Table[0] && (created.Table[0].컨텐츠번호 || created.Table[0].contentNo)) {
            newNo = Number(created.Table[0].컨텐츠번호 || created.Table[0].contentNo);
          }
        } catch { }
        if (!newNo || !Number.isFinite(newNo)) throw new Error('신규 컨텐츠번호 생성 실패');
        contentNo = newNo;
        // 신규 컨텐츠번호를 상태에 반영하여 이후 저장/편집에서 재사용
        setPreviewModalContentNo(newNo);
      }
      // 2차 저장(본문/HTML 포함 최종 저장)
      await saveProductContentInfo({
        productNo: productId,
        contentNo,
        companyNo,
        userNo,
        title: effectiveTitle,
        text,
        html,
        expose: true,
        dealerOnly: false,
        imageMappingUse: true,
        fileAttach: uploadedFiles.length > 0,
      });
      // refresh list and close editor
      await loadContentMain();
      // 미리보기 새로고침
      setIframeKey((k) => k + 1);
      setPreviewModalOpen(false);
      setPreviewModalContentNo(null);
      setPreviewModalMode('edit');
      setPreviewModalContentTypeCode('');
      alert('콘텐츠가 저장되었습니다.');
    } catch (err) {
      console.error(err);
      alert('저장 중 오류가 발생했습니다.');
    } finally {
      setSaveContentLoading(false);
    }
  }, [productId, seReady, getEditorHtml, previewModalTitleEdit, previewModalTitle, previewModalContentNo, previewModalMode, previewModalContentTypeCode, loadContentMain, uploadedFiles]);

  // 일정표 HTML 정리: 내용 없는 info-section 블럭 제거
  const cleanItineraryHtml = useCallback((html) => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(String(html || ''), 'text/html');
      const sections = doc.querySelectorAll('div.info-section');
      sections.forEach((sec) => {
        const textEmpty = (sec.textContent || '').trim() === '';
        const children = Array.from(sec.children || []);
        if (textEmpty) {
          if (children.length === 0) {
            sec.remove();
            return;
          }
          if (children.length === 1) {
            const c = children[0];
            const childEmpty = ((c.textContent || '').trim() === '') && (c.children?.length === 0);
            if (childEmpty) {
              sec.remove();
              return;
            }
          }
        }
      });
      // 보존을 위해 전체 문서 반환 (DOCTYPE 포함)
      return '<!DOCTYPE html>\n' + doc.documentElement.outerHTML;
    } catch {
      return String(html || '');
    }
  }, []);

  // 일정표 전환: 현재 에디터 HTML을 서버에 전송하여 변환된 HTML을 다시 에디터에 반영
  const handleConvertItinerary = useCallback(async () => {
    if (!seReady) {
      alert('에디터가 아직 준비되지 않았습니다.');
      return;
    }
    setConvertingItinerary(true);
    try {
      const { html } = await getEditorHtml();
      const resp = await fetch('https://revato.mytripbox.co.kr/itinerary/df_cellMerge', {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain; charset=UTF-8' },
        // 서버 요구사항: JSON 래핑 없이 순수 HTML 문자열만 전달
        body: String(html || ''),
      });
      if (!resp.ok) throw new Error(`요청 실패 (${resp.status})`);
      const data = await resp.json();
      let returned = String((data && data.html) || '');
      // 서버가 이스케이프된 문자열을 반환하는 경우를 대비하여 최소한의 정리 수행
      if (returned.includes('\\n') || returned.includes('\\"') || returned.includes('\\/')) {
        returned = returned
          .replace(/\\n/g, '\n')
          .replace(/\\t/g, '\t')
          .replace(/\\\"/g, '"')
          .replace(/\\\//g, '/');
      }
      // 내용 없는 info-section 블럭 제거
      returned = cleanItineraryHtml(returned);
      // 에디터와 로컬 상태 모두 갱신
      try {
        const w = seIframeRef.current?.contentWindow;
        if (w) w.postMessage({ type: 'SE_SET', html: returned }, '*');
      } catch { }
      setPreviewModalHtml(returned);
      if (data && data.status && data.status !== 'success') {
        console.warn('변환 응답 상태:', data.status);
      }
    } catch (err) {
      console.error(err);
      alert('일정표 전환 중 오류가 발생했습니다.');
    } finally {
      setConvertingItinerary(false);
    }
  }, [seReady, getEditorHtml, cleanItineraryHtml]);

  // 일정 복사 모달 상태
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copySourceScheduleNo, setCopySourceScheduleNo] = useState(null);
  const [copyTargetDay, setCopyTargetDay] = useState('');
  const [copyLoading, setCopyLoading] = useState(false);
  const openCopyModal = (scheduleNo) => {
    setCopySourceScheduleNo(scheduleNo);
    setCopyTargetDay('');
    setCopyModalOpen(true);
  };
  const closeCopyModal = () => {
    if (copyLoading) return;
    setCopyModalOpen(false);
    setCopySourceScheduleNo(null);
    setCopyTargetDay('');
  };
  const handleConfirmCopy = async () => {
    try {
      if (!productId || !itineraryTypeNo || !copySourceScheduleNo) return;
      const dayNum = Number(copyTargetDay);
      if (!dayNum || dayNum <= 0) {
        alert('복사할 여행일차를 선택하세요.');
        return;
      }
      setCopyLoading(true);
      await copyScheduleToDay({ productNo: productId, itineraryTypeNo, day: dayNum, selectedScheduleNo: copySourceScheduleNo });
      await loadItinerary();
      setIframeKey((k) => k + 1);
      closeCopyModal();
      alert(`선택한일차 ${dayNum}일차로 일정이 복사 되었습니다.`);
    } catch (e) {
      console.error(e);
      alert('일정 복사 중 오류가 발생했습니다.');
    } finally {
      setCopyLoading(false);
    }
  };

  // 이미지 설정 모달 닫기 + 우측 미리보기 새로고침
  const closeImageStatusAndRefresh = () => {
    setImageStatusOpen(false);
    setIframeKey((k) => k + 1);
  };

  const openImageStatus = async (scheduleNo) => {
    try {
      setImageStatusError('');
      setImageStatusLoading(true);
      setImageStatusScheduleNo(scheduleNo);
      const { companyNo } = getUserInfo() || {};
      const supplierNo = companyNo;
      const ds = await getSchedulePlaceImages({ scheduleNo, optionScheduleNo: -1, supplierNo, placeNo: 0 });
      const rowsPlace = Array.isArray(ds?.table) ? ds.table : [];
      const rowsSchedule = Array.isArray(ds?.table1) ? ds.table1 : [];
      // 그룹핑: 장소명 기준
      const groups = {};
      for (const row of rowsPlace) {
        const name = String(row?.장소명 || '').trim() || '장소 미지정';
        if (!groups[name]) groups[name] = [];
        groups[name].push(row);
      }
      setImageStatusPlaceGroups(groups);
      setImageStatusScheduleRows(rowsSchedule);
      // 기본 선택: 일정 이미지 + 장소 이미지의 모든 사용자 이미지(업체번호 != -1)
      const scheduleNos = rowsSchedule
        .filter(r => Number(r?.업체번호) !== -1)
        .map(r => r.이미지번호)
        .filter(Boolean);
      const placeNosAll = rowsPlace
        .filter(r => Number(r?.업체번호) !== -1)
        .map(r => r.이미지번호)
        .filter(Boolean);
      const allSelected = Array.from(new Set([...scheduleNos, ...placeNosAll]));
      setImageStatusSelected(allSelected);
      setImageStatusOpen(true);
    } catch (e) {
      console.error(e);
      setImageStatusError('이미지 현황을 불러오지 못했습니다.');
    } finally {
      setImageStatusLoading(false);
    }
  };

  const handleDeleteSingleImage = async (imageNo) => {
    if (!imageStatusScheduleNo || !imageNo) return;
    if (!window.confirm('이 이미지를 삭제하시겠습니까?')) return;
    try {
      setImageStatusDeleting(true);
      const { companyNo, userNo } = getUserInfo() || {};
      const supplierNo = companyNo;
      await deleteScheduleImage({ imageNo, supplierNo, placeNo: 0, scheduleNo: -1, optionScheduleNo: -1, userNo });
      // 삭제 후 모달 데이터 새로 고침
      const ds = await getSchedulePlaceImages({ scheduleNo: imageStatusScheduleNo, optionScheduleNo: -1, supplierNo, placeNo: 0 });
      const rowsPlace = Array.isArray(ds?.table) ? ds.table : [];
      const rowsSchedule = Array.isArray(ds?.table1) ? ds.table1 : [];
      const groups = {};
      for (const row of rowsPlace) {
        const name = String(row?.장소명 || '').trim() || '장소 미지정';
        if (!groups[name]) groups[name] = [];
        groups[name].push(row);
      }
      setImageStatusPlaceGroups(groups);
      setImageStatusScheduleRows(rowsSchedule);
      // 카드 미리보기 즉시 갱신
      await refreshScheduleImagesForCard(imageStatusScheduleNo);
      // 전체 일정 상세 재조회하여 최신 정보로 랜더링
      await refreshItineraryDetail();
      // 선택값에서 제거
      setImageStatusSelected((prev) => prev.filter((n) => n !== imageNo));
    } catch (err) {
      console.error(err);
      alert('이미지 삭제에 실패했습니다.');
    } finally {
      setImageStatusDeleting(false);
    }
  };

  // 불러온 일정 데이터 (읽기 전용 미리보기용)
  const [itineraryLoading, setItineraryLoading] = useState(false);
  const [itineraryError, setItineraryError] = useState('');
  const [itineraryTypeNo, setItineraryTypeNo] = useState(null);
  const [itineraryData, setItineraryData] = useState({ table: [], table1: [], table2: [], table3: [], table4: [], table5: [] });

  // 방문지 키워드(드래그 정렬용 상태 - dnd-kit 사용)
  const [visitKeywords, setVisitKeywords] = useState([]);
  // 방문지 키워드 추가/편집 상태
  const [newVisitKeyword, setNewVisitKeyword] = useState('');
  const [visitKwEditIndex, setVisitKwEditIndex] = useState(null);

  // 미팅장소 비고 입력 상태 및 저장
  const [meetingRemarkText, setMeetingRemarkText] = useState('');
  const [meetingRemarkSaving, setMeetingRemarkSaving] = useState(false);
  const [meetingRemarkError, setMeetingRemarkError] = useState('');

  // HTML <-> textarea 변환 헬퍼
  const htmlToTextareaText = useCallback((html) => {
    const s = String(html || '');
    // Strip outer <p>...</p> if present (tolerate attributes and spaces)
    const inner = s.replace(/^\s*<p[^>]*>/i, '').replace(/<\/p>\s*$/i, '');
    // Replace <br> variants with newlines
    return inner.replace(/<br\s*\/?>(\r?\n)?/gi, '\n');
  }, []);

  const textareaTextToHtml = useCallback((text) => {
    const t = String(text || '')
      .replace(/\r\n/g, '\n');
    const withBr = t.split('\n').map(line => line.length ? line : '').join('<br>');
    return `<p>${withBr}</p>`;
  }, []);
  const [visitKwEditValue, setVisitKwEditValue] = useState('');
  const [visitKwActiveId, setVisitKwActiveId] = useState(null);
  const [visitKwAdding, setVisitKwAdding] = useState(false);

  // 일정 데이터 로드/변경 시 방문지 키워드 초기화
  useEffect(() => {
    try {
      const kwStr = itineraryData?.table4?.[0]?.방문지키워드;
      const arr = String(kwStr || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      setVisitKeywords(arr);
    } catch {
      setVisitKeywords([]);
    }
  }, [itineraryData?.table4]);

  // 방문지 키워드 순서 자동 저장
  const handleSaveVisitKeywords = async (keywordsArray) => {
    const arr = Array.isArray(keywordsArray) ? keywordsArray : visitKeywords;
    const joined = arr.join(',');
    // 로컬 업데이트
    setItineraryData((prev) => {
      const next = { ...prev };
      if (Array.isArray(next.table4) && next.table4.length > 0) {
        next.table4 = next.table4.map((row, idx) => idx === 0 ? { ...row, 방문지키워드: joined } : row);
      }
      return next;
    });
    // 서버 저장
    try {
      if (productId && itineraryTypeNo) {
        await saveItineraryVisitKeywords({ productNo: productId, itineraryTypeNo, keywords: joined });
        // 저장 성공 시 프리뷰(우측 아이프레임) 새로고침
        setIframeKey((k) => k + 1);
      }
    } catch (e) {
      console.error('방문지키워드 저장 실패:', e);
    }
  };

  // 방문지 키워드 추가
  const handleAddVisitKeyword = async () => {
    const v = (newVisitKeyword || '').trim();
    if (!v) return;
    const next = [...visitKeywords, v];
    setVisitKeywords(next);
    await handleSaveVisitKeywords(next);
    setNewVisitKeyword('');
  };

  // 방문지 키워드 삭제
  const handleDeleteVisitKeyword = async (index) => {
    const next = visitKeywords.filter((_, i) => i !== index);
    setVisitKeywords(next);
    await handleSaveVisitKeywords(next);
  };

  // 미팅장소 비고 저장
  const handleSaveMeetingRemark = async () => {
    if (!productId || !itineraryTypeNo) return;
    try {
      setMeetingRemarkError('');
      setMeetingRemarkSaving(true);
      const html = textareaTextToHtml(meetingRemarkText || '');
      await saveTourMeetingRemark({ productNo: productId, itineraryTypeNo, remarkHtml: html });
      // 성공 알림 및 우측 프리뷰 새로고침
      try { alert('미팅장소가 저장되었습니다.'); } catch { }
      setIframeKey((k) => k + 1);
      // Optionally refresh server value
      try {
        const meetResp = await getTourMeetingSetting({ productNo: productId, itineraryTypeNo });
        const row0 = Array.isArray(meetResp?.table) ? meetResp.table[0] : Array.isArray(meetResp?.Table) ? meetResp.Table[0] : null;
        const remarkHtml2 = row0?.투어미팅비고 ?? row0?.MeetingRemark;
        setMeetingRemarkText(htmlToTextareaText(remarkHtml2 || meetingRemarkText));
      } catch { }
    } catch (e) {
      console.error('미팅장소 저장 실패', e);
      setMeetingRemarkError('미팅장소 저장에 실패했습니다. 잠시 후 다시 시도하세요.');
    } finally {
      setMeetingRemarkSaving(false);
    }
  };

  // 방문지 키워드 편집 시작
  const startEditVisitKeyword = (index) => {
    visitKwEditingJustActivatedRef.current = true;
    setVisitKwEditIndex(index);
    setVisitKwEditValue(visitKeywords[index] || '');
    // 아주 짧은 시간 후 초기 blur 허용
    setTimeout(() => {
      visitKwEditingJustActivatedRef.current = false;
    }, 150);
  };

  // 방문지 키워드 편집 저장
  const commitEditVisitKeyword = async () => {
    const v = (visitKwEditValue || '').trim();
    if (visitKwEditIndex == null) return;
    const original = visitKeywords[visitKwEditIndex] || '';
    // 변경 없으면 저장 호출 생략
    if (v === original) {
      setVisitKwEditIndex(null);
      setVisitKwEditValue('');
      return;
    }
    if (!v) {
      // empty -> 삭제로 처리
      await handleDeleteVisitKeyword(visitKwEditIndex);
      setVisitKwEditIndex(null);
      setVisitKwEditValue('');
      return;
    }
    const next = visitKeywords.map((kw, i) => (i === visitKwEditIndex ? v : kw));
    setVisitKeywords(next);
    await handleSaveVisitKeywords(next);
    setVisitKwEditIndex(null);
    setVisitKwEditValue('');
  };

  // 카드별 장소별 이미지(장소명 존재): { [scheduleNo: number]: { [placeName: string]: string[] } }
  const [cardImagesByPlace, setCardImagesByPlace] = useState({});
  // 카드별 장소명 없는 이미지(일정내용 아래 노출): { [scheduleNo: number]: string[] }
  const [cardImagesNoPlace, setCardImagesNoPlace] = useState({});

  // 카드별 연관장소 캐시: { [scheduleNo: number]: Array<{ value: number, Description: string }> }
  const [relatedPlacesBySchedule, setRelatedPlacesBySchedule] = useState({});
  // 카드별 연관장소 입력 상태: { [scheduleNo: number]: { value: string, desc: string } }
  const [newPlaceInputs, setNewPlaceInputs] = useState({});

  // Airports cache and flight form states
  // Airports list: [{ 코드, 코드명 }]
  const [airports, setAirports] = useState([]);
  const [airportsLoaded, setAirportsLoaded] = useState(false);
  const [airportsLoading, setAirportsLoading] = useState(false);
  // Per-schedule flight mode toggle
  const [flightModeBySchedule, setFlightModeBySchedule] = useState({});
  // Per-schedule draft inputs for flight details
  // { [scheduleNo]: { flightNo, depAirport, arrAirport, depTime, arrTime, duration } }
  const [flightDraftBySchedule, setFlightDraftBySchedule] = useState({});

  const ensureAirportsLoaded = useCallback(async () => {
    if (airportsLoaded || airportsLoading) return;
    try {
      setAirportsLoading(true);
      const { companyNo } = getUserInfo() || {};
      const list = await getSupplierAirports({ companyNo });
      setAirports(Array.isArray(list) ? list : []);
      setAirportsLoaded(true);
    } catch (e) {
      console.warn('공항 목록 조회 실패:', e);
      setAirports([]);
      setAirportsLoaded(true);
    } finally {
      setAirportsLoading(false);
    }
  }, [airportsLoaded, airportsLoading]);

  const toggleFlightMode = useCallback(async (scheduleNo) => {
    await ensureAirportsLoaded();
    setFlightModeBySchedule(prev => ({ ...prev, [scheduleNo]: !prev[scheduleNo] }));
    // Initialize draft if not exists
    setFlightDraftBySchedule(prev => ({
      ...prev,
      [scheduleNo]: prev[scheduleNo] || { flightNo: '', depAirport: '', arrAirport: '', depTime: '', arrTime: '', duration: '' },
    }));
    // Also ensure we're in edit mode for this schedule
    setEditingSchedules(prev => ({ ...prev, [scheduleNo]: prev[scheduleNo] || { name: '', content: '' } }));
  }, [ensureAirportsLoaded]);

  const synthesizeFlightName = (draft) => {
    const trim = (s) => (s || '').toString().trim();
    const dep = trim(draft?.depAirport);
    const arr = trim(draft?.arrAirport);
    const fno = trim(draft?.flightNo);
    const seg = dep && arr ? `${dep} → ${arr}` : (dep || arr);
    // Times (출발시간/도착시간) are intentionally excluded from auto name per request
    const parts = [seg, fno].filter(Boolean);
    return parts.length ? parts.join(' · ') : '';
  };

  // Extract IATA-like code from input value e.g. "ICN", "(ICN) 인천", or return uppercased 3-4 letters
  const extractAirportCode = (val) => {
    const s = String(val || '').trim();
    if (!s) return '';
    const m = s.match(/\(([A-Za-z0-9]{3,4})\)/);
    if (m) return m[1].toUpperCase();
    const m2 = s.match(/^([A-Za-z0-9]{3,4})\b/);
    if (m2) return m2[1].toUpperCase();
    return s.toUpperCase();
  };

  // Extract first flight info from an itinerary row
  const parseFlightFromRow = (row) => {
    try {
      if (!row || !row.항공편) return null;
      const arr = JSON.parse(row.항공편);
      if (!Array.isArray(arr) || !arr.length) return null;
      const f = arr[0] || {};
      return {
        flightNo: String(f.편명 || '').trim(),
        depAirport: String(f.출발공항명 || f.출발공항 || '').trim(),
        arrAirport: String(f.도착공항명 || f.도착공항 || '').trim(),
        depTime: String(f.출발시간 || row.시작시간 || '').trim(),
        arrTime: String(f.도착시간 || row.종료시간 || '').trim(),
        duration: String(f.소요시간 || row.소요시간 || '').trim(),
        depCode: String(f.출발공항코드 || '').trim().toUpperCase(),
        arrCode: String(f.도착공항코드 || '').trim().toUpperCase(),
      };
    } catch {
      return null;
    }
  };

  // Prepare inline flight edit form from an existing row
  const prepareFlightDraftFromRow = useCallback(async (scheduleNo, row) => {
    await ensureAirportsLoaded();
    const parsed = parseFlightFromRow(row);
    setFlightModeBySchedule(prev => ({ ...prev, [scheduleNo]: true }));
    setFlightDraftBySchedule(prev => ({
      ...prev,
      [scheduleNo]: parsed ? {
        flightNo: parsed.flightNo || '',
        depAirport: parsed.depAirport || '',
        arrAirport: parsed.arrAirport || '',
        depTime: parsed.depTime || '',
        arrTime: parsed.arrTime || '',
        duration: parsed.duration || ''
      } : (prev[scheduleNo] || { flightNo: '', depAirport: '', arrAirport: '', depTime: '', arrTime: '', duration: '' })
    }));
  }, [ensureAirportsLoaded]);

  // helper: 행에서 연관장소 필드 추정 및 파싱
  const parseRelatedPlacesFromRow = (row) => {
    try {
      // 우선적으로 '연관장소' 키를 시도
      const candidates = ['연관장소', '연관 장소', '관련장소', '관련 장소'];
      let raw = null;
      for (const key of candidates) {
        if (row && Object.prototype.hasOwnProperty.call(row, key) && typeof row[key] === 'string') {
          raw = row[key];
          break;
        }
      }
      // 후보가 없으면 문자열 형태의 JSON 배열값을 가진 첫 필드를 탐색
      if (!raw) {
        for (const k of Object.keys(row || {})) {
          const v = row[k];
          if (typeof v === 'string' && v.trim().startsWith('[') && v.includes('Description')) {
            raw = v;
            break;
          }
        }
      }
      if (!raw || typeof raw !== 'string') return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed
        .filter((x) => x && Number(x.value) > 0 && String(x.Description || '').trim())
        .map((x) => ({ value: Number(x.value), Description: String(x.Description).trim() }));
    } catch (e) {
      return [];
    }
  };

  // Save helper for an editing schedule card. Returns the finalized scheduleNo.
  const saveScheduleEdits = async (scheduleNoLocal, dayNo, nameVal, contentVal, opts = {}) => {
    try {
      // 일반 일정은 일정명이 필수 (항공 모드일 때는 자동완성/빈값 허용 흐름이 있을 수 있음)
      if (!flightModeBySchedule?.[scheduleNoLocal] && !String(nameVal || '').trim()) {
        alert('일정명을 입력해주세요.');
        return null;
      }
      setEditingSchedules(prev => ({ ...prev, [scheduleNoLocal]: { ...prev[scheduleNoLocal], saving: true } }));
      const { userNo } = getUserInfo() || {};
      let finalScheduleNo = scheduleNoLocal;
      // If this is a temporary card, create it first to get a real schedule number
      if (scheduleNoLocal < 0) {
        const addDs = await addItinerarySchedule({
          productNo: productId,
          itineraryTypeNo,
          day: dayNo,
          productTypeCode,
          name: nameVal || '',
          selectedScheduleNo: tempAnchors?.[scheduleNoLocal] || '',
        });
        const newNo = Number(addDs?.table?.[0]?.상품일정번호 || 0);
        if (!newNo) throw new Error('신규 일정 번호를 가져오지 못했습니다.');
        finalScheduleNo = newNo;
        // 임시 앵커 매핑 정리
        setTempAnchors(prev => {
          const n = { ...prev };
          delete n[scheduleNoLocal];
          return n;
        });
      }
      await saveItineraryContent({
        scheduleNo: finalScheduleNo,
        itineraryTypeNo,
        productNo: productId,
        name: nameVal,
        content: contentVal,
        userNo,
      });

      // If inline flight mode is active for this schedule, also persist flight itinerary
      try {
        if (flightModeBySchedule[finalScheduleNo]) {
          const draft = flightDraftBySchedule[finalScheduleNo] || {};
          const dep = String(draft.depAirport || '').trim();
          const arr = String(draft.arrAirport || '').trim();
          const fno = String(draft.flightNo || '').trim();
          const dt = String(draft.depTime || '').trim();
          const at = String(draft.arrTime || '').trim();
          const dur = String(draft.duration || '').trim();
          const depCode = extractAirportCode(dep);
          const arrCode = extractAirportCode(arr);
          if (!depCode || !arrCode) throw new Error('출발/도착 공항을 선택하세요.');

          // Build new or updated flight record
          const flightData = {
            일차: Number(dayNo),
            순서: 0, // set below
            편명: fno,
            출발공항: depCode,
            도착공항: arrCode,
            구분: '217001',
            출발시간: dt,
            도착시간: at,
            소요시간: dur,
          };

          // Load existing and merge (replace if same day and same flightNo or same dep/arr)
          const itineraryDetail = await getItineraryDetail({ productNo: productId, itineraryTypeNo });
          let existingFlightItinerary = [];
          const tables = ['table', 'table1', 'table2', 'table3', 'table4', 'table5'];
          for (const tableName of tables) {
            const table = itineraryDetail[tableName];
            if (Array.isArray(table) && table.length > 0) {
              const firstRow = table[0];
              if (firstRow && firstRow.항공여정) {
                try {
                  const parsed = JSON.parse(firstRow.항공여정);
                  if (Array.isArray(parsed)) {
                    existingFlightItinerary = parsed.map((item, index) => ({
                      일차: item.일차 || 1,
                      순서: item.순서 || (index + 1),
                      편명: item.편명 || '',
                      출발공항: item.출발공항코드 || extractAirportCode(item.출발공항) || item.출발공항 || '',
                      도착공항: item.도착공항코드 || extractAirportCode(item.도착공항) || item.도착공항 || '',
                      구분: item.구분 || '217001',
                      출발시간: item.출발시간 || '',
                      도착시간: item.도착시간 || '',
                      소요시간: item.소요시간 || '',
                    }));
                    break;
                  }
                } catch { }
              }
            }
          }

          let maxSeq = 0;
          if (existingFlightItinerary.length) {
            maxSeq = Math.max(...existingFlightItinerary.map(x => Number(x.순서) || 0));
          }
          // find a matching entry to update
          const matchIdx = existingFlightItinerary.findIndex(x => Number(x.일차) === Number(dayNo) && (
            (x.편명 && fno && x.편명 === fno) ||
            (x.출발공항 === depCode && x.도착공항 === arrCode)
          ));
          if (matchIdx >= 0) {
            flightData.순서 = existingFlightItinerary[matchIdx].순서 || (matchIdx + 1);
            existingFlightItinerary[matchIdx] = { ...existingFlightItinerary[matchIdx], ...flightData };
          } else {
            flightData.순서 = maxSeq + 1;
            existingFlightItinerary.push(flightData);
          }
          // sort
          existingFlightItinerary.sort((a, b) => {
            const d = Number(a.일차) - Number(b.일차);
            return d !== 0 ? d : Number(a.순서) - Number(b.순서);
          });

          await saveFlightItinerary({
            productNo: productId,
            itineraryTypeNo,
            flightItinerary: existingFlightItinerary,
          });
        }
      } catch (e) {
        console.warn('항공 정보 저장 중 경고:', e);
      }
      // Reflect changes in local state
      setItineraryData((prev) => {
        const t1 = Array.isArray(prev.table1) ? prev.table1.slice() : [];
        for (let i = 0; i < t1.length; i++) {
          const row = t1[i];
          const no = Number(row.상품일정번호 ?? row.일정번호 ?? row.장소번호 ?? 0);
          if (no === scheduleNoLocal) {
            t1[i] = { ...row, 상품일정번호: finalScheduleNo, 일정명: nameVal, 일정내용: contentVal };
            break;
          }
        }
        return { ...prev, table1: t1 };
      });
      // Exit edit mode for this card
      setEditingSchedules(prev => {
        const n = { ...prev };
        delete n[scheduleNoLocal];
        return n;
      });
      // Optionally refresh itinerary and preview (iframe)
      const { refreshPreview = true } = opts || {};
      if (refreshPreview) {
        try {
          await loadItinerary();
        } finally {
          setIframeKey((k) => k + 1);
        }
      }
      return finalScheduleNo;
    } catch (err) {
      // Restore saving flag to false so user can retry
      setEditingSchedules(prev => ({ ...prev, [scheduleNoLocal]: { ...prev[scheduleNoLocal], saving: false } }));
      throw err;
    }
  };

  // Save helper for hotel inline edits (persist via SP and update table5)
  const saveHotelEdits = async (hotelKey, dayNo, nameVal, contentVal, opts = {}) => {
    setEditingHotels(prev => ({ ...prev, [hotelKey]: { ...prev[hotelKey], saving: true } }));
    try {
      // Locate the target hotel row from table5 to build payload
      const t5 = Array.isArray(itineraryData?.table5) ? itineraryData.table5 : [];
      let idx = t5.findIndex(row => {
        const key = Number(row.상품일정번호 ?? row.일정번호 ?? row.장소번호 ?? 0);
        return key && key === hotelKey;
      });
      // Fallback: match by same day and (planNo, resourceNo)
      if (idx < 0) {
        const planNoHint = String(opts?.row?.가격옵션번호 ?? '');
        const resourceNoHint = String(opts?.row?.자원번호 ?? '');
        const sameDay = t5.filter(r => Number(r.일차) === Number(dayNo));
        const candIdx = sameDay.findIndex(r => String(r.가격옵션번호 ?? '') === planNoHint && String(r.자원번호 ?? '') === resourceNoHint);
        if (candIdx >= 0) {
          // Map back to original t5 index
          idx = t5.findIndex(r => r === sameDay[candIdx]);
        }
      }
      // Use row from t5 if found; otherwise, use provided row data as last resort
      const row = idx >= 0 ? (t5[idx] || {}) : (opts?.row || {});
      if (!row || (!row.가격옵션번호 && !row.가격옵션명 && !row.자원번호)) {
        throw new Error('해당 호텔 항목을 찾을 수 없습니다.');
      }

      const planNo = row.가격옵션번호;
      const planName = row.가격옵션명;
      const resourceNo = row.자원번호;
      const prevPlaceName = row.일정명 || '';
      const prevBigo = row.일정내용 || '';

      // Required IDs
      const productNo = productId; // from params/state
      // itineraryTypeNo exists in component state from itinerary load

      await saveHotelItineraryEdits({
        productNo,
        itineraryTypeNo,
        dayNo,
        placeNo: null,
        placeName: nameVal || '',
        bigo: contentVal || '',
        planNo,
        planName,
        prevPlaceName,
        prevBigo,
        resourceNo,
      });

      // Update local state on success
      setItineraryData(prev => {
        const copy = Array.isArray(prev.table5) ? prev.table5.slice() : [];
        let i = copy.findIndex(r => {
          const key = Number(r.상품일정번호 ?? r.일정번호 ?? r.장소번호 ?? 0);
          return key && key === hotelKey;
        });
        if (i < 0) {
          // Fallback same as above for local update
          const planNoHint = String(row?.가격옵션번호 ?? '');
          const resourceNoHint = String(row?.자원번호 ?? '');
          const sameDay = copy.filter(r => Number(r.일차) === Number(dayNo));
          const candIdx = sameDay.findIndex(r => String(r.가격옵션번호 ?? '') === planNoHint && String(r.자원번호 ?? '') === resourceNoHint);
          if (candIdx >= 0) {
            i = copy.findIndex(r => r === sameDay[candIdx]);
          }
        }
        if (i >= 0) {
          copy[i] = { ...copy[i], 일정명: nameVal || '', 일정내용: contentVal || '' };
        }
        return { ...prev, table5: copy };
      });

      // Optionally refresh itinerary and preview
      try {
        await loadItinerary();
      } finally {
        setIframeKey(k => k + 1);
      }
    } catch (err) {
      console.error('호텔 저장 실패', err);
      alert('호텔 정보 저장에 실패했습니다. 다시 시도해주세요.');
      throw err;
    } finally {
      setEditingHotels(prev => ({ ...prev, [hotelKey]: { ...prev[hotelKey], saving: false } }));
    }
  };

  // 일정 카드 이미지 로딩 (불러오기 이후 실행)
  useEffect(() => {
    const loadImages = async () => {
      try {
        const cards = Array.isArray(itineraryData?.table1) ? itineraryData.table1 : [];
        if (!cards.length) return;
        const { companyNo } = getUserInfo() || {};
        const supplierNo = companyNo; // @공급자번호는 토큰에서 추출
        const tasks = [];
        const nextByPlace = { ...cardImagesByPlace };
        const nextNoPlace = { ...cardImagesNoPlace };
        for (const r of cards) {
          const scheduleNo = Number(r.상품일정번호 ?? r.일정번호 ?? r.장소번호 ?? 0);
          // Skip temporary or invalid schedule numbers until saved (scheduleNo must be > 0)
          if (!Number.isFinite(scheduleNo) || scheduleNo <= 0) continue;
          if (nextByPlace[scheduleNo] && nextNoPlace[scheduleNo]) continue;
          // Important: use placeNo = 0 to fetch BOTH schedule-level (table1) and place-level (table) images
          // If we pass a non-zero placeNo, the SP returns only place images, and schedule images (table1) are omitted
          const placeNo = 0;
          tasks.push(
            (async () => {
              const ds = await getSchedulePlaceImages({ scheduleNo, optionScheduleNo: -1, supplierNo, placeNo });
              // 두 테이블 모두에서 이미지 추출 후 업체번호 필터
              const rowsA = Array.isArray(ds?.table) ? ds.table : [];
              const rowsB = Array.isArray(ds?.table1) ? ds.table1 : [];
              // Strict filter for place images (table)
              const rowsAFiltered = rowsA.filter(x => Number(x?.업체번호) === Number(supplierNo));
              // Looser filter for schedule images (table1): accept when 업체번호 matches, or when it's empty/null/0
              const rowsBFiltered = rowsB.filter(x => {
                const v = Number(x?.업체번호);
                return Number.isFinite(v) ? (v === Number(supplierNo) || v === 0) : true;
              });
              const all = [...rowsAFiltered, ...rowsBFiltered];
              const grouped = {};
              const noPlace = [];
              for (const row of all) {
                const name = String(row?.장소명 || '').trim();
                // Use fallbacks: some schedule images (table1) only have 이미지경로2 or 원본이미지경로
                const url = row?.이미지경로2 || row?.이미지경로 || row?.원본이미지경로;
                if (!url) continue;
                if (name) {
                  if (!grouped[name]) grouped[name] = [];
                  if (grouped[name].length < 12) grouped[name].push(url); // 안전상 제한
                } else {
                  if (noPlace.length < 12) noPlace.push(url);
                }
              }
              nextByPlace[scheduleNo] = grouped;
              nextNoPlace[scheduleNo] = noPlace;

              setCardImagesByPlace(curr => ({ ...curr, [scheduleNo]: nextByPlace[scheduleNo] || {} }));
              setCardImagesNoPlace(curr => ({ ...curr, [scheduleNo]: nextNoPlace[scheduleNo] || [] }));
            })()
          );
        }
        // 병렬 실행
        if (tasks.length) await Promise.all(tasks);
      } catch (e) {
        console.error('카드 이미지 로딩 실패', e);
      }
    };
    loadImages();
  }, [itineraryData?.table1]);

  // 단일 일정 카드 이미지 새로고침 헬퍼
  const refreshScheduleImagesForCard = async (scheduleNo) => {
    try {
      // Only run for valid saved schedules
      if (!Number.isFinite(scheduleNo) || scheduleNo <= 0) return;
      const { companyNo } = getUserInfo() || {};
      const supplierNo = companyNo;
      const placeNo = 0;
      const ds = await getSchedulePlaceImages({ scheduleNo, optionScheduleNo: -1, supplierNo, placeNo });
      const rowsA = Array.isArray(ds?.table) ? ds.table : [];
      const rowsB = Array.isArray(ds?.table1) ? ds.table1 : [];
      const rowsAFiltered = rowsA.filter(x => Number(x?.업체번호) === Number(supplierNo));
      const rowsBFiltered = rowsB.filter(x => {
        const v = Number(x?.업체번호);
        return Number.isFinite(v) ? (v === Number(supplierNo) || v === 0) : true;
      });
      const all = [...rowsAFiltered, ...rowsBFiltered];
      const grouped = {};
      const noPlace = [];
      for (const row of all) {
        const name = String(row?.장소명 || '').trim();
        const url = row?.이미지경로2 || row?.이미지경로 || row?.원본이미지경로;
        if (!url) continue;
        if (name) {
          if (!grouped[name]) grouped[name] = [];
          if (grouped[name].length < 12) grouped[name].push(url);
        } else {
          if (noPlace.length < 12) noPlace.push(url);
        }
      }
      setCardImagesByPlace(curr => ({ ...curr, [scheduleNo]: grouped }));
      setCardImagesNoPlace(curr => ({ ...curr, [scheduleNo]: noPlace }));
    } catch (e) {
      console.error('단일 일정 이미지 새로고침 실패', e);
    }
  };

  // 일정 카드 연관장소 초기화 (불러오기 이후 1회 세팅)
  useEffect(() => {
    const cards = Array.isArray(itineraryData?.table1) ? itineraryData.table1 : [];
    if (!cards.length) return;
    setRelatedPlacesBySchedule((prev) => {
      const next = { ...prev };
      for (const r of cards) {
        const scheduleNo = Number(r.상품일정번호 ?? r.일정번호 ?? r.장소번호 ?? 0);
        if (!scheduleNo || Array.isArray(next[scheduleNo])) continue;
        next[scheduleNo] = parseRelatedPlacesFromRow(r);
      }
      return next;
    });
  }, [itineraryData?.table1]);

  const loadItinerary = async () => {
    if (!productId) return;
    try {
      setItineraryError('');
      setItineraryLoading(true);
      // 1단계: 일정타입 조회
      const step1 = await getItineraryType({ productNo: productId });
      const typeNo = Array.isArray(step1?.table) && step1.table.length ? step1.table[0]?.일정타입번호 : null;
      if (!typeNo) {
        setItineraryError('일정타입번호를 찾을 수 없습니다.');
        setItineraryLoading(false);
        return;
      }
      setItineraryTypeNo(typeNo);
      // 미팅장소 설정값 조회 (TRP_S_투어미팅장소설정값) -> table[0].투어미팅비고
      try {
        const meetResp = await getTourMeetingSetting({ productNo: productId, itineraryTypeNo: typeNo });
        const row0 = Array.isArray(meetResp?.table) ? meetResp.table[0] : Array.isArray(meetResp?.Table) ? meetResp.Table[0] : null;
        const remarkHtml = row0?.투어미팅비고 ?? row0?.MeetingRemark;
        setMeetingRemarkText(htmlToTextareaText(remarkHtml || ''));
      } catch (e) {
        // keep empty on failure
        setMeetingRemarkText('');
      }
      // 2단계: 타입으로 상세 조회
      const step2 = await getItineraryDetail({ productNo: productId, itineraryTypeNo: typeNo });
      setItineraryData({
        table: Array.isArray(step2?.table) ? step2.table : [],
        table1: Array.isArray(step2?.table1) ? step2.table1 : [],
        table2: Array.isArray(step2?.table2) ? step2.table2 : [],
        table3: Array.isArray(step2?.table3) ? step2.table3 : [],
        table4: Array.isArray(step2?.table4) ? step2.table4 : [],
        table5: Array.isArray(step2?.table5) ? step2.table5 : [],
      });
    } catch (e) {
      console.error(e);
      setItineraryError('일정 불러오기 실패');
    } finally {
      setItineraryLoading(false);
    }
  };

  // 이미지 삭제 등 변경 직후 최신 일정 데이터를 재조회하여 랜더링을 동기화
  const refreshItineraryDetail = async () => {
    try {
      const typeNo = itineraryTypeNo;
      if (!productId || !typeNo) return;
      const step2 = await getItineraryDetail({ productNo: productId, itineraryTypeNo: typeNo });
      setItineraryData({
        table: Array.isArray(step2?.table) ? step2.table : [],
        table1: Array.isArray(step2?.table1) ? step2.table1 : [],
        table2: Array.isArray(step2?.table2) ? step2.table2 : [],
        table3: Array.isArray(step2?.table3) ? step2.table3 : [],
        table4: Array.isArray(step2?.table4) ? step2.table4 : [],
        table5: Array.isArray(step2?.table5) ? step2.table5 : [],
      });
    } catch (e) {
      console.error('일정 상세 재조회 실패', e);
    }
  };

  // 최초 로드 및 상품 변경 시 일정 데이터 로딩
  useEffect(() => {
    loadItinerary();
  }, [productId]);

  // 일정표 탭: 불러온 데이터 기준으로 일차 목록 구성
  const [activeDayId, setActiveDayId] = useState(1);
  const dayNumbers = React.useMemo(() => {
    try {
      const nums = new Set();
      const t1 = Array.isArray(itineraryData?.table1) ? itineraryData.table1 : [];
      const t2 = Array.isArray(itineraryData?.table2) ? itineraryData.table2 : [];
      const t3 = Array.isArray(itineraryData?.table3) ? itineraryData.table3 : [];
      const t5 = Array.isArray(itineraryData?.table5) ? itineraryData.table5 : [];
      for (const r of t1) { const n = Number(r.일차); if (n > 0) nums.add(n); }
      for (const r of t2) { const n = Number(r.일차); if (n > 0) nums.add(n); }
      for (const r of t3) { const n = Number(r.일차); if (n > 0) nums.add(n); }
      for (const r of t5) { const n = Number(r.일차); if (n > 0) nums.add(n); }
      const arr = Array.from(nums).sort((a, b) => a - b);
      return arr.length ? arr : [1];
    } catch { return [1]; }
  }, [itineraryData]);
  useEffect(() => {
    if (!dayNumbers.includes(activeDayId)) {
      setActiveDayId(dayNumbers[0] || 1);
    }
  }, [dayNumbers]);
  // 상품 공통 항공편/호텔 (일차 구분 없이 전체 일정에 적용)
  const [globalFlights, setGlobalFlights] = useState([]); // { id, airline, flightNo, depart, arrive }

  useEffect(() => {
    const keys = Object.keys(editingSchedules || {});
    if (keys.length === 0) return; // nothing in edit mode
    const currentKey = keys[0];
    const isSaving = !!editingSchedules[currentKey]?.saving;

    const handlePointerDown = (e) => {
      if (isSaving) return; // don't interrupt save
      // Close if click target is not inside any editing schedule container
      const insideEditing = e.target?.closest && e.target.closest('.editing-schedule');
      if (!insideEditing) {
        // If the currently editing card is a temporary (unsaved) one and still blank, remove it
        try {
          const k = currentKey;
          const kNum = Number(k);
          const info = editingSchedules[k] || {};
          const isTemp = Number.isFinite(kNum) && kNum <= 0;
          const nameEmpty = !String(info?.name || '').trim();
          const contentEmpty = !String(info?.content || '').trim();
          if (isTemp && nameEmpty && contentEmpty) {
            // Remove the temp card from itineraryData.table1
            setItineraryData((prev) => {
              const t1 = Array.isArray(prev.table1) ? prev.table1.filter((row) => {
                const no = Number(row.상품일정번호 ?? row.일정번호 ?? row.장소번호 ?? 0);
                return no !== kNum;
              }) : [];
              return { ...prev, table1: t1 };
            });
            // Clean up temp anchor and related places state
            setTempAnchors((prev) => {
              const n = { ...prev };
              delete n[k];
              return n;
            });
            setRelatedPlacesBySchedule((prev) => {
              const n = { ...prev };
              delete n[kNum];
              return n;
            });
          }
        } catch { }
        // Exit edit mode
        setEditingSchedules({});
      }
    };

    const handleKeyDown = (e) => {
      if (isSaving) return; // don't interrupt save
      if (e.key !== 'Escape') return;
      try {
        const k = currentKey;
        const kNum = Number(k);
        const info = editingSchedules[k] || {};
        const isTemp = Number.isFinite(kNum) && kNum <= 0;
        const nameEmpty = !String(info?.name || '').trim();
        const contentEmpty = !String(info?.content || '').trim();
        if (isTemp && nameEmpty && contentEmpty) {
          setItineraryData((prev) => {
            const t1 = Array.isArray(prev.table1) ? prev.table1.filter((row) => {
              const no = Number(row.상품일정번호 ?? row.일정번호 ?? row.장소번호 ?? 0);
              return no !== kNum;
            }) : [];
            return { ...prev, table1: t1 };
          });
          setTempAnchors((prev) => {
            const n = { ...prev };
            delete n[k];
            return n;
          });
          setRelatedPlacesBySchedule((prev) => {
            const n = { ...prev };
            delete n[kNum];
            return n;
          });
        }
      } catch { }
      setEditingSchedules({});
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [editingSchedules]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!addMenuOpen) return;
      if (addMenuRef.current && !addMenuRef.current.contains(e.target)) {
        setAddMenuOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setAddMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [addMenuOpen]);

  // (moved) 제품 변경 시 콘텐츠 메인 리스트 자동 로딩은 상단으로 이동

  // Server image select modal state
  const [serverImgOpen, setServerImgOpen] = useState(false);
  const [serverCityNo, setServerCityNo] = useState('');
  const [serverCityName, setServerCityName] = useState('');
  const [allCities, setAllCities] = useState([]);
  const [serverImgs, setServerImgs] = useState([]);
  const [serverLoading, setServerLoading] = useState(false);
  const [serverSelected, setServerSelected] = useState([]); // 이미지번호 배열
  const [serverCityQuery, setServerCityQuery] = useState('');
  const [serverCityOpen, setServerCityOpen] = useState(false);

  // New schedule draft modal (create only after Save)
  const [newScheduleDraft, setNewScheduleDraft] = useState(null); // { dayNo, afterIndex, name, content, flightMode? }
  // Airports for flight modal
  const [airportOptions, setAirportOptions] = useState([]);
  const [airportLoading, setAirportLoading] = useState(false);
  // Airport select UI state (queries and open flags)
  const [depAirportQuery, setDepAirportQuery] = useState('');
  const [arrAirportQuery, setArrAirportQuery] = useState('');
  const [depAirportOpen, setDepAirportOpen] = useState(false);
  const [arrAirportOpen, setArrAirportOpen] = useState(false);

  // 초성 추출 유틸
  const getChosung = (str) => {
    if (!str) return '';
    const CHO = ['ㄱ', 'ㄲ', 'ㄴ', 'ㄷ', 'ㄸ', 'ㄹ', 'ㅁ', 'ㅂ', 'ㅃ', 'ㅅ', 'ㅆ', 'ㅇ', 'ㅈ', 'ㅉ', 'ㅊ', 'ㅋ', 'ㅌ', 'ㅍ', 'ㅎ'];
    let out = '';
    for (const ch of String(str)) {
      const code = ch.charCodeAt(0);
      if (code >= 0xAC00 && code <= 0xD7A3) {
        const idx = Math.floor((code - 0xAC00) / 588);
        out += CHO[idx] || ch;
      } else {
        out += ch;
      }
    }
    return out;
  };

  // Airport helpers (format and filter with 초성검색)
  const formatAirportOption = (a) => {
    const code = String(a?.코드 ?? '').trim();
    const name = String(a?.코드명 ?? '').trim();
    if (!code && !name) return '';
    return `(${code}) ${name}`.trim();
  };

  // Auto-format time while typing. Keep only digits, up to 4, and insert ':' after HH
  // Examples: '1' -> '1', '12' -> '12', '123' -> '12:3', '1234' -> '12:34'
  const autoFormatTime = (raw) => {
    const digits = String(raw || '').replace(/\D/g, '').slice(0, 4);
    if (digits.length <= 2) return digits;
    return `${digits.slice(0, 2)}:${digits.slice(2)}`;
  };

  const getAirportMatches = (q) => {
    const list = Array.isArray(airportOptions) ? airportOptions : [];
    const query = String(q || '').trim();
    if (!query) return list.slice(0, 100);
    const qLower = query.toLowerCase();
    const qCho = getChosung(query);
    const scored = list.map((a) => {
      const code = String(a?.코드 || '').trim();
      const name = String(a?.코드명 || '').trim();
      const codeLower = code.toLowerCase();
      const nameLower = name.toLowerCase();
      const nameCho = getChosung(name);
      let score = -1e9;
      if (codeLower === qLower) score = 1200; // exact code
      else if (nameLower === qLower) score = 1100; // exact name
      else if (codeLower.startsWith(qLower)) score = 1000; // code starts with
      else if (nameLower.startsWith(qLower)) score = 900; // name starts with
      else if (codeLower.includes(qLower)) score = 800; // code contains
      else if (nameLower.includes(qLower)) score = 700; // name contains
      else if (nameCho === qCho) score = 650; // exact 초성
      else if (nameCho.startsWith(qCho)) score = 600; // 초성 starts
      else if (nameCho.includes(qCho)) score = 550; // 초성 contains
      return { a, score };
    })
      .filter(x => x.score > -1e9)
      .sort((x, y) => y.score - x.score || String(x.a?.코드명 || '').length - String(y.a?.코드명 || '').length)
      .map(x => x.a);
    return scored;
  };

  const filteredDepAirports = React.useMemo(() => getAirportMatches(depAirportQuery), [airportOptions, depAirportQuery]);
  const filteredArrAirports = React.useMemo(() => getAirportMatches(arrAirportQuery), [airportOptions, arrAirportQuery]);

  const filteredCities = React.useMemo(() => {
    const q = (serverCityQuery || '').trim();
    if (!q) return allCities || [];
    const qLower = q.toLowerCase();
    const qCho = getChosung(q);
    const scored = (allCities || []).map((c) => {
      const name = String(c.코드명 || '').trim();
      const nameLower = name.toLowerCase();
      const nameCho = getChosung(name);
      // Scoring: higher is better
      let score = -1e9;
      if (name === q) score = 1000; // exact match
      else if (nameLower.startsWith(qLower)) score = 900; // starts with string
      else if (nameLower.includes(qLower)) score = 800; // contains string
      else if (nameCho === qCho) score = 700; // exact chosung
      else if (nameCho.startsWith(qCho)) score = 600; // starts with chosung
      else if (nameCho.includes(qCho)) score = 500; // contains chosung
      return { c, score };
    })
      // filter out those that didn't match at all
      .filter(x => x.score > -1e9)
      // secondary key: shorter names slightly preferred when score equal
      .sort((a, b) => b.score - a.score || String(a.c.코드명 || '').length - String(b.c.코드명 || '').length)
      .map(x => x.c);
    return scored;
  }, [allCities, serverCityQuery]);

  const extractDisplayName = (s) => {
    if (!s) return '';
    const parts = String(s).split('/');
    const last = parts[parts.length - 1];
    return (last || String(s)).trim();
  };

  // 매우 기본적인 HTML sanitizer (DOMPurify 미사용 환경 대비)
  // - <script>, <style> 태그 제거
  // - on* 이벤트 속성 제거 (onclick 등)
  // - javascript:, data:text/html 등 위험 URL 스킴 제거
  const sanitizeHtml = (html) => {
    try {
      let out = String(html);
      // Remove script/style blocks
      out = out.replace(/<\/(?:script|style)>/gi, '</removed>');
      out = out.replace(/<\s*(script|style)([\s\S]*?)>([\s\S]*?)<\s*\/\s*\1\s*>/gi, '');
      // Remove on* attributes
      out = out.replace(/\s+on[a-zA-Z]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '');
      // Neutralize javascript: and data:text/html
      out = out.replace(/(href|src)\s*=\s*(["'])\s*(javascript:|data:text\/html)/gi, '$1=$2about:blank');
      out = out.replace(/(href|src)\s*=\s*(javascript:|data:text\/html)/gi, '$1=about:blank');
      return out;
    } catch {
      return '';
    }
  };

  // 항목 객체에서 HTML로 보이는 필드 추출
  const extractHtmlCandidate = (item) => {
    if (!item) return '';
    // 우선순위 키 후보
    const keys = ['테그', '태그', 'html', 'HTML', '내용', 'content', 'body', 'desc', 'description'];
    for (const k of keys) {
      if (item && Object.prototype.hasOwnProperty.call(item, k)) {
        const v = item[k];
        if (typeof v === 'string' && /<[^>]+>/.test(v)) return v;
      }
    }
    // 문자열 필드 중 태그 포함하는 첫 값
    if (typeof item === 'string' && /<[^>]+>/.test(item)) return item;
    if (item && typeof item === 'object') {
      for (const v of Object.values(item)) {
        if (typeof v === 'string' && /<[^>]+>/.test(v)) return v;
      }
    }
    return '';
  };

  // 미리보기 HTML을 다른 전역 스타일과 격리하기 위한 iframe 컴포넌트
  const HtmlSandboxPreview = ({ html, height = 320 }) => {
    const safe = sanitizeHtml(html);
    const srcDoc = `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <style>
    /* 기본 reset & 박스 사이징 */
    *, *::before, *::after { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; }
    body { font: 14px/1.5 Pretendard, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Apple SD Gothic Neo', '맑은 고딕', 'Malgun Gothic', sans-serif; color: #111; }
    img, video, iframe { max-width: 100%; height: auto; }
    table { width: 100%; border-collapse: collapse; }
    a { color: inherit; }
    /* 사용자가 넣는 HTML이 폭을 강제로 줄이는 스타일을 무력화 */
    :root, body { width: 100% !important; }
    [style*="width:"] { max-width: 100% !important; }
  </style>
  <base target="_blank">
  </head>
  <body>${safe}</body>
  </html>`;
    return (
      <iframe
        title="content-preview"
        srcDoc={srcDoc}
        sandbox=""
        style={{ width: '100%', height, border: 'none', background: 'transparent' }}
      />
    );
  };

  useEffect(() => {
    const fetchName = async () => {
      try {
        const { companyNo } = getUserInfo() || {};
        if (!companyNo) return;
        const details = await getProductDetails(productId, companyNo);
        if (details?.상품명) setProductName(details.상품명);
        // 상품타입코드는 일정 추가 SP에서 038001/038002 체계를 기대하므로 제품 타입 리스트로 정규화
        try {
          const types = await getProductTypes(); // [{ 코드, 코드명 }]
          const nameFromDetails = details?.상품타입; // 예: '투어' / '호텔'
          const matched = Array.isArray(types) ? types.find(t => t?.코드명 === nameFromDetails) : null;
          if (matched?.코드) {
            setProductTypeCode(String(matched.코드)); // 기대값: 038001/038002
          } else if (details?.상품타입코드) {
            // 매칭 실패 시 기존 코드 유지
            setProductTypeCode(String(details.상품타입코드));
          }
        } catch (e) {
          // 타입 조회 실패 시 기존 코드로 폴백
          if (details?.상품타입코드) setProductTypeCode(String(details.상품타입코드));
        }
      } catch (e) {
        console.warn('Failed to fetch product name:', e);
      }
    };
    fetchName();
  }, [productId]);

  // 일정 추가 드래프트 열기: 저장 전까지 실제 카드 추가하지 않음
  const openAddScheduleDraft = (dayNo, afterIndex, opts = {}) => {
    setNewScheduleDraft({ dayNo, afterIndex, name: '', content: '', flightMode: !!opts.flightMode });
  };

  // Fetch airport options when flight modal opens
  useEffect(() => {
    const fetchAirports = async () => {
      try {
        setAirportLoading(true);
        const { companyNo } = getUserInfo() || {};
        if (!companyNo) return;
        const list = await getSupplierAirports({ companyNo });
        setAirportOptions(Array.isArray(list) ? list : []);
      } catch (e) {
        console.warn('공항 목록 조회 실패:', e);
        setAirportOptions([]);
      } finally {
        setAirportLoading(false);
      }
    };
    if (newScheduleDraft?.flightMode) {
      // Load only when entering flight mode; avoid refetch if we already have items
      if (!airportOptions || airportOptions.length === 0) {
        fetchAirports();
      }
    }
  }, [newScheduleDraft?.flightMode]);

  // 새 일정 저장: 백엔드에 생성 후 해당 위치에 삽입
  const saveNewScheduleDraft = async () => {
    if (!newScheduleDraft) return;
    const { dayNo, afterIndex, name, content, flightMode, flight } = newScheduleDraft;
    try {
      const { userNo } = getUserInfo() || {};

      // 항공 모드일 경우 항공 여정만 저장
      if (flightMode) {
        const f = flight || {};
        const dep = (f.depAirport || '').trim();
        const arr = (f.arrAirport || '').trim();
        const fno = (f.flightNo || '').trim();
        const dt = (f.depTime || '').trim();
        const at = (f.arrTime || '').trim();
        const dur = (f.duration || '').trim();

        // Times optional but if provided must be HH:MM
        const timeRe = /^([01]?\d|2[0-3]):[0-5]\d$/;
        if (dt && !timeRe.test(dt)) {
          alert('출발시간 형식이 올바르지 않습니다. 00:00 형식으로 입력해주세요.');
          return;
        }
        if (at && !timeRe.test(at)) {
          alert('도착시간 형식이 올바르지 않습니다. 00:00 형식으로 입력해주세요.');
          return;
        }

        // Airports basic validation
        if (!dep || !arr) {
          alert('출발/도착 공항을 선택해주세요.');
          return;
        }

        // Extract airport codes from the formatted strings
        const depAirportCode = extractAirportCode(dep);
        const arrAirportCode = extractAirportCode(arr);

        // Validate airport codes
        if (!depAirportCode || !arrAirportCode) {
          alert('출발/도착 공항 코드를 추출할 수 없습니다. 공항을 다시 선택해주세요.');
          return;
        }

        // Prepare flight data in the correct format directly
        // Format: [{"일차":1,"순서":1,"편명":"oz2211","출발공항":"ICN","도착공항":"DAD","구분":"217001","출발시간":"10:25","도착시간":"13:25","소요시간":"05:00"}]
        const flightData = {
          일차: Number(dayNo),
          순서: 1, // Default order
          편명: fno,
          출발공항: depAirportCode,
          도착공항: arrAirportCode,
          구분: '217001', // Default flight type code
          출발시간: dt,
          도착시간: at,
          소요시간: dur
        };

        try {
          // Get existing flight itinerary data
          const itineraryDetail = await getItineraryDetail({
            productNo: productId,
            itineraryTypeNo
          });

          // Extract existing flight itinerary from the response
          let existingFlightItinerary = [];

          // Check each table for flight itinerary data
          const tables = ['table', 'table1', 'table2', 'table3', 'table4', 'table5'];
          for (const tableName of tables) {
            const table = itineraryDetail[tableName];
            if (Array.isArray(table) && table.length > 0) {
              // Look for flight itinerary data in the first row of each table
              const firstRow = table[0];
              if (firstRow && firstRow.항공여정) {
                try {
                  // Try to parse the JSON string
                  const parsed = JSON.parse(firstRow.항공여정);
                  if (Array.isArray(parsed)) {
                    // Convert the existing flight itinerary to the required format if needed
                    // Expected format: [{"일차":1,"순서":1,"편명":"oz2211","출발공항":"ICN","도착공항":"DAD","구분":"217001","출발시간":"10:25","도착시간":"13:25","소요시간":"05:00"}]

                    // Map the existing data to the required format
                    existingFlightItinerary = parsed.map((item, index) => ({
                      일차: item.일차 || 1,
                      순서: item.순서 || (index + 1),
                      편명: item.편명 || '',
                      // Use the airport code fields if available, otherwise use the airport fields
                      출발공항: item.출발공항코드 || extractAirportCode(item.출발공항) || item.출발공항 || '',
                      도착공항: item.도착공항코드 || extractAirportCode(item.도착공항) || item.도착공항 || '',
                      구분: item.구분 || '217001',
                      출발시간: item.출발시간 || '',
                      도착시간: item.도착시간 || '',
                      소요시간: item.소요시간 || ''
                    }));
break;
                  }
                } catch (parseError) {
                  console.warn('항공여정 JSON 파싱 실패:', parseError);
                }
              }
            }
          }

          // Determine the highest sequence number in the existing flight itinerary
          let maxSequence = 0;
          if (existingFlightItinerary.length > 0) {
            maxSequence = Math.max(...existingFlightItinerary.map(item => Number(item.순서) || 0));
          }

          // Update the sequence number for the new flight data
          flightData.순서 = maxSequence + 1;

          // Ensure the day number is a number
          flightData.일차 = Number(flightData.일차);

          // Add the new flight data to the existing flight itinerary
          const combinedFlightItinerary = [...existingFlightItinerary, flightData];

          // Sort the combined flight itinerary by day and sequence
          combinedFlightItinerary.sort((a, b) => {
            const dayDiff = Number(a.일차) - Number(b.일차);
            return dayDiff !== 0 ? dayDiff : Number(a.순서) - Number(b.순서);
          });
// Save the combined flight itinerary - only call this procedure for flight mode
          await saveFlightItinerary({
            productNo: productId,
            itineraryTypeNo,
            flightItinerary: combinedFlightItinerary
          });
// 최신 데이터 재조회 및 우측 미리보기 새로고침
          try {
            await loadItinerary();
          } finally {
            setIframeKey((k) => k + 1);
          }
          setNewScheduleDraft(null);
        } catch (flightError) {
          console.error('항공 여정 저장 실패:', flightError);
          alert('항공 여정을 저장하지 못했습니다. 다시 시도해주세요.');
        }
        return; // 항공 모드일 경우 여기서 종료
      }

      // 일반 일정 모드 (항공 모드가 아닌 경우)
      // Validate and prepare defaults
      let finalName = name || '';
      let finalContent = content || '';

      if (!String(finalName).trim()) {
        alert('일정명을 입력해주세요.');
        return;
      }

      // 1) 백엔드에 신규 일정 생성 (일차 및 위치 기반)
      const addDs = await addItinerarySchedule({
        productNo: productId,
        itineraryTypeNo,
        day: dayNo,
        productTypeCode,
        name: finalName,
        selectedScheduleNo: (() => {
          // 기준 앵커: afterIndex의 기존 카드
          try {
            const t1 = Array.isArray(itineraryData?.table1) ? itineraryData.table1 : [];
            const dayRows = t1.filter(r => Number(r.일차) === Number(dayNo));
            if (afterIndex >= 0 && afterIndex < dayRows.length) {
              const anchorRow = dayRows[afterIndex];
              return Number(anchorRow?.상품일정번호 ?? anchorRow?.일정번호 ?? anchorRow?.장소번호 ?? 0) || '';
            }
          } catch { }
          return '';
        })(),
      });
      const newNo = Number(addDs?.table?.[0]?.상품일정번호 || 0);
      if (!newNo) throw new Error('신규 일정 번호를 가져오지 못했습니다.');

      // 2) 콘텐츠 저장
      await saveItineraryContent({
        scheduleNo: newNo,
        itineraryTypeNo,
        productNo: productId,
        name: finalName,
        content: finalContent,
        userNo,
      });

      // 3) 로컬 상태 삽입 (해당 day의 afterIndex 다음 위치)
      setItineraryData((prev) => {
        const t1 = Array.isArray(prev.table1) ? prev.table1.slice() : [];
        const indices = [];
        t1.forEach((row, i) => { if (Number(row.일차) === Number(dayNo)) indices.push(i); });
        let insertGlobalIndex = t1.length;
        if (indices.length > 0) {
          const dayPos = Math.min(Math.max(afterIndex + 1, 0), indices.length);
          insertGlobalIndex = dayPos < indices.length ? indices[dayPos] : (indices[indices.length - 1] + 1);
        }
        const newRow = { 상품일정번호: newNo, 일차: dayNo, 일정명: finalName || '', 일정내용: finalContent || '' };
        t1.splice(insertGlobalIndex, 0, newRow);
        return { ...prev, table1: t1 };
      });

      // 최신 데이터 재조회 및 우측 미리보기 새로고침
      try {
        await loadItinerary();
      } finally {
        setIframeKey((k) => k + 1);
      }

      setNewScheduleDraft(null);
    } catch (e) {
      console.error('새 일정 저장 실패', e);
      alert('새 일정을 저장하지 못했습니다. 다시 시도해주세요.');
    }
  };

  // Convenience: open draft for a new flight schedule at end; create on Save
  const addFlightScheduleAtEnd = async (dayNo) => {
    openAddScheduleDraft(dayNo, -1, { flightMode: true });
  };

  // Fetch representative images for the product
  useEffect(() => {
    let mounted = true;
    const run = async () => {
      try {
        setRepLoading(true);
        const imgs = await getRepresentativeImages(productId);
        if (!mounted) return;
        setRepImages(Array.isArray(imgs) ? imgs : []);
      } catch (e) {
        console.warn('대표이미지 조회 실패:', e);
        if (mounted) setRepImages([]);
      } finally {
        if (mounted) setRepLoading(false);
      }
    };
    if (productId) run();
    return () => { mounted = false; };
  }, [productId]);

  const openBasicSettings = async () => {
    try {
      setModalLoading(true);
      const { companyNo, userNo } = getUserInfo() || {};
      const details = await getProductDetails(productId, companyNo);
      if (details) {
        setSelectedProduct(details);
        setIsModalOpen(true);
      } else {
        // 신규 상품: 상세가 없으면 기본정보를 최소값으로 초기화하여 DB 레코드를 만든 뒤 재조회한다.
        try {
          const basicInfo = {
            상품번호: productId,
            상품명: productName || '',
            노출여부: false,
          };
          await saveProductBasicInfo({ companyNo, userNo, productNo: productId, basicInfo, keywords: '' });
          const refreshed = await getProductDetails(productId, companyNo).catch(() => null);
          if (refreshed) {
            setSelectedProduct(refreshed);
          } else {
            setSelectedProduct(basicInfo);
          }
          setIsModalOpen(true);
        } catch (_) {
          // 초기화 실패 시에도 편집은 가능하도록 최소 정보로 연다.
          setSelectedProduct({ 상품번호: productId, 상품명: productName || '' });
          setIsModalOpen(true);
        }
      }
    } catch (e) {
      console.error(e);
      // 오류가 발생해도 신규 상품 편집을 진행할 수 있도록 최소 정보로 모달을 연다.
      setSelectedProduct({ 상품번호: productId, 상품명: productName || '' });
      setIsModalOpen(true);
    } finally {
      setModalLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProduct(null);
  };

  const handleSaveSettings = async (updatedProduct) => {
    try {
      setModalLoading(true);
      const { companyNo, userNo } = getUserInfo() || {};
      const productNo = updatedProduct.상품번호 || selectedProduct?.상품번호 || productId;

      const [cities, managers, productTypes] = await Promise.all([
        getCities().catch(() => []),
        getManagers().catch(() => []),
        getProductTypes().catch(() => []),
      ]);

      const findByName = (list, name) => Array.isArray(list) ? list.find(i => i.코드명 === name) : undefined;
      const original = selectedProduct || {};

      const keywordStr = (updatedProduct.키워드 || '').trim();
      const keywordArr = keywordStr ? keywordStr.split(',').map(k => k.trim()).filter(Boolean) : [];

      const cityName = updatedProduct.도시 ?? original.도시;
      const cityItem = findByName(cities, cityName);
      const cityNo = cityItem?.코드 ?? original.도시번호;

      const productTypeName = updatedProduct.상품타입 ?? original.상품타입;
      const productTypeItem = findByName(productTypes, productTypeName);
      const productTypeCode = productTypeItem?.코드 ?? original.상품타입코드;

      const pdName = updatedProduct.상품담당 ?? original.상품담당;
      const rdName = updatedProduct.예약담당 ?? original.예약담당;
      const pdItem = findByName(managers, pdName);
      const rdItem = findByName(managers, rdName);
      const pdNo = pdItem?.코드 ?? original.상품담당번호;
      const rdNo = rdItem?.코드 ?? original.예약담당번호;

      const basicInfo = {
        ...original,
        상품번호: productNo,
        상품명: updatedProduct.상품명 ?? original.상품명,
        도시번호: cityNo,
        도시: cityName,
        상품타입: productTypeName,
        상품타입코드: productTypeCode,
        상품코드: updatedProduct.상품코드 ?? original.상품코드,
        상품코드룰사용: original.상품코드룰사용 ?? false,
        상품담당: pdName,
        상품담당번호: pdNo,
        예약담당: rdName,
        예약담당번호: rdNo,
        디폴트수배담당: (original.디폴트수배담당 ?? original.예약담당 ?? original.상품담당) || '예약담당',
        소요타입: original.소요타입 ?? original.소요시간타입 ?? original.소요단위 ?? '',
        소요: original.소요 ?? original.소요단위 ?? '',
        소요시간: updatedProduct.소요시간 ?? original.소요시간 ?? 0,
        통화코드: original.통화코드 ?? '',
        통화: original.통화 ?? '',
        가이드타입: original.가이드타입 ?? '',
        가이드타입코드: original.가이드타입코드 ?? '',
        카테고리: original.카테고리 ?? '',
        카테고리코드: original.카테고리코드 ?? '',
        노출여부: typeof updatedProduct.노출여부 === 'boolean' ? updatedProduct.노출여부 : (original.노출여부 ?? false),
        키워드: keywordArr,
        상품뱃지: updatedProduct.상품뱃지 ?? original.상품뱃지,
      };

      await saveProductBasicInfo({
        companyNo,
        userNo,
        productNo,
        basicInfo,
        keywords: keywordArr.join(','),
      });

      // 저장 후 최신 상세 재조회
      const refreshed = await getProductDetails(productNo, companyNo);
      if (refreshed) setSelectedProduct(refreshed);
      // 새로 저장된 내용을 반영하도록 미리보기 리프레시
      setIframeKey((k) => k + 1);
      alert('성공적으로 저장되었습니다.');

      return true;
    } catch (e) {
      console.error('Failed to save product details:', e);
      alert('저장에 실패했습니다. 다시 시도해주세요.');

      return false;
    } finally {
      setModalLoading(false);
    }
  };

  // Print Preview Handler
  const [pdfExporting, setPdfExporting] = useState(false);
  const handleExportPDF = async () => {
    try {
      setPdfExporting(true);

      // Fetch fresh data from both procedures
      const [contentData, itineraryDetailData] = await Promise.all([
        getContentMainList({ productNo: productId }),
        itineraryTypeNo ? getItineraryDetail({ productNo: productId, itineraryTypeNo }) : Promise.resolve(null)
      ]);

      // Helper function to extract HTML content (same logic as UI)
      const extractHtmlCandidate = (item) => {
        if (!item) return '';
        const keys = ['테그', '태그', 'html', 'HTML', '내용', 'content', 'body', 'desc', 'description'];
        for (const k of keys) {
          if (item && Object.prototype.hasOwnProperty.call(item, k)) {
            const v = item[k];
            if (typeof v === 'string' && /<[^>]+>/.test(v)) return v;
          }
        }
        if (typeof item === 'string' && /<[^>]+>/.test(item)) return item;
        if (item && typeof item === 'object') {
          for (const v of Object.values(item)) {
            if (typeof v === 'string' && /<[^>]+>/.test(v)) return v;
          }
        }
        return '';
      };

      // Prepare content list with parsed data
      const contentMainList = [];
      if (Array.isArray(contentData)) {
        for (const row of contentData) {
          try {
            const items = JSON.parse(row.컨텐츠 || '[]');
            if (Array.isArray(items) && items.length > 0) {
              items.forEach(item => {
                // 제목 추출 (UI와 동일한 로직)
                const titleKey = ['컨텐츠명', '콘텐츠명', '항목명', '제목', '타이틀', '이름', '명', 'title', 'name', 'text'].find(k => item && Object.prototype.hasOwnProperty.call(item, k));
                const contentName = titleKey ? item[titleKey] : (typeof item === 'string' ? item : row.코드명 || '상세 정보');

                // HTML 내용 추출 (UI와 동일한 로직)
                const contentHtml = extractHtmlCandidate(item);

                const contentNoKey = ['컨텐츠번호', '콘텐츠번호', 'contentNo', '번호', 'id', 'no'].find(k => item && Object.prototype.hasOwnProperty.call(item, k));
                const contentNo = contentNoKey ? item[contentNoKey] : null;

                // 콘텐츠 내용이 있는 경우만 추가
                if (contentHtml || contentName) {
                  contentMainList.push({
                    콘텐츠명: String(contentName || ''),
                    콘텐츠내용: String(contentHtml || ''),
                    콘텐츠번호: contentNo,
                  });
                }
              });
            }
          } catch (e) {
            console.error('Failed to parse content:', e, row);
          }
        }
      }
// Get product basic info
      const { companyNo } = getUserInfo() || {};
      const productDetails = await getProductDetails(productId, companyNo).catch(() => null);

      const productInfo = {
        상품번호: productId,
        상품명: productName || productDetails?.상품명 || '여행 상품',
        상품코드: productDetails?.상품코드,
        도시명: productDetails?.도시,
        여행기간: productDetails?.소요시간 ? `${productDetails.소요시간}일` : '',
      };

      // Open print preview window
      await openTravelProductPrintPreview({
        contentMainList,
        itineraryData: itineraryDetailData,
        productInfo,
      });
    } catch (error) {
      console.error('Print preview failed:', error);
      alert('인쇄 미리보기 열기에 실패했습니다. 팝업 허용을 확인한 뒤 다시 시도해주세요.');
    } finally {
      setPdfExporting(false);
    }
  };

  return (
    <div className="relative flex h-full min-h-0 bg-gray-50">
      {isModalOpen && (
        <ProductSettingsModal
          product={selectedProduct}
          onClose={handleCloseModal}
          onSave={handleSaveSettings}
          onPreviewRefresh={() => setIframeKey((k) => k + 1)}
          loading={modalLoading}
        />
      )}
      <ProductSwitcherModal
        open={switcherOpen}
        onClose={() => setSwitcherOpen(false)}
        currentProductId={productId}
        onlyTour={true}
        onSelect={(id) => { setSwitcherOpen(false); navigate(`/detail-settings/${id}`); }}
      />
      {/* Left edge gradient to separate from global sidebar */}

      {/* Left Settings Panel */}
      <div className={`px-6 md:px-8 flex-1 min-w-[420px] bg-white overflow-auto scroll-smooth shadow-sm ${showPreview ? 'border-r border-r-gray-200' : ''}`}>
        {/* 상단 고정: 상품명, 상품번호, 바로가기를 하나의 스티키 블록으로 */}
        <div className="sticky top-0 z-30 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80 border-b mb-3">
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center gap-3 min-w-0">
              <button
                type="button"
                onClick={() => setSwitcherOpen(true)}
                className="text-left text-lg font-semibold text-gray-800 truncate hover:underline"
                title="다른 상품으로 이동"
              >
                {productName || '상세 페이지 설정'}
              </button>
              <span className="text-sm text-gray-600 shrink-0">(상품번호: {productId})</span>
            </div>

          </div>
          <div className="px-1 pb-2 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <button onClick={openBasicSettings} className="text-xs px-2 py-1.5 rounded bg-emerald-600 text-white hover:bg-gray-900">기본설정</button>
              {/* /price-stock 은 PriceStockRouter 인데 비호텔을 전부 투어 화면으로 보낸다.
                  단품은 일반상품 전용 화면으로 직접 보내야 한다. */}
              <Link
                to={`/general/price-stock/${productId}`}
                className="text-xs px-2 py-1.5 rounded border bg-white hover:bg-gray-50"
              >
                가격/재고
              </Link>
              <button
                type="button"
                className="text-xs px-2 py-1.5 rounded border bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                disabled
                title="현재 화면"
              >
                상세 페이지 설정
              </button>
              <Link
                to={`/general/orders/${encodeURIComponent(productId || '')}`}
                state={{ productName, productNo: productId }}
                className="text-xs px-2 py-1.5 rounded border bg-white hover:bg-gray-50"
              >
                주문내역
              </Link>
            </div>
            <button
              type="button"
              className="text-xs px-2 py-1.5 rounded border bg-white hover:bg-gray-50 flex items-center gap-1.5"
              onClick={handleExportPDF}
              disabled={pdfExporting}
              title="인쇄"
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 9V2h12v7" />
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                <rect x="6" y="14" width="12" height="8" />
              </svg>
              {pdfExporting ? '준비 중...' : '인쇄'}
            </button>
          </div>
        </div>

        <div className="space-y-4">
          {/* 대표이미지 목록 섹션 */}
          <section id="rep-section" className="pt-2">
            <div className="sticky top-10 z-10 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80">
              <div className="text-base font-semibold text-gray-800 mb-3 px-2 py-2 border-b border-gray-200">
                <h3 className="text-[16px] font-semibold tracking-wide text-gray-800 uppercase flex items-center gap-2">
                  {/* Camera icon */}
                  <svg className="w-4 h-4 text-gray-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h3l2-3h8l2 3h3a2 2 0 0 1 2 2z" />
                    <circle cx="12" cy="13" r="4" />
                  </svg>
                  대표이미지
                  <span className="inline-flex items-center justify-center min-w-[22px] h-[18px] px-1 rounded-full bg-gray-100 text-[11px] text-gray-700 border">
                    {repImages?.length || 0}
                  </span>
                </h3>
                <div className="flex items-center gap-2">
                  {repLoading && <span className="text-xs text-gray-500">불러오는 중...</span>}
                  {savingOrder && <span className="text-xs text-blue-600">순서 저장 중...</span>}
                  {uploadProgress && (
                    <span className="text-xs text-emerald-700">
                      업로드 {uploadProgress.current}/{uploadProgress.total}
                    </span>
                  )}
                </div>
              </div>

            </div>
            <div className="px-3 text-[11px] text-gray-500 mt-2 mb-2">드래그하여 순서를 바꿀 수 있어요.</div>
            {/* Hidden file input for local upload */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={async (e) => {
                const files = Array.from(e.target.files || []);
                if (!files.length) return;
                try {
                  setUploading(true);
                  setUploadProgress({ current: 0, total: files.length });
                  const { companyNo, userNo } = getUserInfo() || {};
                  const filePath = `${companyNo}/Product/${productId}/`;
                  for (let i = 0; i < files.length; i++) {
                    const file = files[i];
                    const fileName = file.name;
                    const fileBase64 = await new Promise((resolve, reject) => {
                      const reader = new FileReader();
                      reader.onload = () => {
                        const result = reader.result || '';
                        const base64 = String(result).includes(',') ? String(result).split(',')[1] : String(result);
                        resolve(base64);
                      };
                      reader.onerror = reject;
                      reader.readAsDataURL(file);
                    });
                    await uploadAllSizeS3({ fileBase64, fileName, filePath });
                    const imagePath = `${filePath}${fileName}`;
                    const regRes = await registerUploadedImage({
                      companyNo,
                      userNo,
                      productNo: productId,
                      fileName,
                      imagePath,
                    });
                    const imageNo = regRes?.table && regRes.table[0]?.이미지번호;
                    if (!imageNo) throw new Error('이미지번호를 찾을 수 없습니다.');
                    await saveRepresentativeImage({ imageNo, productNo: productId, userNo });
                    setUploadProgress({ current: i + 1, total: files.length });
                  }
                  setIframeKey((k) => k + 1);
                  const imgs = await getRepresentativeImages(productId);
                  setRepImages(Array.isArray(imgs) ? imgs : []);
                  e.target.value = '';
                } catch (err) {
                  console.error(err);
                  alert('대표이미지 업로드에 실패했습니다. 다시 시도해주세요.');
                } finally {
                  setUploading(false);
                  setUploadProgress(null);
                }
              }}
              disabled={uploading}
            />

            {repLoading ? (
              <ul className="px-3 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                {Array.from({ length: 10 }).map((_, idx) => (
                  <li key={`rep-skeleton-${idx}`} className="animate-pulse">
                    <div className="relative w-full pt-[56.25%] rounded-lg overflow-hidden border bg-gray-100" />
                  </li>
                ))}
              </ul>
            ) : (
              <ul className="px-3 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                {/* Add tile */}
                <li className="relative" ref={addMenuRef}>
                  <button
                    type="button"
                    className="group block w-full rounded-lg border-2 border-dashed border-gray-300 bg-white hover:border-gray-400 hover:bg-gray-50 transition-colors"
                    onClick={() => setAddMenuOpen((v) => !v)}
                    title="이미지 추가"
                  >
                    <div className="relative w-full pt-[56.25%] flex items-center justify-center">
                      <span className="absolute inset-0 flex items-center justify-center text-gray-400 group-hover:text-gray-500 text-3xl">+</span>
                    </div>
                  </button>
                  {addMenuOpen && (
                    <div className="absolute z-20 mt-1 w-36 bg-white border rounded shadow">
                      <button
                        type="button"
                        className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50"
                        onClick={() => {
                          setAddMenuOpen(false);
                          if (fileInputRef.current) fileInputRef.current.click();
                        }}
                      >내 PC 이미지</button>
                      <button
                        type="button"
                        className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50"
                        onClick={async () => {
                          setAddMenuOpen(false);
                          try {
                            setServerImgOpen(true);
                            setServerLoading(true);
                            const pc = await getProductCity({ productNo: productId }).catch(() => null);
                            const defaultCityNo = pc?.도시번호 ? String(pc.도시번호) : '';
                            const defaultCityName = pc?.도시명 || '';
                            setServerCityNo(defaultCityNo);
                            setServerCityName(defaultCityName);
                            const { companyNo } = getUserInfo() || {};
                            const cityList = await getSupplierCities({ companyNo }).catch(() => []);
                            setAllCities(Array.isArray(cityList) ? cityList : []);
                            if (defaultCityNo) {
                              const imgs = await getCommonImagesByCity({ cityNo: defaultCityNo });
                              setServerImgs(Array.isArray(imgs) ? imgs : []);
                            } else {
                              setServerImgs([]);
                            }
                            setServerSelected([]);
                          } catch (e) {
                            console.error(e);
                            alert('서버 이미지 목록을 불러오지 못했습니다.');
                          } finally {
                            setServerLoading(false);
                          }
                        }}
                      >공용 이미지</button>
                      <button
                        type="button"
                        className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50"
                        onClick={async () => {
                          setAddMenuOpen(false);
                          try {
                            setGalleryOpen(true);
                            setGalleryLoading(true);
                            const { companyNo } = getUserInfo() || {};
                            const imgs = await getProductGallery({ companyNo, productNo: productId }).catch(() => []);
                            setGalleryImgs(Array.isArray(imgs) ? imgs : []);
                            setGallerySelected([]);
                          } catch (e) {
                            console.error(e);
                            alert('상품 갤러리 이미지를 불러오지 못했습니다.');
                          } finally {
                            setGalleryLoading(false);
                          }
                        }}
                      >상품 갤러리</button>
                    </div>
                  )}
                </li>
                <DndContext
                  collisionDetection={closestCenter}
                  onDragStart={({ active }) => setRepActiveId(active?.id ?? null)}
                  onDragEnd={async ({ active, over }) => {
                    setRepActiveId(null);
                    if (!over || active.id === over.id) return;
                    const ids = (repImages || []).map((it) => String(it.이미지번호));
                    const from = ids.indexOf(String(active.id));
                    const to = ids.indexOf(String(over.id));
                    if (from < 0 || to < 0) return;
                    const next = arrayMove(repImages, from, to);
                    const prev = repImages;
                    setRepImages(next);
                    try {
                      setSavingOrder(true);
                      // If the first image changed, update representative image first
                      try {
                        const currFirst = (prev && prev[0] && prev[0].이미지번호) || null;
                        const newFirst = (next && next[0] && next[0].이미지번호) || null;
                        if (newFirst && String(newFirst) !== String(currFirst)) {
                          const { userNo } = getUserInfo() || {};
                          await saveRepresentativeImage({ imageNo: Number(newFirst), productNo: productId, userNo });
                        }
                      } catch (e) { console.error(e); }
                      const orderList = next.map((it, idx) => ({ 이미지번호: Number(it.이미지번호), 순서: idx + 1 }));
                      await saveRepresentativeImageOrder({ productNo: productId, orderList });
                      setIframeKey((k) => k + 1);
                    } catch (err) {
                      console.error(err);
                      alert('순서 저장에 실패했습니다.');
                      setRepImages(prev);
                    } finally {
                      setSavingOrder(false);
                    }
                  }}
                  onDragCancel={() => setRepActiveId(null)}
                >
                  <SortableContext
                    items={(repImages || []).map((it) => String(it.이미지번호))}
                    strategy={rectSortingStrategy}
                  >
                    {(repImages || []).map((img, index) => (
                      <SortableRepItem
                        key={img.이미지번호}
                        id={String(img.이미지번호)}
                        index={index}
                        img={img}
                        onDelete={async (ev) => {
                          ev?.stopPropagation?.();
                          const ok = window.confirm('선택한 대표 이미지를 삭제하시겠습니까?');
                          if (!ok) return;
                          try {
                            const { userNo } = getUserInfo() || {};
                            await deleteRepresentativeImage({ imageNo: img.이미지번호, productNo: productId, userNo });
                            const imgs = await getRepresentativeImages(productId);
                            setRepImages(Array.isArray(imgs) ? imgs : []);
                            setIframeKey((k) => k + 1);
                          } catch (e) {
                            console.error(e);
                            alert('이미지 삭제에 실패했습니다. 다시 시도해주세요.');
                          }
                        }}
                        onMakeCover={async (ev) => {
                          ev?.stopPropagation?.();
                          try {
                            const next = arrayMove(repImages, index, 0);
                            setRepImages(next);
                            setSavingOrder(true);
                            // Make the selected image the representative first
                            try {
                              const { userNo } = getUserInfo() || {};
                              const newFirst = (next && next[0] && next[0].이미지번호) || null;
                              if (newFirst) {
                                await saveRepresentativeImage({ imageNo: Number(newFirst), productNo: productId, userNo });
                              }
                            } catch (e) { console.error(e); }
                            const orderList = next.map((it, idx) => ({ 이미지번호: Number(it.이미지번호), 순서: idx + 1 }));
                            await saveRepresentativeImageOrder({ productNo: productId, orderList });
                            setIframeKey((k) => k + 1);
                          } catch (err) {
                            console.error(err);
                            alert('표지 설정에 실패했습니다.');
                          } finally {
                            setSavingOrder(false);
                          }
                        }}
                      />
                    ))}
                  </SortableContext>
                  <DragOverlay>
                    {repActiveId != null ? (
                      <div className="w-32 h-20 rounded border bg-white shadow overflow-hidden">
                        <img
                          src={(repImages.find((it) => String(it.이미지번호) === String(repActiveId)) || {}).이미지경로}
                          alt="drag-overlay"
                          className="w-full h-full object-cover"
                          draggable={false}
                        />
                      </div>
                    ) : null}
                  </DragOverlay>
                </DndContext>
              </ul>
            )}
          </section>

          {/* 선택옵션 편집 모달 */}
          {selectEditOpen && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center">
              <div className="absolute inset-0 bg-black/30" onClick={() => setSelectEditOpen(false)} />
              <div className="relative bg-white rounded-lg shadow-xl w-[560px] max-w-[95vw]">
                <div className="px-4 py-3 border-b flex items-center justify-between">
                  <div className="text-sm font-semibold">선택옵션 수정</div>
                  <button className="text-gray-500 hover:text-gray-700" onClick={() => setSelectEditOpen(false)}>✕</button>
                </div>
                <div className="p-4 space-y-3 text-sm">
                  {selectEditError && <div className="text-xs text-red-600">{selectEditError}</div>}
                  <div className="grid grid-cols-3 gap-3 items-center">
                    <label className="text-gray-600">한글명</label>
                    <input className="col-span-2 border rounded px-2 py-1" value={selectEditForm.name} onChange={(e) => setSelectEditForm(f => ({ ...f, name: e.target.value }))} />
                    <label className="text-gray-600">설명</label>
                    <textarea className="col-span-2 border rounded px-2 py-1 min-h-[72px]" value={selectEditForm.desc} onChange={(e) => setSelectEditForm(f => ({ ...f, desc: e.target.value }))} />
                    <label className="text-gray-600">요금부과방식</label>
                    <select className="col-span-2 border rounded px-2 py-1" value={selectEditForm.chargeModeCode} onChange={(e) => setSelectEditForm(f => ({ ...f, chargeModeCode: e.target.value }))}>
                      <option value="066001">수량</option>
                      <option value="066003">인원 전체</option>
                    </select>
                    <label className="text-gray-600">성인요금</label>
                    <input type="text" inputMode="numeric" className="col-span-2 border rounded px-2 py-1" value={selectEditForm.adultFeeStr} onChange={(e) => setSelectEditForm(f => ({ ...f, adultFeeStr: sanitizeMoneyInput(e.target.value) }))} />
                    <label className="text-gray-600">소아요금</label>
                    <input type="text" inputMode="numeric" className="col-span-2 border rounded px-2 py-1" value={selectEditForm.childFeeStr} onChange={(e) => setSelectEditForm(f => ({ ...f, childFeeStr: sanitizeMoneyInput(e.target.value) }))} />
                    <label className="text-gray-600">유아요금</label>
                    <input type="text" inputMode="numeric" className="col-span-2 border rounded px-2 py-1" value={selectEditForm.infantFeeStr} onChange={(e) => setSelectEditForm(f => ({ ...f, infantFeeStr: sanitizeMoneyInput(e.target.value) }))} />
                  </div>
                </div>
                <div className="px-4 py-3 border-t flex items-center justify-between gap-2">
                  <button className="px-3 py-1.5 text-xs rounded border border-red-300 text-red-600 hover:bg-red-50" onClick={deleteSelectOption} disabled={selectEditSaving}>삭제</button>
                  <div className="flex items-center gap-2">
                    <button className="px-3 py-1.5 text-xs rounded border bg-white hover:bg-gray-50" onClick={() => setSelectEditOpen(false)} disabled={selectEditSaving}>취소</button>
                    <button className="px-3 py-1.5 text-xs rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50" onClick={saveSelectEdit} disabled={selectEditSaving}>{selectEditSaving ? '저장 중...' : '저장'}</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 상품 갤러리 이미지 선택 모달 */}
          {galleryOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center">
              <div className="absolute inset-0 bg-black/40" onClick={() => setGalleryOpen(false)} />
              <div className="relative z-10 w-full max-w-4xl max-h-[80vh] overflow-hidden rounded-md border bg-white shadow-lg">
                <div className="flex items-center justify-between px-4 py-2 border-b bg-gray-50">
                  <h4 className="text-sm font-semibold">상품 갤러리</h4>
                  <button type="button" className="px-2 py-1 text-xs rounded border bg-white hover:bg-gray-50" onClick={() => setGalleryOpen(false)}>닫기</button>
                </div>
                <div className="p-3 overflow-auto" style={{ maxHeight: '65vh' }}>
                  {galleryLoading ? (
                    <div className="text-xs text-gray-500">불러오는 중...</div>
                  ) : (
                    <ul className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                      {(galleryImgs || []).map((img) => {
                        const id = Number(img.이미지번호 ?? img.imageNo ?? 0);
                        const src = img.이미지경로 || img.imagePath || '';
                        const selected = gallerySelected.includes(id);
                        return (
                          <li key={`gal-${id}`}>
                            <button
                              type="button"
                              className={`group relative block w-full pt-[56.25%] rounded border overflow-hidden ${selected ? 'ring-2 ring-blue-500' : 'hover:shadow'}`}
                              title={String(img.이미지명 || img.imageName || '갤러리 이미지')}
                              onClick={() => {
                                setGallerySelected((prev) => {
                                  if (!id) return prev;
                                  const has = prev.includes(id);
                                  if (has) return prev.filter((x) => x !== id);
                                  return [...prev, id];
                                });
                              }}
                            >
                              <img src={src} alt="gallery" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                              {selected && (
                                <span className="absolute top-1 left-1 z-10 inline-flex items-center justify-center px-1.5 h-5 rounded bg-blue-600 text-white text-[10px]">선택됨</span>
                              )}
                            </button>
                          </li>
                        );
                      })}
                      {(galleryImgs || []).length === 0 && (
                        <li className="text-xs text-gray-500">이미지가 없습니다.</li>
                      )}
                    </ul>
                  )}
                </div>
                <div className="flex items-center justify-between px-4 py-2 border-t bg-gray-50">
                  <div className="text-[11px] text-gray-600">선택: {gallerySelected.length}개</div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      className="px-3 py-1.5 text-xs rounded border bg-white hover:bg-gray-50"
                      onClick={() => setGalleryOpen(false)}
                    >취소</button>
                    <button
                      type="button"
                      className="px-3 py-1.5 text-xs rounded bg-blue-600 text-white hover:bg-primary-600 disabled:opacity-50"
                      disabled={galleryLoading || gallerySelected.length === 0}
                      onClick={async () => {
                        try {
                          const { userNo } = getUserInfo() || {};
                          // Apply each selected image as representative (append behavior handled by API/order saving)
                          for (const imageNo of gallerySelected) {
                            await saveRepresentativeImage({ imageNo, productNo: productId, userNo });
                          }
                          // Refresh list
                          setIframeKey((k) => k + 1);
                          const imgs = await getRepresentativeImages(productId);
                          setRepImages(Array.isArray(imgs) ? imgs : []);
                          setGalleryOpen(false);
                          setGallerySelected([]);
                        } catch (e) {
                          console.error(e);
                          alert('대표이미지로 설정하는 중 오류가 발생했습니다.');
                        }
                      }}
                    >선택 적용</button>
                  </div>
                </div>
              </div>
            </div>
          )}



          {/* 콘텐츠 메인 리스트 섹션 (자동 로딩, 하위 컨텐츠 표시) */}
          <section id="content-section" className="pt-16">
            <div className="sticky top-10 z-10 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80">
              <div className="text-base font-semibold text-gray-800 mb-3 px-2 py-2 border-b border-gray-200">
                <h3 className="text-[16px] font-semibold tracking-wide text-gray-800 uppercase flex items-center gap-2">
                  {/* Document icon */}
                  <svg className="w-4 h-4 text-gray-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                  상품 상세 정보
                </h3>
                {contentLoading && <span className="text-xs text-gray-500 ml-2">불러오는 중...</span>}
                {contentReorderSaving && <span className="text-xs text-gray-500 ml-2">저장 중...</span>}
              </div>
            </div>
            {contentError && <div className="text-xs text-red-600 mb-2">{contentError}</div>}
            <ul className="">
              {(contentMain || []).filter((row) => {
                try {
                  const name = String(row?.코드명 || '');
                  const compact = name.replace(/\s+/g, '');
                  return compact.includes('판매정보') || name.includes('판매');
                } catch { return false; }
              }).map((row, idx) => {
                let items = [];
                try {
                  const parsed = JSON.parse(row.컨텐츠 || '[]');
                  if (Array.isArray(parsed)) items = parsed;
                } catch { }
                const codeKey = row.코드 ?? String(idx);
                return (
                  <li key={`cm-${idx}`} className="px-3 py-2 text-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 truncate mr-3">
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-gray-400"></span>
                        <span className="font-medium">{row.코드명}</span>
                      </div>
                    </div>
                    <div className="mt-2 mb-6 p-2">
                      <DndContext
                        sensors={contentDnDSensors}
                        collisionDetection={closestCenter}
                        onDragEnd={async ({ active, over }) => {
                          if (!over || active.id === over.id) return;
                          const itemIds = items.map((it, i) => {
                            const key = ['컨텐츠번호', '콘텐츠번호', 'contentNo', '번호', 'id', 'no'].find(k => it && Object.prototype.hasOwnProperty.call(it, k));
                            const no = key ? Number(it[key]) : null;
                            return (Number.isFinite(no) && no > 0) ? String(no) : String(i);
                          });
                          const from = itemIds.indexOf(String(active.id));
                          const to = itemIds.indexOf(String(over.id));
                          if (from < 0 || to < 0) return;
                          try {
                            setContentReorderSaving(true);
                            const newItems = arrayMove(items, from, to);
                            // 1) Optimistically update UI for this section to prevent snap-back
                            setContentMain((prev) => {
                              try {
                                const next = Array.isArray(prev) ? [...prev] : [];
                                if (next[idx]) {
                                  next[idx] = { ...next[idx], 컨텐츠: JSON.stringify(newItems) };
                                }
                                return next;
                              } catch {
                                return prev;
                              }
                            });
                            const merged = (contentMain || []).flatMap((r, rIdx) => {
                              let arr = [];
                              try {
                                const p = JSON.parse(r.컨텐츠 || '[]');
                                if (Array.isArray(p)) arr = p;
                              } catch { }
                              const useArr = (rIdx === idx) ? newItems : arr;
                              const typeCode = r.코드 ? String(r.코드) : '';
                              return useArr.map((obj, i2) => {
                                const out = { ...(obj || {}) };
                                if (!out.컨텐츠종류 && typeCode) out.컨텐츠종류 = typeCode;
                                out.콘텐츠순서 = (i2 + 1);
                                if (typeof out.노출여부 === 'undefined') out.노출여부 = true;
                                if (typeof out.순서 === 'undefined') out.순서 = 100;
                                if (typeof out.컨텐츠유무 === 'undefined') out.컨텐츠유무 = 'Y';
                                return out;
                              });
                            });
                            await saveContentOrder({ productNo: productId, contentList: merged });
                            await loadContentMain();
                          } catch (e) {
                            console.error('콘텐츠 순서 저장 실패', e);
                            alert('콘텐츠 순서 저장에 실패했습니다. 잠시 후 다시 시도해주세요.');
                          } finally {
                            setContentReorderSaving(false);
                          }
                        }}
                      >
                        <SortableContext items={(() => {
                          const ids = items.map((it, i) => {
                            const key = ['컨텐츠번호', '콘텐츠번호', 'contentNo', '번호', 'id', 'no'].find(k => it && Object.prototype.hasOwnProperty.call(it, k));
                            const no = key ? Number(it[key]) : null;
                            return (Number.isFinite(no) && no > 0) ? String(no) : String(i);
                          });
                          return ids;
                        })()} strategy={rectSortingStrategy}>
                          <ul className="flex flex-col gap-2">
                            {items.map((it, i) => {
                              const titleKey = ['컨텐츠명', '콘텐츠명', '항목명', '제목', '타이틀', '이름', '명', 'title', 'name', 'text'].find(k => it && Object.prototype.hasOwnProperty.call(it, k));
                              const title = titleKey ? it[titleKey] : (typeof it === 'string' ? it : '항목');
                              const htmlRaw = extractHtmlCandidate(it);
                              const contentNoKey = ['컨텐츠번호', '콘텐츠번호', 'contentNo', '번호', 'id', 'no'].find(k => it && Object.prototype.hasOwnProperty.call(it, k));
                              const detectedContentNo = contentNoKey ? Number(it[contentNoKey]) : null;
                              const sortableId = (Number.isFinite(detectedContentNo) && detectedContentNo > 0) ? String(detectedContentNo) : String(i);
                              return (
                                <SortableContentCard key={`cm-${idx}-item-${sortableId}`} id={sortableId}>
                                  <div className="flex items-stretch">
                                    <button
                                      type="button"
                                      className="w-full flex items-stretch text-left overflow-hidden"
                                      onClick={() => openEditModal({
                                        contentNo: detectedContentNo,
                                        title: title || '콘텐츠',
                                        html: htmlRaw || '',
                                        contentTypeCode: row.코드 || '',
                                      })}
                                    >
                                      {/* 왼쪽 타이틀 컬럼 (고정 폭) */}
                                      <div className="flex items-center w-44 max-w-xs flex-shrink-0 pr-3 border-r border-gray-200">
                                        <span
                                          className="text-[13px] font-medium text-gray-800 truncate"
                                          title={String(title || '')}
                                        >
                                          {title}
                                        </span>
                                      </div>
                                      {/* 오른쪽 본문 컬럼 */}
                                      <div className="flex-1 min-w-0 flex items-center gap-2 pl-3">
                                        {(() => {
                                          const textOnly = (() => {
                                            const raw = typeof htmlRaw === 'string' ? htmlRaw : '';
                                            // Remove style/script blocks and tags, collapse whitespace
                                            const noScripts = raw.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, ' ');
                                            const noStyles = noScripts.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, ' ');
                                            const noTags = noStyles.replace(/<[^>]+>/g, ' ');
                                            const normalized = noTags.replace(/\s+/g, ' ').trim();
                                            // 본문이 비어있거나 코드값만 있는 경우 공란 처리 (예: 040100, 040901 등 6자리 숫자 코드)
                                            if (!normalized || /^\d{6}$/.test(normalized)) return '';
                                            return normalized;
                                          })();
                                          return (
                                            <span
                                              className="flex-1 min-w-0 text-[12px] text-gray-600 truncate"
                                              title={textOnly}
                                            >
                                              {textOnly}
                                            </span>
                                          );
                                        })()}
                                        <span className="ml-2 text-xs text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity duration-150 shrink-0">
                                          편집
                                        </span>
                                      </div>
                                    </button>
                                  </div>
                                </SortableContentCard>
                              );
                            })}

                            <li className="self-center">
                              <button
                                type="button"
                                className="inline-flex items-center justify-center w-20 h-7 rounded-full border border-gray-300 bg-white text-gray-600 hover:border-gray-400 hover:text-gray-700"
                                title="콘텐츠 추가"
                                aria-label="콘텐츠 추가"
                                onClick={() => {
                                  setPreviewModalMode('create');
                                  setPreviewModalContentTypeCode(String(row.코드 || ''));
                                  const defaultTitle = `제목`;
                                  setPreviewModalTitle(defaultTitle);
                                  setPreviewModalTitleEdit('');
                                  setPreviewModalHtml('');
                                  setPreviewModalContentNo(null);
                                  setPreviewModalOpen(true);
                                }}
                              >+
                              </button>
                            </li>
                          </ul>
                        </SortableContext>
                      </DndContext>
                    </div>
                  </li>
                );
              })}
              {(!contentLoading && (!contentMain || contentMain.length === 0)) && (
                <li className="px-3 py-6 text-center text-xs text-gray-500">데이터가 없습니다.</li>
              )}
            </ul>
            {/* 선택옵션 표: 섹션 최하단에 배치 */}
            <div className="mt-6 px-3 pb-4 hidden">
              {selectOptionsLoading && (
                <div className="text-xs text-gray-500">선택옵션 불러오는 중...</div>
              )}
              {!selectOptionsLoading && selectOptionsError && (
                <div className="text-xs text-red-600">{selectOptionsError}</div>
              )}
              {!selectOptionsLoading && !selectOptionsError && (
                <div className="rounded-md border-0 bg-transparent">
                  <div className="mb-2 text-[14px] font-semibold text-gray-600 flex items-center gap-2"><span className="inline-block w-1.5 h-1.5 rounded-full bg-gray-400"></span><span>선택옵션 정보</span></div>
                  {(Array.isArray(selectOptions) && selectOptions.length > 0) ? (<>
                    <ul className="flex flex-col gap-2">
                      {(selectOptions || []).map((opt, i) => {
                        const korName = opt?.한글명 ?? opt?.서비스명 ?? opt?.명 ?? '';
                        const desc = String(opt?.설명 ?? '').replace(/\s+/g, ' ').trim();
                        return (
                          <li key={`opt-list-${i}`} className="text-sm">
                            <div className="group p-3 border border-gray-200 rounded-md bg-white hover:border-gray-300 transition-colors">
                              <div className="flex items-center gap-2">
                                <div className="flex-1 min-w-0">
                                  <button
                                    type="button"
                                    className="w-full flex items-center gap-2 text-left overflow-hidden whitespace-nowrap"
                                    onClick={() => openSelectEdit(opt)}
                                  >
                                    <span className="text-[13px] font-medium text-gray-800 truncate min-w-0 max-w-[100px]" title={String(korName || '')}>{korName}</span>
                                    <span className="flex-1 min-w-0 inline-flex items-center gap-2 overflow-hidden">
                                      {desc && <span className="text-gray-300 shrink-0">-</span>}
                                      <span className="text-[12px] text-gray-600 truncate" title={desc}>{desc}</span>
                                    </span>
                                    <span className="ml-2 shrink-0 w-8 text-right text-xs text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity duration-150">편집</span>
                                  </button>
                                </div>
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                    <div className="mt-2 flex justify-center">
                      <button
                        type="button"
                        className="inline-flex items-center justify-center w-20 h-7 rounded-full border border-gray-300 bg-white text-gray-600 hover:border-gray-400 hover:text-gray-700"
                        title="선택옵션 추가"
                        aria-label="선택옵션 추가"
                        onClick={openSelectCreate}
                      >+
                      </button>
                    </div>
                  </>) : (
                    <div className="px-3 py-2 text-xs text-gray-500">
                      <div className="mb-2">선택옵션 데이터가 없습니다.</div>
                      <div className="mt-2 flex justify-center">
                        <button
                          type="button"
                          className="inline-flex items-center justify-center w-20 h-7 rounded-full border border-gray-300 bg-white text-gray-600 hover:border-gray-400 hover:text-gray-700"
                          title="선택옵션 추가"
                          aria-label="선택옵션 추가"
                          onClick={openSelectCreate}
                        >+
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>

          {/* 일정표 편집 섹션 */}
          <section id="itinerary-section" className="pt-16 hidden">
            <div className="sticky top-10 z-10 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80">
              <div className="flex items-center justify-between text-base font-semibold text-gray-800 mb-3 px-2 py-2 border-b border-gray-200">
                <h3 className="text-[16px] font-semibold tracking-wide text-gray-800 uppercase flex items-center gap-2">
                  {/* Calendar icon */}
                  <svg className="w-4 h-4 text-gray-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                  일정표 편집
                </h3>
                {/* 인쇄 버튼 제거됨 */}
              </div>
            </div>
            <div className="px-3">
              {/* 미팅장소 입력/편집 (항상 표시) */}
              <div className="mb-0 p-0 bg-white">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-semibold flex items-center gap-2"><span className="inline-block w-1.5 h-1.5 rounded-full bg-gray-400"></span><span>미팅장소</span></div>
                  <div className="flex items-center gap-2">
                    {meetingRemarkError && (
                      <span className="text-xs text-red-600">{meetingRemarkError}</span>
                    )}
                    <button
                      type="button"
                      className="text-xs px-3 py-1.5 rounded bg-white border border-gray-200 text-gray-800 hover:bg-gray-100 disabled:opacity-50"
                      onClick={handleSaveMeetingRemark}
                      disabled={meetingRemarkSaving}
                    >{meetingRemarkSaving ? '저장 중...' : '저장'}</button>
                  </div>
                </div>
                <textarea
                  className="w-full border rounded px-3 py-2 text-sm min-h-[80px]"
                  placeholder="미팅장소를 입력하세요. (줄바꿈 지원)"
                  value={meetingRemarkText}
                  onChange={(e) => setMeetingRemarkText(e.target.value)}
                />

              </div>

              {/* 불러온 일정 미리보기 (읽기전용) */}
              {(itineraryData?.table?.length || itineraryData?.table1?.length || itineraryData?.table3?.length || itineraryData?.table4?.length) ? (
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-2">


                  </div>
                  {itineraryError && <div className="text-[12px] text-red-600 mb-2">{itineraryError}</div>}

                  {/* 키워드 표시: dnd-kit으로 부드러운 드래그 정렬 */}
                  {Array.isArray(visitKeywords) && (
                    <div className="mb-3">
                      <div className="flex items-center justify-between mb-4">
                        <div className="text-sm font-semibold flex items-center gap-2"><span className="inline-block w-1.5 h-1.5 rounded-full bg-gray-400"></span><span>주요 관광지 방문순서 설정</span></div>
                      </div>
                      {/* 리스트 */}
                      <div className="flex flex-wrap items-center gap-1 text-[12px] select-none px-2 py-4 bg-gray-200">
                        <DndContext
                          collisionDetection={closestCenter}
                          onDragStart={({ active }) => setVisitKwActiveId(active?.id ?? null)}
                          onDragEnd={({ active, over }) => {
                            setVisitKwActiveId(null);
                            if (!over || active.id === over.id) return;
                            const from = parseInt(String(active.id), 10);
                            const to = parseInt(String(over.id), 10);
                            if (Number.isNaN(from) || Number.isNaN(to)) return;
                            setVisitKeywords((prev) => {
                              const next = arrayMove(prev, from, to);
                              // 즉시 자동 저장
                              handleSaveVisitKeywords(next);
                              return next;
                            });
                          }}
                          onDragCancel={() => setVisitKwActiveId(null)}
                        >
                          <SortableContext
                            // id는 인덱스를 문자열로 사용 (정렬 후 자동 재생성)
                            items={visitKeywords.map((_, i) => String(i))}
                            strategy={horizontalListSortingStrategy}
                          >
                            {visitKeywords.map((kw, i) => (
                              <React.Fragment key={`kw-${i}`}>
                                {visitKwEditIndex === i ? (
                                  <input
                                    type="text"
                                    className="px-2 py-0.5 text-[12px] border rounded w-40"
                                    value={visitKwEditValue}
                                    onChange={(e) => setVisitKwEditValue(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') commitEditVisitKeyword();
                                      if (e.key === 'Escape') { setVisitKwEditIndex(null); setVisitKwEditValue(''); }
                                    }}
                                    onBlur={() => {
                                      if (visitKwEditingJustActivatedRef.current) return;
                                      commitEditVisitKeyword();
                                    }}
                                    autoFocus
                                  />
                                ) : (
                                  <SortableKeyword
                                    id={String(i)}
                                    label={kw}
                                    disabled={visitKwEditIndex != null}
                                    onClick={() => startEditVisitKeyword(i)}
                                    onDelete={() => handleDeleteVisitKeyword(i)}
                                  />
                                )}
                                {i < visitKeywords.length - 1 && (
                                  <span className="mx-1 text-gray-400 select-none">→</span>
                                )}
                              </React.Fragment>
                            ))}
                            {/* Add button as a chip at the end (always visible when not adding) */}
                            {!visitKwAdding && (
                              <button
                                type="button"
                                className="ml-1 px-2 py-0.5 rounded border bg-white text-gray-600 hover:bg-gray-50 text-[12px]"
                                title="키워드 추가"
                                onClick={() => { setVisitKwAdding(true); setNewVisitKeyword(''); }}
                              >
                                +
                              </button>
                            )}
                            {visitKwAdding && (
                              <div className="ml-1 inline-flex items-center gap-2">
                                <input
                                  type="text"
                                  className="px-2 py-1 text-[12px] border rounded w-48"
                                  placeholder="방문지 입력"
                                  value={newVisitKeyword}
                                  onChange={(e) => setNewVisitKeyword(e.target.value)}
                                  onKeyDown={(e) => { if (e.key === 'Enter') { handleAddVisitKeyword(); setVisitKwAdding(false); } if (e.key === 'Escape') { setVisitKwAdding(false); setNewVisitKeyword(''); } }}
                                  autoFocus
                                />
                                <button
                                  type="button"
                                  className="text-[11px] px-2 py-1 border rounded bg-blue-600 text-white hover:bg-blue-700"
                                  onClick={() => { handleAddVisitKeyword(); setVisitKwAdding(false); }}
                                >추가</button>
                                <button
                                  type="button"
                                  className="text-[11px] px-2 py-1 border rounded bg-white hover:bg-gray-50"
                                  onClick={() => { setVisitKwAdding(false); setNewVisitKeyword(''); }}
                                >취소</button>
                              </div>
                            )}
                          </SortableContext>
                          <DragOverlay>
                            {visitKwActiveId != null ? (
                              <span className="px-2 py-0.5 rounded border bg-white text-gray-800 shadow">
                                {visitKeywords[parseInt(String(visitKwActiveId), 10)]}
                              </span>
                            ) : null}
                          </DragOverlay>
                        </DndContext>
                        {/* 인라인 편집만 사용: 별도 편집 영역 제거 */}
                      </div>
                    </div>
                  )}
                  {/* 일정표 설정 타이틀 */}
                  <div className="flex items-center justify-between mt-6 mb-2 px-1">
                    <div className="text-sm font-semibold flex items-center gap-2"><span className="inline-block w-1.5 h-1.5 rounded-full bg-gray-400"></span><span>일정표 설정</span></div>
                  </div>
                  {/* 일차 탭 (상단) */}
                  <div className="flex items-center gap-2 px-1 mb-3 mt-6">
                    {dayNumbers.map((dn) => (
                      <button
                        key={dn}
                        type="button"
                        className={`px-3 py-1.5 rounded-full text-xs border ${activeDayId === dn ? 'bg-primary-600 text-white border-primary-600' : 'bg-white hover:bg-gray-50'}`}
                        onClick={() => setActiveDayId(dn)}
                        title={`${dn}일차로 전환`}
                      >{dn}일차</button>
                    ))}
                  </div>

                  {/* 일차별 표시 */}
                  {(() => {
                    return (
                      <div className="space-y-4">
                        {(activeDayId ? [activeDayId] : dayNumbers).map((dno) => {
                          const cards = (itineraryData.table1 || []).filter(r => Number(r.일차) === dno);
                          const hotels = (itineraryData.table5 || []).filter(r => Number(r.일차) === dno);
                          const mealRow = (itineraryData.table3 || []).find(r => Number(r.일차) === dno);
                          const mealsText = String(mealRow?.식사정보 || '').replaceAll('미정 /', '미정/').replaceAll('/ ', '/');
                          const meals = mealsText ? mealsText.split('/').map(s => s.trim()) : [];
                          return (
                            <div key={dno} className="bg-gray-200 rounded p-3">
                              <div className="text-sm font-semibold mb-2">{dno}일차</div>
                              <div className="gap-3">
                                <div className="md:col-span-2">
                                  <ul className="space-y-2">
                                    {cards.length ? cards.map((r, idx) => {
                                      const scheduleNo = Number(r.상품일정번호 ?? r.일정번호 ?? r.장소번호 ?? 0);
                                      const byPlace = scheduleNo ? (cardImagesByPlace[scheduleNo] || {}) : {};
                                      const noPlaceImgs = scheduleNo ? (cardImagesNoPlace[scheduleNo] || []) : [];
                                      const isEditingLocalForCard = !!editingSchedules[scheduleNo];
                                      return (
                                        <li
                                          key={idx}
                                          className={`group relative text-xs text-gray-800 rounded-lg border transition
                                            ${scheduleDrag.day === dno && scheduleDrag.over === idx ? 'ring-2 ring-blue-500' : ''}
                                            ${scheduleDrag.day === dno && scheduleDrag.from === idx ? 'opacity-60' : ''}
                                            ${isEditingLocalForCard ? 'bg-amber-50 border-amber-200 shadow-inner' : 'bg-white hover:bg-blue-50 hover:shadow hover:border-blue-200'}
                                            cursor-grab active:cursor-grabbing`}
                                          draggable
                                          onDragStart={(e) => {
                                            setScheduleDrag({ day: dno, from: idx, over: null });
                                            if (e.dataTransfer) {
                                              e.dataTransfer.effectAllowed = 'move';
                                              try { e.dataTransfer.setData('text/plain', String(idx)); } catch { }
                                            }
                                          }}
                                          onDragOver={(e) => { e.preventDefault(); if (scheduleDrag.day === dno) setScheduleDrag(s => ({ ...s, over: idx })); }}
                                          onDragEnter={() => { if (scheduleDrag.day === dno) setScheduleDrag(s => ({ ...s, over: idx })); }}
                                          onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setScheduleDrag(s => ({ ...s, over: null })); }}
                                          onDragEnd={() => setScheduleDrag({ day: null, from: null, over: null })}
                                          onDrop={async (e) => {
                                            e.preventDefault();
                                            let from = scheduleDrag.from;
                                            if (from === null && e.dataTransfer) {
                                              const v = e.dataTransfer.getData('text/plain');
                                              if (v) from = parseInt(v, 10);
                                            }
                                            if (from === null || Number.isNaN(from) || from === idx) return;
                                            // Reorder within this day's cards
                                            const dayCards = cards.slice();
                                            const [moved] = dayCards.splice(from, 1);
                                            dayCards.splice(idx, 0, moved);
                                            // Build new table1 preserving other days order
                                            const prevTable1 = itineraryData.table1 || [];
                                            const indices = [];
                                            prevTable1.forEach((row, i2) => { if (Number(row.일차) === dno) indices.push(i2); });
                                            const newTable1 = prevTable1.slice();
                                            indices.forEach((globalIdx, j) => { newTable1[globalIdx] = dayCards[j]; });
                                            const prevSnapshot = itineraryData.table1;
                                            setItineraryData(prev => ({ ...prev, table1: newTable1 }));
                                            setScheduleDrag({ day: null, from: null, over: null });
                                            try {
                                              setSavingScheduleOrder(true);
                                              const orderList = dayCards.map((it, iorder) => ({ 상품일정번호: Number(it.상품일정번호 ?? it.일정번호 ?? it.장소번호 ?? 0), 순서: iorder + 1 }));
                                              await saveItineraryOrder({ productNo: productId, itineraryTypeNo, dayNo: dno, orderList });
                                            } catch (err) {
                                              console.error('일정 순서 저장 실패', err);
                                              // revert
                                              setItineraryData(prev => ({ ...prev, table1: prevSnapshot }));
                                              alert('일정 순서 저장에 실패했습니다. 다시 시도해주세요.');
                                            } finally {
                                              setSavingScheduleOrder(false);
                                            }
                                          }}
                                          title="드래그하여 순서를 변경하세요"
                                        >
                                          <div ref={(() => { const scheduleNoLocal = Number(r.상품일정번호 ?? r.일정번호 ?? r.장소번호 ?? 0); return !!editingSchedules[scheduleNoLocal] ? editingContainerRef : null; })()} className={`bg-white rounded-lgborder border-gray-100 flex items-start gap-2 p-2 pb-5 relative ${(() => { const scheduleNoLocal = Number(r.상품일정번호 ?? r.일정번호 ?? r.장소번호 ?? 0); return !!editingSchedules[scheduleNoLocal] ? 'editing-schedule' : '' })()}`}>
                                            <div className="flex-1 min-w-0">
                                              {(() => {
                                                const scheduleNoLocal = Number(r.상품일정번호 ?? r.일정번호 ?? r.장소번호 ?? 0);
                                                const isEditing = !!editingSchedules[scheduleNoLocal];
                                                const nameVal = isEditing ? editingSchedules[scheduleNoLocal].name : (r.일정명 || r.서비스종류 || '일정');
                                                const contentVal = isEditing ? editingSchedules[scheduleNoLocal].content : (r.일정내용 || r.일정설명 || '');
                                                // 항공편 존재 여부 체크 (JSON 문자열)
                                                let hasFlight = false;
                                                try {
                                                  if (r?.항공편) {
                                                    const arr = JSON.parse(r.항공편);
                                                    hasFlight = Array.isArray(arr) && arr.length > 0;
                                                  }
                                                } catch { }
                                                return (
                                                  <div>
                                                    <div className="flex items-start justify-between gap-2 mt-7">
                                                      <div
                                                        className="flex-1 min-w-0 font-medium text-sm flex items-center gap-1"
                                                        onDoubleClick={() => {
                                                          // Double-click the title area to switch to this item's edit mode
                                                          setEditingSchedules({ [scheduleNoLocal]: { name: r.일정명 || '', content: (r.일정내용 || r.일정설명 || '') } });
                                                        }}
                                                      >
                                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 text-gray-500" aria-hidden="true">
                                                          <path d="M12 2.25c-3.728 0-6.75 3.022-6.75 6.75 0 4.448 4.98 9.27 6.17 10.35a.75.75 0 0 0 1.16 0c1.19-1.08 6.17-5.902 6.17-10.35 0-3.728-3.022-6.75-6.75-6.75Zm0 9.75a3 3 0 1 1 0-6 3 3 0 0 1 0 6Z" />
                                                        </svg>
                                                        {isEditing ? (
                                                          <input
                                                            className="w-full text-sm font-medium px-2 py-1 border rounded"
                                                            value={nameVal}
                                                            onChange={(e) => setEditingSchedules((prev) => ({ ...prev, [scheduleNoLocal]: { ...prev[scheduleNoLocal], name: e.target.value } }))}
                                                            placeholder={flightModeBySchedule[scheduleNoLocal] ? '항공 정보를 입력하면 자동완성 됩니다' : '일정명'}
                                                          />
                                                        ) : (
                                                          <span className="truncate">{nameVal}</span>
                                                        )}
                                                      </div>
                                                      <div className="flex items-center gap-1 shrink-0 w-[130px] justify-end">
                                                        {!isEditing ? (
                                                          <button
                                                            type="button"
                                                            className="px-2 py-1 text-[11px] rounded border bg-blue-100 hover:bg-gray-50 transition-opacity opacity-0 group-hover:opacity-100"
                                                            onClick={() => {
                                                              // Switch edit mode to this schedule only
                                                              setEditingSchedules({ [scheduleNoLocal]: { name: r.일정명 || '', content: (r.일정내용 || r.일정설명 || '') } });
                                                              // If this row has existing flight, open flight edit with prefilled values
                                                              const f = parseFlightFromRow(r);
                                                              if (f) {
                                                                prepareFlightDraftFromRow(scheduleNoLocal, r);
                                                              } else {
                                                                // keep whatever current mode is, but do not force flight mode
                                                              }
                                                            }}
                                                          >수정</button>
                                                        ) : (
                                                          <>
                                                            <button
                                                              type="button"
                                                              className="px-2 py-1 text-[11px] rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                                                              disabled={!!editingSchedules[scheduleNoLocal]?.saving}
                                                              onClick={async () => {
                                                                try {
                                                                  await saveScheduleEdits(scheduleNoLocal, dno, nameVal, contentVal, { row: r });
                                                                } catch (err) {
                                                                  console.error('일정 저장 실패', err);
                                                                  alert('일정 저장에 실패했습니다. 다시 시도해주세요.');
                                                                }
                                                              }}
                                                            >{editingSchedules[scheduleNoLocal]?.saving ? '저장중...' : '저장'}</button>
                                                            <button
                                                              type="button"
                                                              className="px-2 py-1 text-[11px] rounded border bg-white hover:bg-red-50 text-red-600 border-red-300"
                                                              onClick={async () => {
                                                                const ok = window.confirm('이 일정을 삭제하시겠습니까?\n삭제 후 되돌릴 수 없습니다.');
                                                                if (!ok) return;
                                                                try {
                                                                  const { userNo } = getUserInfo() || {};
                                                                  await deleteItinerarySchedule({
                                                                    scheduleNo: scheduleNoLocal,
                                                                    itineraryTypeNo,
                                                                    productNo: productId,
                                                                    userNo,
                                                                  });
                                                                  // remove from local state
                                                                  setItineraryData(prev => {
                                                                    const t1 = Array.isArray(prev.table1) ? prev.table1.filter(row => {
                                                                      const no = Number(row.상품일정번호 ?? row.일정번호 ?? row.장소번호 ?? 0);
                                                                      return no !== scheduleNoLocal;
                                                                    }) : [];
                                                                    return { ...prev, table1: t1 };
                                                                  });
                                                                  setEditingSchedules(prev => { const n = { ...prev }; delete n[scheduleNoLocal]; return n; });
                                                                } catch (err) {
                                                                  console.error('일정 삭제 실패', err);
                                                                  alert('일정 삭제에 실패했습니다. 다시 시도해주세요.');
                                                                }
                                                              }}
                                                            >삭제</button>
                                                          </>
                                                        )}
                                                      </div>
                                                    </div>
                                                    {/* Flight-specific editor when in flight mode */}
                                                    {isEditing && flightModeBySchedule[scheduleNoLocal] ? (
                                                      <div className="mt-2 p-2 border rounded bg-white">
                                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                                          <div>
                                                            <label className="block text-[11px] text-gray-500 mb-0.5">편명</label>
                                                            <input
                                                              type="text"
                                                              className="w-full px-2 py-1 border rounded text-[12px]"
                                                              value={flightDraftBySchedule[scheduleNoLocal]?.flightNo || ''}
                                                              onChange={(e) => {
                                                                const next = { ...(flightDraftBySchedule[scheduleNoLocal] || {}), flightNo: e.target.value };
                                                                setFlightDraftBySchedule(prev => ({ ...prev, [scheduleNoLocal]: next }));
                                                                const autoName = synthesizeFlightName(next);
                                                                setEditingSchedules(prev => ({ ...prev, [scheduleNoLocal]: { ...prev[scheduleNoLocal], name: autoName || prev[scheduleNoLocal]?.name || '' } }));
                                                              }}
                                                              placeholder="예: KE123"
                                                            />
                                                          </div>
                                                          <div>
                                                            <label className="block text-[11px] text-gray-500 mb-0.5">출발공항</label>
                                                            <AirportDropdownNew
                                                              value={flightDraftBySchedule[scheduleNoLocal]?.depAirport || ''}
                                                              airports={airports}
                                                              loading={airportsLoading}
                                                              placeholder="(ICN) 인천"
                                                              onChange={(val) => {
                                                                const next = { ...(flightDraftBySchedule[scheduleNoLocal] || {}), depAirport: val };
                                                                setFlightDraftBySchedule(prev => ({ ...prev, [scheduleNoLocal]: next }));
                                                                const autoName = synthesizeFlightName(next);
                                                                setEditingSchedules(prev => ({ ...prev, [scheduleNoLocal]: { ...prev[scheduleNoLocal], name: autoName || prev[scheduleNoLocal]?.name || '' } }));
                                                              }}
                                                            />
                                                          </div>
                                                          <div>
                                                            <label className="block text-[11px] text-gray-500 mb-0.5">도착공항</label>
                                                            <AirportDropdownNew
                                                              value={flightDraftBySchedule[scheduleNoLocal]?.arrAirport || ''}
                                                              airports={airports}
                                                              loading={airportsLoading}
                                                              placeholder="(NRT) 나리타"
                                                              onChange={(val) => {
                                                                const next = { ...(flightDraftBySchedule[scheduleNoLocal] || {}), arrAirport: val };
                                                                setFlightDraftBySchedule(prev => ({ ...prev, [scheduleNoLocal]: next }));
                                                                const autoName = synthesizeFlightName(next);
                                                                setEditingSchedules(prev => ({ ...prev, [scheduleNoLocal]: { ...prev[scheduleNoLocal], name: autoName || prev[scheduleNoLocal]?.name || '' } }));
                                                              }}
                                                            />
                                                          </div>
                                                          <div>
                                                            <label className="block text-[11px] text-gray-500 mb-0.5">출발시간</label>
                                                            <input
                                                              type="text"
                                                              inputMode="numeric"
                                                              pattern="[0-9]{2}:[0-9]{2}"
                                                              className="w-full px-2 py-1 border rounded text-[12px]"
                                                              value={flightDraftBySchedule[scheduleNoLocal]?.depTime || ''}
                                                              onChange={(e) => {
                                                                const v = autoFormatTime(e.target.value);
                                                                const next = { ...(flightDraftBySchedule[scheduleNoLocal] || {}), depTime: v };
                                                                setFlightDraftBySchedule(prev => ({ ...prev, [scheduleNoLocal]: next }));
                                                                const autoName = synthesizeFlightName(next);
                                                                setEditingSchedules(prev => ({ ...prev, [scheduleNoLocal]: { ...prev[scheduleNoLocal], name: autoName || prev[scheduleNoLocal]?.name || '' } }));
                                                              }}
                                                              placeholder="00:00"
                                                            />
                                                          </div>
                                                          <div>
                                                            <label className="block text-[11px] text-gray-500 mb-0.5">도착시간</label>
                                                            <input
                                                              type="text"
                                                              inputMode="numeric"
                                                              pattern="[0-9]{2}:[0-9]{2}"
                                                              className="w-full px-2 py-1 border rounded text-[12px]"
                                                              value={flightDraftBySchedule[scheduleNoLocal]?.arrTime || ''}
                                                              onChange={(e) => {
                                                                const v = autoFormatTime(e.target.value);
                                                                const next = { ...(flightDraftBySchedule[scheduleNoLocal] || {}), arrTime: v };
                                                                setFlightDraftBySchedule(prev => ({ ...prev, [scheduleNoLocal]: next }));
                                                                const autoName = synthesizeFlightName(next);
                                                                setEditingSchedules(prev => ({ ...prev, [scheduleNoLocal]: { ...prev[scheduleNoLocal], name: autoName || prev[scheduleNoLocal]?.name || '' } }));
                                                              }}
                                                              placeholder="00:00"
                                                            />
                                                          </div>
                                                          <div>
                                                            <label className="block text-[11px] text-gray-500 mb-0.5">소요시간</label>
                                                            <input
                                                              type="text"
                                                              inputMode="numeric"
                                                              pattern="[0-9]{2}:[0-9]{2}"
                                                              className="w-full px-2 py-1 border rounded text-[12px]"
                                                              value={flightDraftBySchedule[scheduleNoLocal]?.duration || ''}
                                                              onChange={(e) => {
                                                                const v = autoFormatTime(e.target.value);
                                                                const next = { ...(flightDraftBySchedule[scheduleNoLocal] || {}), duration: v };
                                                                setFlightDraftBySchedule(prev => ({ ...prev, [scheduleNoLocal]: next }));
                                                              }}
                                                              placeholder="00:00"
                                                            />
                                                          </div>
                                                        </div>
                                                        {/* datalist 제거: 커스텀 드롭다운 사용 */}
                                                      </div>
                                                    ) : null}
                                                    {isEditing ? (
                                                      <textarea
                                                        className="w-full mt-2 px-2 py-1 text-[12px] border rounded min-h-[60px]"
                                                        value={contentVal}
                                                        onChange={(e) => setEditingSchedules((prev) => ({ ...prev, [scheduleNoLocal]: { ...prev[scheduleNoLocal], content: e.target.value } }))}
                                                        placeholder="일정내용"
                                                      />
                                                    ) : (
                                                      contentVal ? <div className="px-1 mt-3 text-[13px] text-gray-600 whitespace-pre-line">{contentVal}</div> : null
                                                    )}
                                                    {/* 항공편 전용 레이아웃 */}
                                                    {(() => {
                                                      let flights = [];
                                                      try {
                                                        const raw = r?.항공편;
                                                        if (raw) {
                                                          const parsed = JSON.parse(raw);
                                                          flights = Array.isArray(parsed) ? parsed : [];
                                                        }
                                                      } catch { }
                                                      if (!flights || flights.length === 0) return null;
                                                      const f = flights[0] || {};
                                                      const airline = r?.대표항공사 || f?.항공사 || '';
                                                      const flightNo = f?.편명 || '';
                                                      const logo = f?.로고경로 || r?.이미지경로 || '';
                                                      const depTime = f?.출발시간 || r?.시작시간 || '';
                                                      const arrTime = f?.도착시간 || r?.종료시간 || '';
                                                      const depCode = (f?.출발공항코드 || '').toString().toUpperCase();
                                                      const arrCode = (f?.도착공항코드 || '').toString().toUpperCase();
                                                      const depName = f?.출발공항명 || '';
                                                      const arrName = f?.도착공항명 || '';
                                                      const duration = f?.소요시간 || r?.소요시간 || '';
                                                      return (
                                                        <div className="mt-3 px-3 py-3 border rounded bg-white">
                                                          <div className="grid grid-cols-12 items-center gap-3">
                                                            <div className="col-span-4 sm:col-span-3 flex items-center gap-2 min-w-0">
                                                              {logo ? (
                                                                <img src={logo} alt="" className="w-6 h-6 object-contain" />
                                                              ) : (
                                                                <div className="w-6 h-6 rounded bg-gray-200" />
                                                              )}
                                                              <div className="leading-tight min-w-0">
                                                                <div className="text-[12px] font-medium truncate">{airline}</div>
                                                                {flightNo ? <div className="text-[11px] text-gray-500 truncate">{flightNo}</div> : null}
                                                              </div>
                                                            </div>
                                                            <div className="col-span-4 sm:col-span-3 text-right sm:text-center min-w-0">
                                                              <div className="text-base sm:text-lg font-semibold">{depTime}</div>
                                                              {depName ? <div className="text-[11px] text-gray-500 truncate">{depName}</div> : null}
                                                            </div>
                                                            <div className="col-span-4 sm:col-span-3 flex flex-col items-center justify-center">
                                                              <div className="flex items-center gap-1 text-[11px] text-gray-600">
                                                                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-3 h-3">
                                                                  <path d="M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20Z" stroke="currentColor" strokeWidth="1.5" />
                                                                  <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                                </svg>
                                                                <span>{duration}</span>
                                                              </div>
                                                              <div className="flex items-center gap-2 text-sm font-medium">
                                                                <span className="text-blue-600">{depCode}</span>
                                                                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-gray-500">
                                                                  <path d="M2 12h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                                                  <path d="M14 12l6-3v6l-6-3Z" fill="currentColor" />
                                                                </svg>
                                                                <span className="text-blue-600">{arrCode}</span>
                                                              </div>
                                                            </div>
                                                            <div className="hidden sm:block sm:col-span-3 text-center min-w-0">
                                                              <div className="text-lg font-semibold">{arrTime}</div>
                                                              {arrName ? <div className="text-[11px] text-gray-500 truncate">{arrName}</div> : null}
                                                            </div>
                                                          </div>
                                                          {/* 모바일에서 오른쪽 도착 블럭을 다음 줄로 표시 */}
                                                          <div className="sm:hidden mt-2 grid grid-cols-12">
                                                            <div className="col-span-12 text-left">
                                                              <div className="text-base font-semibold">{arrTime}</div>
                                                              {arrName ? <div className="text-[11px] text-gray-500 truncate">{arrName}</div> : null}
                                                            </div>
                                                          </div>
                                                        </div>
                                                      );
                                                    })()}
                                                    {/* 첫 카드 상단에 추가 버튼 (호버 시 표시) - 편집 중에는 숨김 */}
                                                    {idx === 0 && !isEditing && (
                                                      <button
                                                        type="button"
                                                        className="absolute -top-3 left-1/2 -translate-x-1/2 z-50 px-2 py-0.5 rounded-full bg-white border shadow-sm text-[11px] opacity-0 group-hover:opacity-100 transition pointer-events-auto"
                                                        onClick={(e) => { e.stopPropagation(); openAddScheduleDraft(dno, -1); }}
                                                        title="이 위치 위에 일정 추가"
                                                      >+ 일정 추가</button>
                                                    )}
                                                    {/* Hover '+' add button under each card - 편집 중에는 숨김 */}
                                                    {!isEditing && (
                                                      <button
                                                        type="button"
                                                        className="absolute -bottom-3 left-1/2 -translate-x-1/2 z-50 px-2 py-0.5 rounded-full bg-white border shadow-sm text-[11px] opacity-0 group-hover:opacity-100 transition pointer-events-auto"
                                                        onClick={(e) => { e.stopPropagation(); openAddScheduleDraft(dno, idx); }}
                                                        title="이 위치 아래에 일정 추가"
                                                      >+ 일정 추가</button>
                                                    )}
                                                  </div>
                                                );
                                              })()}
                                              {/* 연관장소별 이미지 표시: table1 -> place명, 그 다음 table -> place명 */}
                                              {/* 연관장소 */}
                                              {(() => {
                                                const scheduleNo = Number(r.상품일정번호 ?? r.일정번호 ?? r.장소번호 ?? 0);
                                                const rel = Array.isArray(relatedPlacesBySchedule[scheduleNo]) ? relatedPlacesBySchedule[scheduleNo] : [];
                                                const inputs = newPlaceInputs[scheduleNo] || { value: '', desc: '' };
                                                const setInputs = (next) => setNewPlaceInputs((prev) => ({ ...prev, [scheduleNo]: { ...(prev[scheduleNo] || { value: '', desc: '' }), ...next } }));
                                                const isEditingLocal = !!editingSchedules[scheduleNo];
                                                // 항공편 존재 여부 (버튼 노출 제어용)
                                                let hasFlightForButtons = false;
                                                try {
                                                  if (r?.항공편) {
                                                    const arr = JSON.parse(r.항공편);
                                                    hasFlightForButtons = Array.isArray(arr) && arr.length > 0;
                                                  }
                                                } catch { }
                                                const addPlace = () => {
                                                  const v = Number(inputs.value);
                                                  const d = String(inputs.desc || '').trim();
                                                  if (!v || !d) return;
                                                  setRelatedPlacesBySchedule((prev) => {
                                                    const list = Array.isArray(prev[scheduleNo]) ? prev[scheduleNo] : [];
                                                    return { ...prev, [scheduleNo]: [...list, { value: v, Description: d }] };
                                                  });
                                                  setInputs({ value: '', desc: '' });
                                                };
                                                const removePlace = async (value, placeName) => {
                                                  // 삭제 확인 다이얼로그 표시
                                                  if (!window.confirm(`장소 "${placeName}"을(를) 삭제하시겠습니까?`)) {
                                                    return;
                                                  }

                                                  try {
                                                    // API 호출하여 DB에서 장소 삭제
                                                    await deleteItineraryPlace({
                                                      scheduleNo,
                                                      productNo: Number(productId),
                                                      day: Number(r.일차 || 0),
                                                      placeName,
                                                      placeNo: value
                                                    });

                                                    // UI 상태 업데이트
                                                    setRelatedPlacesBySchedule((prev) => {
                                                      const list = Array.isArray(prev[scheduleNo]) ? prev[scheduleNo] : [];
                                                      return { ...prev, [scheduleNo]: list.filter((p) => p.value !== value) };
                                                    });
                                                    // 로컬 상태에서 해당 장소 제거 후, 최신 데이터 재로딩
                                                    // 일정데이터 재조회 (일정/장소/호텔 등)
                                                    await loadItinerary();
                                                    // 이미지 캐시 초기화: 해당 일정의 장소/무장소 이미지 캐시 제거하여 stale 방지
                                                    setCardImagesByPlace(prev => {
                                                      const next = { ...prev };
                                                      delete next[scheduleNo];
                                                      return next;
                                                    });
                                                    setCardImagesNoPlace(prev => {
                                                      const next = { ...prev };
                                                      delete next[scheduleNo];
                                                      return next;
                                                    });
                                                    // 단일 일정 카드 이미지 즉시 재로딩
                                                    await refreshScheduleImagesForCard(scheduleNo);
                                                    setIframeKey((k) => k + 1);
                                                  } catch (error) {
                                                    console.error('장소 삭제 실패:', error);
                                                    alert('장소 삭제에 실패했습니다.');
                                                  }
                                                };
                                                return (
                                                  <div className="mt-2">

                                                    {rel.length ? (
                                                      <div className="space-y-3 mb-2">
                                                        {rel.map((p) => {
                                                          const name = p.Description;
                                                          const imgs = byPlace && name ? (byPlace[name] || []) : [];
                                                          return (
                                                            <div key={p.value} className="bg-white border rounded p-2 space-y-2">
                                                              <div className="flex items-center justify-between">
                                                                <span className="mt-0 text-[11px] text-gray-600 whitespace-pre-line">{name}</span>
                                                                <button type="button" className="text-gray-500 hover:text-red-600 text-[12px]" onClick={() => removePlace(p.value, p.Description)} aria-label="장소 제거">×</button>
                                                              </div>
                                                              {Array.isArray(imgs) && imgs.length > 0 ? (
                                                                <div className="grid grid-cols-5 gap-2">
                                                                  {imgs.slice(0, 5).map((src, i) => (
                                                                    <img key={i} src={src} alt="" className="h-32 w-full object-cover rounded border" />
                                                                  ))}
                                                                </div>
                                                              ) : null}
                                                            </div>
                                                          );
                                                        })}
                                                      </div>
                                                    ) : null}
                                                    {/* 기타 장소 이미지: 연관장소 목록에 없지만 그룹핑된 장소 이미지 표시 */}
                                                    {(() => {
                                                      const relNames = new Set((rel || []).map(x => x.Description));
                                                      const extraNames = Object.keys(byPlace || {}).filter(n => n && !relNames.has(n));
                                                      if (extraNames.length === 0) return null;
                                                      return (
                                                        <div className="space-y-3 mb-2">
                                                          {extraNames.map((name) => {
                                                            const imgs = byPlace[name] || [];
                                                            return (
                                                              <div key={name} className="bg-white border rounded p-2 space-y-2">
                                                                <div className="flex items-center justify-between">
                                                                  <span className="mt-0 text-[11px] text-gray-600 whitespace-pre-line">{name}</span>
                                                                </div>
                                                                {Array.isArray(imgs) && imgs.length > 0 ? (
                                                                  <div className="grid grid-cols-5 gap-2">
                                                                    {imgs.slice(0, 5).map((src, i) => (
                                                                      <img key={i} src={src} alt="" className="h-32 w-full object-cover rounded border" />
                                                                    ))}
                                                                  </div>
                                                                ) : null}
                                                              </div>
                                                            );
                                                          })}
                                                        </div>
                                                      );
                                                    })()}
                                                    {/* 일정 이미지 (장소명 없는 이미지) */}
                                                    {Array.isArray(noPlaceImgs) && noPlaceImgs.length > 0 ? (
                                                      <div className="space-y-3 mb-2">
                                                        <div className="bg-white border rounded p-2 space-y-2">
                                                          <div className="flex items-center justify-between">
                                                            <span className="mt-0 text-[11px] text-gray-600 whitespace-pre-line">일정 이미지</span>
                                                          </div>
                                                          <div className="grid grid-cols-5 gap-2">
                                                            {noPlaceImgs.slice(0, 5).map((src, i) => (
                                                              <img key={i} src={src} alt="" className="h-32 w-full object-cover rounded border" />)
                                                            )}
                                                          </div>
                                                        </div>
                                                      </div>
                                                    ) : null}
                                                    <div className="flex items-center gap-2">

                                                      {!hasFlightForButtons && !isEditingLocal && (
                                                        <>
                                                          <button
                                                            type="button"
                                                            className="px-2 py-1 text-[11px] border rounded bg-white hover:bg-gray-50"
                                                            onPointerDown={(e) => { e.stopPropagation(); }}
                                                            onClick={async () => {
                                                              const isEditing = !!editingSchedules[scheduleNo];
                                                              if (isEditing) {
                                                                const nameVal = editingSchedules[scheduleNo]?.name ?? (r.일정명 || '');
                                                                const contentVal = editingSchedules[scheduleNo]?.content ?? (r.일정내용 || r.일정설명 || '');
                                                                try {
                                                                  const finalNo = await saveScheduleEdits(scheduleNo, dno, nameVal, contentVal, { refreshPreview: false });
                                                                  if (!finalNo) return;
                                                                  setPlaceModalScheduleNo(finalNo);
                                                                  setPlaceModalOpen(true);
                                                                } catch (err) {
                                                                  console.error('일정 저장 실패', err);
                                                                  alert('일정 저장에 실패했습니다. 다시 시도해주세요.');
                                                                }
                                                              } else {
                                                                setPlaceModalScheduleNo(scheduleNo);
                                                                setPlaceModalOpen(true);
                                                              }
                                                            }}
                                                          >+ 장소 등록</button>
                                                          <button
                                                            type="button"
                                                            className="px-2 py-1 text-[11px] border rounded bg-white hover:bg-gray-50"
                                                            onPointerDown={(e) => { e.stopPropagation(); }}
                                                            onClick={async () => {
                                                              const isEditing = !!editingSchedules[scheduleNo];
                                                              if (isEditing) {
                                                                const nameVal = editingSchedules[scheduleNo]?.name ?? (r.일정명 || '');
                                                                const contentVal = editingSchedules[scheduleNo]?.content ?? (r.일정내용 || r.일정설명 || '');
                                                                try {
                                                                  const finalNo = await saveScheduleEdits(scheduleNo, dno, nameVal, contentVal, { refreshPreview: false });
                                                                  if (!finalNo) return;
                                                                  await openImageStatus(finalNo);
                                                                } catch (err) {
                                                                  console.error('일정 저장 실패', err);
                                                                  alert('일정 저장에 실패했습니다. 다시 시도해주세요.');
                                                                }
                                                              } else {
                                                                await openImageStatus(scheduleNo);
                                                              }
                                                            }}
                                                          >이미지 설정</button>
                                                        </>
                                                      )}
                                                      {!isEditingLocal && (
                                                        <button
                                                          type="button"
                                                          className="px-2 py-1 text-[11px] border rounded bg-white hover:bg-gray-50"
                                                          onPointerDown={(e) => { e.stopPropagation(); }}
                                                          onClick={async () => {
                                                            const isEditing = !!editingSchedules[scheduleNo];
                                                            if (isEditing) {
                                                              const nameVal = editingSchedules[scheduleNo]?.name ?? (r.일정명 || '');
                                                              const contentVal = editingSchedules[scheduleNo]?.content ?? (r.일정내용 || r.일정설명 || '');
                                                              try {
                                                                const finalNo = await saveScheduleEdits(scheduleNo, dno, nameVal, contentVal, { refreshPreview: false });
                                                                if (!finalNo) return;
                                                                openCopyModal(finalNo);
                                                              } catch (err) {
                                                                console.error('일정 저장 실패', err);
                                                                alert('일정 저장에 실패했습니다. 다시 시도해주세요.');
                                                              }
                                                            } else {
                                                              openCopyModal(scheduleNo);
                                                            }
                                                          }}
                                                        >일정 복사</button>
                                                      )}
                                                    </div>
                                                  </div>
                                                );
                                              })()}
                                              {/* 일정내용 (위로 이동됨) */}
                                            </div>
                                          </div>
                                        </li>
                                      );
                                    }) : (
                                      <li className="list-none">
                                        <div className="flex flex-col items-center justify-center gap-3 p-8 border-2 border-dashed rounded-lg bg-white text-gray-500">
                                          <div className="text-sm">등록된 일정이 없습니다.</div>
                                          <div className="flex items-center gap-2">
                                            <button
                                              type="button"
                                              className="px-3 py-1.5 text-xs rounded bg-primary-600 text-white hover:bg-blue-700"
                                              onClick={() => openAddScheduleDraft(dno, -1)}
                                              title="일정 추가"
                                            >+ 일정 추가</button>
                                          </div>
                                        </div>
                                      </li>
                                    )}
                                  </ul>
                                </div>
                                {/* 구분선: 일정(table1)과 호텔(table2) 사이 */}
                                <div className="mt-6 my-3 border-t border-dashed border-gray-300" role="separator" aria-label="일정과 호텔 구분선"></div>

                                <div>
                                  <div className="px-1 font-medium truncate text-sm mt-7 flex items-center gap-1">
                                    {/* Hotel icon (bed) */}
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 text-gray-500" aria-hidden="true">
                                      <path d="M3 5.25a.75.75 0 0 0-.75.75v12a.75.75 0 0 0 1.5 0v-3h16.5v3a.75.75 0 0 0 1.5 0v-6.75A3.75 3.75 0 0 0 18 7.5h-4.5a3.75 3.75 0 0 0-3.53 2.625H3.75V6a.75.75 0 0 0-.75-.75Zm7.5 5.625A2.25 2.25 0 0 1 12.75 8.25H18a2.25 2.25 0 0 1 2.25 2.25v2.25H10.5v-1.875ZM6.375 9A1.875 1.875 0 1 0 6.375 5.25 1.875 1.875 0 0 0 6.375 9Z" />
                                    </svg>
                                    <span>호텔</span>
                                  </div>
                                  <div className="space-y-3">
                                    {hotels.length ? (
                                      (() => {
                                        // Group by 요금제명
                                        const groupsMap = new Map();
                                        for (const h of hotels) {
                                          const tariff = String(h?.요금제명 || '').trim();
                                          if (!groupsMap.has(tariff)) groupsMap.set(tariff, []);
                                          groupsMap.get(tariff).push(h);
                                        }
                                        const groups = Array.from(groupsMap.entries());
                                        const multipleGroups = groups.length > 1;
                                        // Determine if 자원명 is unique across all items for this day
                                        const resourceSet = new Set((hotels || []).map(h => String(h?.자원명 || '').trim()).filter(Boolean));
                                        const singleResource = resourceSet.size <= 1;

                                        return (
                                          <div className="space-y-3">
                                            {groups.map(([tariffName, items], gi) => (
                                              <div key={gi} className="mt-3 border border-gray-200 bg-white p-2">
                                                {/* 그룹 헤더 */}
                                                {multipleGroups && (
                                                  <div className="text-sm font-semibold text-gray-800 mb-2">
                                                    {tariffName || '요금제 미지정'}
                                                  </div>
                                                )}
                                                {/* 그룹 내 아이템 리스트 */}
                                                <ul className="space-y-2">
                                                  {items.map((h, i) => {
                                                    const hotelKey = Number(h.상품일정번호 ?? h.일정번호 ?? h.장소번호 ?? 0) || (gi * 10000 + i);
                                                    const isEditing = !!editingHotels[hotelKey];
                                                    const nameVal = isEditing ? (editingHotels[hotelKey]?.name ?? (h.일정명 || '')) : (h.일정명 || '');
                                                    const contentVal = isEditing ? (editingHotels[hotelKey]?.content ?? (h.일정내용 || '')) : (h.일정내용 || '');
                                                    const resourceName = String(h?.자원명 || '').trim();
                                                    const priceOptionName = String(h?.가격옵션명 || '').trim();
                                                    const suppressPriceOption = !!priceOptionName && !!tariffName && multipleGroups && (priceOptionName === tariffName);
                                                    return (
                                                      <li key={hotelKey} className="bg-white flex items-start gap-2 p-2">
                                                        <div className="flex-1 min-w-0">
                                                          <div className="flex items-start justify-between gap-2">
                                                            <div className="flex-1 min-w-0 font-medium text-sm">
                                                              {isEditing ? (
                                                                <input
                                                                  className="w-full text-sm font-medium px-2 py-1 border rounded"
                                                                  value={nameVal}
                                                                  onChange={(e) => setEditingHotels(prev => ({ ...prev, [hotelKey]: { ...prev[hotelKey], name: e.target.value } }))}
                                                                  placeholder="일정명"
                                                                />
                                                              ) : (
                                                                <span className="truncate">
                                                                  {!singleResource && resourceName ? `${resourceName} ` : ''}
                                                                  {!suppressPriceOption && priceOptionName ? `${priceOptionName} : ` : ''}
                                                                  {nameVal || '호텔'}
                                                                </span>
                                                              )}
                                                            </div>
                                                            <div className="shrink-0 flex gap-1">
                                                              {isEditing ? (
                                                                <>
                                                                  <button
                                                                    type="button"
                                                                    className="px-2 py-1 text-[11px] rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                                                                    disabled={!!editingHotels[hotelKey]?.saving}
                                                                    onClick={async () => {
                                                                      await saveHotelEdits(hotelKey, dno, nameVal, contentVal, { row: h });
                                                                      setEditingHotels(prev => {
                                                                        const next = { ...prev };
                                                                        delete next[hotelKey];
                                                                        return next;
                                                                      });
                                                                    }}
                                                                  >{editingHotels[hotelKey]?.saving ? '저장중...' : '저장'}</button>
                                                                  <button
                                                                    type="button"
                                                                    className="px-2 py-1 text-[11px] rounded border bg-white hover:bg-gray-50"
                                                                    onClick={() => setEditingHotels(prev => {
                                                                      const next = { ...prev };
                                                                      delete next[hotelKey];
                                                                      return next;
                                                                    })}
                                                                  >취소</button>
                                                                </>
                                                              ) : (
                                                                <>
                                                                  <button
                                                                    type="button"
                                                                    className="px-2 py-1 text-[11px] border rounded bg-white hover:bg-gray-50"
                                                                    onClick={() => setEditingHotels(prev => ({ ...prev, [hotelKey]: { name: h.일정명 || '', content: h.일정내용 || '' } }))}
                                                                  >편집</button>
                                                                </>
                                                              )}
                                                            </div>
                                                          </div>
                                                          <div className="mt-1">
                                                            {isEditing ? (
                                                              <textarea
                                                                className="w-full text-[13px] px-2 py-1 border rounded resize-y min-h-[48px]"
                                                                value={contentVal}
                                                                onChange={(e) => setEditingHotels(prev => ({ ...prev, [hotelKey]: { ...prev[hotelKey], content: e.target.value } }))}
                                                                placeholder="일정내용"
                                                              />
                                                            ) : (
                                                              <div className="text-[14px] text-gray-700 whitespace-pre-wrap">{contentVal}</div>
                                                            )}
                                                          </div>
                                                        </div>
                                                      </li>
                                                    );
                                                  })}
                                                </ul>
                                              </div>
                                            ))}
                                          </div>
                                        );
                                      })()
                                    ) : (
                                      <div className="text-[11px] text-gray-500 px-1">데이터 없음</div>
                                    )}
                                  </div>
                                  <div className="mt-2">
                                    <div className="px-1 font-medium truncate text-sm mt-7 flex items-center gap-1 justify-between">
                                      {/* Meal icon (fork & knife) */}
                                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 text-gray-500" aria-hidden="true">
                                        <path d="M10.5 2.25a.75.75 0 0 0-.75.75v6a.75.75 0 0 1-1.5 0v-6a.75.75 0 0 0-1.5 0v6a2.25 2.25 0 0 0 4.5 0v-6a.75.75 0 0 0-.75-.75Zm5.25 0a.75.75 0 0 0-.75.75V8.4c0 1.86 1.44 3.36 3.3 3.48a.75.75 0 0 0 .8-.75V3a.75.75 0 0 0-1.5 0v3h-1.5V3a.75.75 0 0 0-.75-.75Z" />
                                        <path d="M4.5 13.5a.75.75 0 0 0-.75.75v6a.75.75 0 0 0 1.5 0v-6a.75.75 0 0 0-.75-.75Zm7.5 0a.75.75 0 0 0-.75.75v6a.75.75 0 0 0 1.5 0v-6a.75.75 0 0 0-.75-.75Zm7.5 0a.75.75 0 0 0-.75.75v6a.75.75 0 0 0 1.5 0v-6a.75.75 0 0 0-.75-.75Z" />
                                      </svg>
                                      <span>식사</span>
                                      <div className="ml-auto text-[10px] text-gray-400"></div>
                                    </div>
                                    {(() => {
                                      return meals.length ? (
                                        <div className="mt-5 px-1 flex flex-wrap gap-10 text-[12px]">
                                          {/* 조식 */}
                                          {editingMealsByDay[dno]?.target === 'breakfast' ? (
                                            <MealInlineEditor
                                              key={`bf-${dno}`}
                                              label="조식"
                                              initialValue={meals[0] || ''}
                                              onCancel={() => setEditingMealsByDay(prev => ({ ...prev, [dno]: {} }))}
                                              onSave={async (val) => {
                                                const oldVal = meals[0] || '';
                                                setEditingMealsByDay(prev => ({ ...prev, [dno]: { target: 'breakfast', saving: true } }));
                                                try {
                                                  await saveItineraryMeal({ productNo: productId, itineraryTypeNo, dayNo: dno, planLabel: '조식', oldContent: oldVal, newContent: val });
                                                  // Update local table3
                                                  setItineraryData(prev => {
                                                    const t3 = Array.isArray(prev.table3) ? prev.table3.slice() : [];
                                                    const idx = t3.findIndex(r => Number(r.일차) === dno);
                                                    const parts = (idx >= 0 && t3[idx]?.식사정보 ? String(t3[idx].식사정보).split('/') : ['', '', '']).map(s => s.trim());
                                                    parts[0] = val;
                                                    const newText = `${parts[0] || ''} / ${parts[1] || ''} / ${parts[2] || ''}`;
                                                    if (idx >= 0) t3[idx] = { ...t3[idx], 식사정보: newText }; else t3.push({ 일차: dno, 식사정보: newText });
                                                    return { ...prev, table3: t3 };
                                                  });
                                                } finally {
                                                  setEditingMealsByDay(prev => ({ ...prev, [dno]: {} }));
                                                  setIframeKey(k => k + 1);
                                                }
                                              }}
                                            />
                                          ) : (
                                            <button type="button" title="클릭하여 조식 편집" className="group inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border hover:bg-gray-50 cursor-pointer"
                                              onClick={() => setEditingMealsByDay(prev => ({ ...prev, [dno]: { target: 'breakfast' } }))}
                                            >
                                              <span className="font-semibold text-gray-800">조식</span>
                                              <span className="mx-0.5 text-gray-300">:</span>
                                              <span className="text-gray-700 font-normal">{meals[0] || ''}</span>

                                            </button>
                                          )}

                                          {/* 중식 */}
                                          {editingMealsByDay[dno]?.target === 'lunch' ? (
                                            <MealInlineEditor
                                              key={`lu-${dno}`}
                                              label="중식"
                                              initialValue={meals[1] || ''}
                                              onCancel={() => setEditingMealsByDay(prev => ({ ...prev, [dno]: {} }))}
                                              onSave={async (val) => {
                                                const oldVal = meals[1] || '';
                                                setEditingMealsByDay(prev => ({ ...prev, [dno]: { target: 'lunch', saving: true } }));
                                                try {
                                                  await saveItineraryMeal({ productNo: productId, itineraryTypeNo, dayNo: dno, planLabel: '중식', oldContent: oldVal, newContent: val });
                                                  setItineraryData(prev => {
                                                    const t3 = Array.isArray(prev.table3) ? prev.table3.slice() : [];
                                                    const idx = t3.findIndex(r => Number(r.일차) === dno);
                                                    const parts = (idx >= 0 && t3[idx]?.식사정보 ? String(t3[idx].식사정보).split('/') : ['', '', '']).map(s => s.trim());
                                                    parts[1] = val;
                                                    const newText = `${parts[0] || ''} / ${parts[1] || ''} / ${parts[2] || ''}`;
                                                    if (idx >= 0) t3[idx] = { ...t3[idx], 식사정보: newText }; else t3.push({ 일차: dno, 식사정보: newText });
                                                    return { ...prev, table3: t3 };
                                                  });
                                                } finally {
                                                  setEditingMealsByDay(prev => ({ ...prev, [dno]: {} }));
                                                  setIframeKey(k => k + 1);
                                                }
                                              }}
                                            />
                                          ) : (
                                            <button type="button" title="클릭하여 중식 편집" className="group inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border hover:bg-gray-50 cursor-pointer"
                                              onClick={() => setEditingMealsByDay(prev => ({ ...prev, [dno]: { target: 'lunch' } }))}
                                            >
                                              <span className="font-semibold text-gray-800">중식</span>
                                              <span className="mx-0.5 text-gray-300">:</span>
                                              <span className="text-gray-700 font-normal">{meals[1] || ''}</span>

                                            </button>
                                          )}

                                          {/* 석식 */}
                                          {editingMealsByDay[dno]?.target === 'dinner' ? (
                                            <MealInlineEditor
                                              key={`di-${dno}`}
                                              label="석식"
                                              initialValue={meals[2] || ''}
                                              onCancel={() => setEditingMealsByDay(prev => ({ ...prev, [dno]: {} }))}
                                              onSave={async (val) => {
                                                const oldVal = meals[2] || '';
                                                setEditingMealsByDay(prev => ({ ...prev, [dno]: { target: 'dinner', saving: true } }));
                                                try {
                                                  await saveItineraryMeal({ productNo: productId, itineraryTypeNo, dayNo: dno, planLabel: '석식', oldContent: oldVal, newContent: val });
                                                  setItineraryData(prev => {
                                                    const t3 = Array.isArray(prev.table3) ? prev.table3.slice() : [];
                                                    const idx = t3.findIndex(r => Number(r.일차) === dno);
                                                    const parts = (idx >= 0 && t3[idx]?.식사정보 ? String(t3[idx].식사정보).split('/') : ['', '', '']).map(s => s.trim());
                                                    parts[2] = val;
                                                    const newText = `${parts[0] || ''} / ${parts[1] || ''} / ${parts[2] || ''}`;
                                                    if (idx >= 0) t3[idx] = { ...t3[idx], 식사정보: newText }; else t3.push({ 일차: dno, 식사정보: newText });
                                                    return { ...prev, table3: t3 };
                                                  });
                                                } finally {
                                                  setEditingMealsByDay(prev => ({ ...prev, [dno]: {} }));
                                                  setIframeKey(k => k + 1);
                                                }
                                              }}
                                            />
                                          ) : (
                                            <button type="button" title="클릭하여 석식 편집" className="group inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border hover:bg-gray-50 cursor-pointer"
                                              onClick={() => setEditingMealsByDay(prev => ({ ...prev, [dno]: { target: 'dinner' } }))}
                                            >
                                              <span className="font-semibold text-gray-800">석식</span>
                                              <span className="mx-0.5 text-gray-300">:</span>
                                              <span className="text-gray-700 font-normal">{meals[2] || ''}</span>

                                            </button>
                                          )}
                                        </div>
                                      ) : (
                                        <div className="text-[11px] text-gray-500">데이터 없음</div>
                                      );
                                    })()}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              ) : itineraryError ? (
                <div className="mb-8 p-3 border rounded bg-white text-[12px] text-red-600">{itineraryError}</div>
              ) : null}
              {/* 편집 UI 제거됨: 조회 전용 */}
              {null}
            </div>
          </section>


        </div>
      </div>
      {placeModalOpen && (
        <PlaceAddModal
          open={placeModalOpen}
          onClose={() => setPlaceModalOpen(false)}
          scheduleNo={placeModalScheduleNo}
          onSuccess={async ({ scheduleNo }) => {
            // 해당 일정의 파생 캐시 무효화 (이미지/연관장소)
            if (scheduleNo) {
              setCardImagesByPlace((prev) => {
                const next = { ...prev };
                delete next[scheduleNo];
                return next;
              });
              setCardImagesNoPlace((prev) => {
                const next = { ...prev };
                delete next[scheduleNo];
                return next;
              });
              setRelatedPlacesBySchedule((prev) => {
                const next = { ...prev };
                delete next[scheduleNo];
                return next;
              });
            }
            // 최신 데이터 재조회 및 미리보기 강제 새로고침
            await loadItinerary();
            setIframeKey((k) => k + 1);
          }}
          googleApiKey={GOOGLE_PLACES_API_KEY}
        />
      )}
      {/* Confirmation dialog for content deletion */}
      {deleteConfirmOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDeleteConfirmOpen(false)} />
          <div className="relative z-10 w-[400px] max-w-[96vw] bg-white rounded shadow-lg flex flex-col">
            <div className="px-4 py-3 border-b">
              <h4 className="text-sm font-semibold">콘텐츠 삭제 확인</h4>
            </div>
            <div className="p-4">
              <p className="text-sm mb-4">정말로 이 콘텐츠를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.</p>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  className="text-xs px-3 py-1.5 rounded bg-gray-200 hover:bg-gray-300"
                  onClick={() => setDeleteConfirmOpen(false)}
                >취소</button>
                <button
                  type="button"
                  className="text-xs px-3 py-1.5 rounded bg-red-600 text-white hover:bg-red-700"
                  onClick={confirmDeleteContent}
                >삭제</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 이미지 설정 모달 - 상품 갤러리 선택 모달 */}
      {imageStatusGalleryOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setImageStatusGalleryOpen(false)} />
          <div className="relative z-10 w-[900px] max-w-[96vw] max-h-[88vh] bg-white rounded shadow-lg flex flex-col">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <h4 className="text-sm font-semibold">상품 갤러리 선택</h4>
              <button className="text-sm text-gray-500 hover:text-gray-800" onClick={() => setImageStatusGalleryOpen(false)}>닫기</button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              {imageStatusGalleryLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-sm text-gray-500">불러오는 중...</div>
                </div>
              ) : (
                <div>
                  {!imageStatusGalleryImgs.length ? (
                    <div className="text-sm text-gray-500">이미지가 없습니다.</div>
                  ) : (
                    <div className="grid grid-cols-6 gap-3">
                      {imageStatusGalleryImgs.map((img) => {
                        const id = Number(img.이미지번호 ?? img.imageNo ?? 0);
                        const src = img.이미지경로2 || img.이미지경로 || img.imagePath || '';
                        const selected = imageStatusGallerySelected.includes(id);
                        return (
                          <div key={`is-gal-${id}`} className="relative">
                            <button
                              type="button"
                              className={`block w-full aspect-square rounded border overflow-hidden hover:ring-2 hover:ring-blue-400 ${selected ? 'ring-2 ring-blue-500' : ''}`}
                              title={String(img.이미지명 || img.imageName || '갤러리 이미지')}
                              onClick={() => {
                                setImageStatusGallerySelected((prev) => {
                                  if (!id) return prev;
                                  const has = prev.includes(id);
                                  if (has) return prev.filter((x) => x !== id);
                                  return [...prev, id];
                                });
                              }}
                            >
                              <img src={src} alt="gallery" className="w-full h-full object-cover" loading="lazy" />
                              {selected && (
                                <span className="absolute top-1 left-1 inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs shadow">✓</span>
                              )}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="px-4 py-3 border-t flex items-center justify-between gap-2">
              <div className="text-xs text-gray-600">선택됨: {imageStatusGallerySelected.length}개</div>
              <div className="flex items-center gap-2">
                <button className="px-3 py-1.5 rounded text-xs bg-gray-200 hover:bg-gray-300" onClick={() => setImageStatusGalleryOpen(false)}>닫기</button>
                <button
                  className="px-3 py-1.5 rounded text-xs bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                  disabled={imageStatusGalleryLoading || imageStatusGallerySelected.length === 0 || !imageStatusScheduleNo}
                  onClick={async () => {
                    if (!imageStatusScheduleNo || imageStatusGallerySelected.length === 0) return;
                    try {
                      setImageStatusGalleryLoading(true);
                      const { userNo } = getUserInfo() || {};
                      const placeNo = Number(imageUploadMenuTarget?.placeNo || 0);
                      for (const imageNo of imageStatusGallerySelected) {
                        if (placeNo > 0) {
                          await savePlaceImageFromGalleryToSchedule({ imageNo, placeNo, scheduleNo: imageStatusScheduleNo, userNo });
                        } else {
                          await saveScheduleImageFromGallery({ imageNo, scheduleNo: imageStatusScheduleNo, userNo });
                        }
                      }
                      // 저장 후 새로고침
                      try {
                        const { companyNo } = getUserInfo() || {};
                        const supplierNo = companyNo;
                        const ds = await getSchedulePlaceImages({ scheduleNo: imageStatusScheduleNo, optionScheduleNo: -1, supplierNo, placeNo: 0 });
                        const rowsPlace = Array.isArray(ds?.table) ? ds.table : [];
                        const rowsSchedule = Array.isArray(ds?.table1) ? ds.table1 : [];
                        const groups = {};
                        for (const row of rowsPlace) {
                          const name = String(row?.장소명 || '').trim() || '장소 미지정';
                          if (!groups[name]) groups[name] = [];
                          groups[name].push(row);
                        }
                        setImageStatusPlaceGroups(groups);
                        setImageStatusScheduleRows(rowsSchedule);
                        await refreshScheduleImagesForCard(imageStatusScheduleNo);
                      } catch (refreshErr) {
                        console.error('이미지 목록 새로고침 실패', refreshErr);
                      }
                      setImageStatusGallerySelected([]);
                      setImageStatusGalleryOpen(false);
                    } catch (e) {
                      console.error(e);
                      alert('갤러리 이미지 추가에 실패했습니다.');
                    } finally {
                      setImageStatusGalleryLoading(false);
                    }
                  }}
                >선택 추가</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Right-side preview modal */}
      {previewModalOpen && (
        <>
          <div className="fixed inset-0 z-[98] bg-black/40" onClick={() => setPreviewModalOpen(false)} />
          <div className="fixed inset-0 z-[99] flex items-center justify-center p-4">
            <div className="relative w-[1400px] max-w-[98vw] h-[80vh] bg-white rounded-xl shadow-2xl flex flex-col">
            <div className="px-4 py-3 border-b flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0 flex items-center gap-2">
                <input
                  type="text"
                  className="flex-1 min-w-0 border rounded px-2 py-1 text-sm"
                  ref={titleInputRef}
                  value={previewModalTitleEdit}
                  placeholder={previewModalTitle || '제목'}
                  onChange={(e) => setPreviewModalTitleEdit(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="text-xs px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                  disabled={saveContentLoading}
                  onClick={handleSaveSmartEditor}
                >{saveContentLoading ? '저장중...' : '저장'}</button>
                <button
                  type="button"
                  className="text-xs px-2 py-1 rounded bg-gray-200 hover:bg-gray-300"
                  onClick={() => setPreviewModalOpen(false)}
                >닫기</button>
              </div>
            </div>
            <div className="p-3 flex-1 min-h-0 overflow-hidden grid grid-cols-1 lg:grid-cols-3 gap-3">
              {/* Left column: SmartEditor */}
              <div className="flex flex-col min-h-0 lg:order-1 order-2 lg:col-span-2">
                {/* SmartEditor */}
                <div className="relative z-[150] flex-1 min-h-0 border rounded overflow-visible flex flex-col">
                  <iframe
                    ref={seIframeRef}
                    title="SmartEditor"
                    src="/smarteditor/SmartEditor4.html"
                    className="w-full flex-1"
                    style={{ border: 0 }}
                    onLoad={() => {
                      setSeReady(false);
                    }}
                  />
                </div>
              </div>

              {/* Right column: Image mapping + Attachments */}
              <div className="flex flex-col min-h-0 overflow-hidden lg:order-2 order-1">
                {/* 컨텐츠 이미지 썸네일 영역 */}
                <div className="border rounded p-2 flex-1 min-h-0 overflow-auto">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold text-gray-800">본문하단 이미지</h4>
                </div>
                {contentImagesLoading ? (
                  <ul className="grid grid-cols-3 gap-2">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <li key={`ci-skel-${i}`} className="animate-pulse">
                        <div className="relative w-full pt-[56.25%] rounded-lg border bg-gray-100 overflow-hidden" />
                        <div className="h-3 mt-1 rounded bg-gray-100" />
                      </li>
                    ))}
                  </ul>
                ) : contentImagesError ? (
                  <div className="text-xs text-red-600">{contentImagesError}</div>
                ) : (contentImages && contentImages.length > 0 ? (
                  <ul className="grid grid-cols-3 gap-2">
                    {/* '+' tile for adding images */}
                    <li className="relative" ref={contentAddMenuRef}>
                      <button
                        type="button"
                        className="w-full pt-[56.25%] rounded-lg border-2 border-dashed flex items-center justify-center bg-white hover:bg-gray-50 text-gray-400 relative"
                        title="이미지 추가"
                        onClick={ensureContentExistsAndToggleAddMenu}
                      >
                        <span className="absolute inset-0 flex items-center justify-center">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-gray-400">
                            <path d="M12 5c.552 0 1 .448 1 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H6a1 1 0 110-2h5V6c0-.552.448-1 1-1z" />
                          </svg>
                        </span>
                      </button>
                      {contentAddMenuOpen && (
                        <div className="fixed z-[300] w-44 bg-white border rounded shadow-lg" style={{ top: contentAddMenuRef.current?.getBoundingClientRect().bottom + 4, left: contentAddMenuRef.current?.getBoundingClientRect().left }}>
                          <button type="button" className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50" onClick={handleContentUploadFromPC}>내 PC에서 업로드</button>
                          <button type="button" className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50" onClick={openContentCommonModal}>공용 이미지에서 선택</button>
                          <button type="button" className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50" onClick={openContentGalleryModal}>상품 갤러리에서 선택</button>
                        </div>
                      )}
                    </li>
                    {(Array.isArray(contentImages) ? contentImages : []).map((img) => (
                      <li key={`contimg-${img.이미지번호}`}>
                        <div className="group relative">
                          <div className="relative w-full pt-[56.25%] rounded-lg overflow-hidden bg-gray-50 border">
                            <img
                              src={img.이미지경로2 || img.이미지경로 || img.원본이미지경로}
                              alt={img.이미지명 || ''}
                              className="absolute inset-0 w-full h-full object-cover"
                              draggable={false}
                              onDragStart={(e) => e.preventDefault()}
                              loading="lazy"
                            />
                            {/* 연결 해제 버튼 */}
                            <button
                              type="button"
                              className="absolute top-1 right-1 z-10 inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/90 text-gray-700 hover:bg-red-600 hover:text-white shadow border"
                              title="연결 해제"
                              onClick={() => handleUnlinkContentImage(img.이미지번호)}
                              disabled={contentUnlinkingId === img.이미지번호}
                            >
                              {contentUnlinkingId === img.이미지번호 ? (
                                <span className="animate-spin">⏳</span>
                              ) : (
                                <span aria-hidden>✕</span>
                              )}
                            </button>
                          </div>
                          <div className="mt-1 flex items-center justify-between">
                            <div className="text-[11px] text-gray-700 truncate" title={img.이미지명 || ''}>{extractDisplayName(img.이미지명) || ''}</div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <ul className="grid grid-cols-3 gap-2">
                    <li className="relative" ref={contentAddMenuRef}>
                      <button
                        type="button"
                        className="w-full pt-[56.25%] rounded-lg border-2 border-dashed bg-white hover:bg-gray-50 text-gray-400 relative"
                        title="이미지 추가"
                        onClick={ensureContentExistsAndToggleAddMenu}
                      >
                        <span className="absolute inset-0 flex flex-col items-center justify-center">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8 text-gray-400">
                            <path d="M12 5c.552 0 1 .448 1 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H6a1 1 0 110-2h5V6c0-.552.448-1 1-1z" />
                          </svg>
                          <span className="text-xs text-gray-500">이미지 추가</span>
                        </span>
                      </button>
                      {contentAddMenuOpen && (
                        <div className="fixed z-[300] w-44 bg-white border rounded shadow-lg" style={{ top: contentAddMenuRef.current?.getBoundingClientRect().bottom + 4, left: contentAddMenuRef.current?.getBoundingClientRect().left }}>
                          <button type="button" className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50" onClick={handleContentUploadFromPC}>내 PC에서 업로드</button>
                          <button type="button" className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50" onClick={openContentCommonModal}>공용 이미지에서 선택</button>
                          <button type="button" className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50" onClick={openContentGalleryModal}>상품 갤러리에서 선택</button>
                        </div>
                      )}
                    </li>
                  </ul>
                ))}
                {/* 콘텐츠 전용 파일 입력 (숨김) */}
                <input
                  ref={contentFileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={onContentFileChange}
                />
              </div>
                {/* 파일 첨부 영역 */}
                <div className="mt-3 border-t pt-3">
                <div className="flex items-center justify-between mb-2">
                  <h5 className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                      <path fillRule="evenodd" d="M15.621 4.379a3 3 0 00-4.242 0l-7 7a3 3 0 004.241 4.243h.001l.497-.5a.75.75 0 011.064 1.057l-.498.501-.002.002a4.5 4.5 0 01-6.364-6.364l7-7a4.5 4.5 0 016.368 6.36l-3.455 3.553A2.625 2.625 0 119.52 9.52l3.45-3.451a.75.75 0 111.061 1.06l-3.45 3.451a1.125 1.125 0 001.587 1.595l3.454-3.553a3 3 0 000-4.242z" clipRule="evenodd" />
                    </svg>
                    파일 첨부
                  </h5>
                  <span className="text-[11px] text-gray-500">파일을 선택하거나 드래그하여 첨부하세요</span>
                </div>
                
                {/* 드래그 앤 드롭 영역 */}
                <div
                  className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${contentFileDragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}`}
                  onDrop={handleContentFileDrop}
                  onDragOver={handleContentFileDragOver}
                  onDragLeave={handleContentFileDragLeave}
                >
                  <input
                    type="file"
                    multiple
                    className="hidden"
                    id="generalFileInput"
                    onChange={onContentFileInputChange}
                  />
                  {contentFileUploading ? (
                    <div className="flex items-center justify-center gap-2 text-blue-600">
                      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="text-sm">업로드 중...</span>
                    </div>
                  ) : (
                    <label
                      htmlFor="generalFileInput"
                      className="text-sm text-gray-600 hover:text-blue-600 cursor-pointer"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-8 h-8 mx-auto mb-1 text-gray-400">
                        <path d="M9.25 13.25a.75.75 0 001.5 0V4.636l2.955 3.129a.75.75 0 001.09-1.03l-4.25-4.5a.75.75 0 00-1.09 0l-4.25 4.5a.75.75 0 101.09 1.03L9.25 4.636v8.614z" />
                        <path d="M3.5 12.75a.75.75 0 00-1.5 0v2.5A2.75 2.75 0 004.75 18h10.5A2.75 2.75 0 0018 15.25v-2.5a.75.75 0 00-1.5 0v2.5c0 .69-.56 1.25-1.25 1.25H4.75c-.69 0-1.25-.56-1.25-1.25v-2.5z" />
                      </svg>                  
                    </label>
                  )}
                </div>

                  {/* 첨부된 파일 목록 */}
                  {uploadedFiles.length > 0 && (
                    <div className="mt-2 space-y-1 max-h-[22vh] overflow-y-auto">
                      {uploadedFiles.map((file, idx) => (
                        <div key={`uploaded-${file.파일번호 || idx}`} className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded border text-sm">
                          <div className="flex items-center gap-2 min-w-0">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-gray-500 shrink-0">
                              <path d="M3 3.5A1.5 1.5 0 014.5 2h6.879a1.5 1.5 0 011.06.44l4.122 4.12A1.5 1.5 0 0117 7.622V16.5a1.5 1.5 0 01-1.5 1.5h-11A1.5 1.5 0 013 16.5v-13z" />
                            </svg>
                            <span className="truncate text-gray-700" title={file.파일명}>{file.파일명}</span>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {file.파일경로 && (
                              <a
                                href={file.파일경로}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 text-xs"
                              >다운로드</a>
                            )}
                            <button
                              type="button"
                              className="text-gray-400 hover:text-red-600"
                              onClick={() => setUploadedFiles(prev => prev.filter((_, i) => i !== idx))}
                              title="삭제"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                                <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            {/* 하단 삭제 버튼 */}
            {previewModalContentNo && (
              <div className="px-4 py-3 border-t flex justify-end">
                <button
                  type="button"
                  className="text-xs px-3 py-1.5 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                  disabled={deleteContentLoading}
                  onClick={handleDeleteContent}
                >{deleteContentLoading ? '삭제중...' : '이 콘텐츠 삭제'}</button>
              </div>
            )}
          </div>
        </div>
        </>
      )}
      {/* Server image select modal */}
      {serverImgOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setServerImgOpen(false)} />
          <div className="relative z-10 w-[900px] max-w-[96vw] max-h-[88vh] bg-white rounded shadow-lg flex flex-col">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <h4 className="text-sm font-semibold">공용 이미지 선택</h4>
              <button className="text-sm text-gray-500 hover:text-gray-800" onClick={() => setServerImgOpen(false)}>닫기</button>
            </div>
            <div className="p-4 flex items-center gap-2">
              <label className="text-xs text-gray-600">도시</label>
              <div className="relative">
                <div className="flex items-center gap-1 border rounded px-2 py-1 text-sm min-w-[260px] bg-white" onClick={() => setServerCityOpen(v => !v)}>
                  <input
                    type="text"
                    className="outline-none w-full bg-transparent"
                    placeholder={serverCityName ? `선택: ${serverCityName}` : '도시명/초성 검색 (예: ㅅㅇ)'}
                    value={serverCityQuery}
                    onChange={(e) => { setServerCityQuery(e.target.value); setServerCityOpen(true); }}
                    onFocus={() => setServerCityOpen(true)}
                  />
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-gray-500">
                    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clipRule="evenodd" />
                  </svg>
                </div>
                {serverCityOpen && (
                  <ul className="absolute z-20 mt-1 max-h-64 overflow-auto w-full bg-white border rounded shadow">
                    <li>
                      <button
                        type="button"
                        className={`w-full text-left px-2 py-1 text-xs ${serverCityNo === '' ? 'bg-gray-100' : ''}`}
                        onClick={async () => {
                          setServerCityNo('');
                          setServerCityName('');
                          setServerCityQuery('');
                          setServerImgs([]);
                          setServerSelected([]);
                          setServerCityOpen(false);
                        }}
                      >도시 선택</button>
                    </li>
                    {(filteredCities || []).map(c => (
                      <li key={c.코드}>
                        <button
                          type="button"
                          className={`w-full text-left px-2 py-1 text-xs hover:bg-gray-50 ${String(serverCityNo) === String(c.코드) ? 'bg-gray-100' : ''}`}
                          onClick={async () => {
                            const v = String(c.코드);
                            setServerCityNo(v);
                            setServerCityName(c.코드명 || '');
                            setServerCityQuery('');
                            setServerCityOpen(false);
                            try {
                              setServerLoading(true);
                              const imgs = await getCommonImagesByCity({ cityNo: v });
                              setServerImgs(Array.isArray(imgs) ? imgs : []);
                              setServerSelected([]);
                            } catch (err) {
                              console.error(err);
                              alert('이미지 목록을 불러오지 못했습니다.');
                            } finally {
                              setServerLoading(false);
                            }
                          }}
                        >{c.코드명}</button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {serverCityName && (
                <span className="text-xs text-gray-500">상품의 대표 도시: {serverCityName}</span>
              )}
              <div className="ml-auto text-xs text-gray-600">선택됨: {serverSelected.length}개</div>
            </div>
            <div className="px-4 pb-4 overflow-auto">
              <ul className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-3 min-h-[280px]">
                {serverLoading
                  ? Array.from({ length: 12 }).map((_, idx) => (
                    <li key={`skeleton-${idx}`} className="animate-pulse">
                      <div className="w-full aspect-square rounded border bg-gray-100" />
                      <div className="h-3 mt-2 mx-2 rounded bg-gray-100" />
                    </li>
                  ))
                  : (serverImgs && serverImgs.length > 0
                    ? serverImgs.map((si) => {
                      const selected = serverSelected.includes(si.이미지번호);
                      return (
                        <li key={si.이미지번호}>
                          <button
                            type="button"
                            className={`block w-full rounded overflow-hidden border bg-white hover:ring-2 hover:ring-emerald-400 ${selected ? 'ring-2 ring-emerald-500' : ''}`}
                            title={(si.이미지명 || si.도시명 || '이미지') + ' - 클릭하여 선택/해제'}
                            onClick={() => {
                              setServerSelected((prev) => {
                                const exists = prev.includes(si.이미지번호);
                                if (exists) return prev.filter((n) => n !== si.이미지번호);
                                return [...prev, si.이미지번호];
                              });
                            }}
                          >
                            <div className="relative w-full aspect-square bg-gray-50">
                              <img
                                src={si.이미지경로2 || si.이미지경로 || si.원본이미지경로}
                                alt={si.이미지명 || '이미지'}
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                              {selected && (
                                <span className="absolute top-1 right-1 inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-600 text-white text-xs shadow">✓</span>
                              )}
                            </div>
                            <div className="px-2 py-1 text-[11px] text-gray-700 truncate">{extractDisplayName(si.이미지명) || si.도시명 || ''}</div>
                          </button>
                        </li>
                      );
                    })
                    : (
                      Array.from({ length: 6 }).map((_, idx) => (
                        <li key={`empty-${idx}`} className="opacity-70">
                          <div className="w-full aspect-square rounded border border-dashed bg-white" />
                          <div className="text-[11px] text-gray-400 text-center mt-1">이미지 없음</div>
                        </li>
                      ))
                    ))}
              </ul>
            </div>
            <div className="px-4 py-3 border-t flex items-center justify-between">
              <div className="text-xs text-gray-500">필요한 이미지를 여러 개 선택한 뒤 '선택 추가'를 눌러주세요.</div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="px-3 py-1.5 rounded text-xs bg-emerald-600 text-white disabled:opacity-50"
                  disabled={serverSelected.length === 0 || serverLoading}
                  onClick={async () => {
                    if (serverSelected.length === 0) return;
                    try {
                      setServerLoading(true);
                      const { companyNo, userNo } = getUserInfo() || {};
                      // 1) 서버 공용이미지 -> 상품 갤러리로 복사
                      await downloadCommonImagesToProduct({
                        companyNo,
                        userNo,
                        productNo: productId,
                        imageNos: serverSelected,
                      });
                      // 2) 상품 갤러리 조회
                      const gallery = await getProductGallery({ companyNo, productNo: productId });
                      // 3) 선택된 공용이미지들의 이미지경로2로 매칭하여 갤러리 이미지번호 추출
                      const selectedSet = new Set(serverSelected.map(Number));
                      const selectedPaths = new Set(
                        (serverImgs || [])
                          .filter(si => selectedSet.has(Number(si.이미지번호)))
                          .map(si => si.이미지경로2)
                          .filter(Boolean)
                      );
                      const toSaveImageNos = gallery
                        .filter(g => selectedPaths.has(g.이미지경로2))
                        .map(g => g.이미지번호)
                        .filter(Boolean);
                      if (toSaveImageNos.length === 0) {
                        console.warn('매칭되는 갤러리 이미지가 없습니다.');
                      } else {
                        // 4) 대표이미지로 저장 (배치)
                        await Promise.all(
                          toSaveImageNos.map((imageNo) =>
                            saveRepresentativeImage({ imageNo, productNo: productId, userNo })
                          )
                        );
                      }
                      // Refresh list and preview
                      const imgs = await getRepresentativeImages(productId);
                      setRepImages(Array.isArray(imgs) ? imgs : []);
                      setIframeKey((k) => k + 1);
                      // Auto-close modal after success
                      setServerImgOpen(false);
                      // Clear selection
                      setServerSelected([]);
                    } catch (err) {
                      console.error(err);
                      alert('대표 이미지 일괄 추가에 실패했습니다.');
                    } finally {
                      setServerLoading(false);
                    }
                  }}
                >
                  선택 추가
                </button>
                <button type="button" className="px-3 py-1.5 rounded text-xs bg-gray-200 hover:bg-gray-300" onClick={() => setServerImgOpen(false)}>닫기</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 이미지 설정 모달 */}
      {imageStatusOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={async () => {
              if (imageStatusScheduleNo) {
                await refreshScheduleImagesForCard(imageStatusScheduleNo);
              }
              closeImageStatusAndRefresh();
            }}
          />
          <div className="relative z-10 w-[1000px] max-w-[96vw] max-h-[90vh] bg-white rounded shadow-lg flex flex-col">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <h4 className="text-sm font-semibold">이미지 설정</h4>
              <button
                className="text-sm text-gray-500 hover:text-gray-800"
                onClick={async () => {
                  if (imageStatusScheduleNo) {
                    await refreshScheduleImagesForCard(imageStatusScheduleNo);
                  }
                  closeImageStatusAndRefresh();
                }}
              >닫기</button>
            </div>
            {/* 모달 전용 업로드 입력 */}
            <input
              ref={imageStatusFileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={async (e) => {
                const files = Array.from(e.target.files || []);
                if (!files.length || !imageStatusScheduleNo) return;
                try {
                  setImageStatusUploading(true);
                  setImageStatusUploadProgress({ current: 0, total: files.length });
                  const { companyNo, userNo } = getUserInfo() || {};
                  const filePath = `${companyNo}/Product/${productId}/Schedule/${imageStatusScheduleNo}/`;
                  for (let i = 0; i < files.length; i++) {
                    const file = files[i];
                    const fileName = file.name;
                    const fileBase64 = await new Promise((resolve, reject) => {
                      const reader = new FileReader();
                      reader.onload = () => {
                        const result = reader.result || '';
                        const base64 = String(result).includes(',') ? String(result).split(',')[1] : String(result);
                        resolve(base64);
                      };
                      reader.onerror = reject;
                      reader.readAsDataURL(file);
                    });
                    await uploadAllSizeS3({ fileBase64, fileName, filePath });
                    const imagePath = `${filePath}${fileName}`;
                    const regRes = await registerUploadedImage({ companyNo, userNo, productNo: productId, fileName, imagePath });
                    const imageNo = regRes?.table && regRes.table[0]?.이미지번호;
                    if (!imageNo) throw new Error('이미지번호를 찾을 수 없습니다.');
                    // 일정/장소 이미지 연결
                    const placeNo = Number(imageStatusTargetPlace?.placeNo || 0);
                    if (placeNo > 0) {
                      await savePlaceImageFromGalleryToSchedule({ imageNo, placeNo, scheduleNo: imageStatusScheduleNo, userNo });
                    } else {
                      await saveScheduleImageFromGallery({ imageNo, scheduleNo: imageStatusScheduleNo, userNo });
                    }
                    setImageStatusUploadProgress({ current: i + 1, total: files.length });
                  }
                  // 업로드 후 목록 새로고침
                  try {
                    const { companyNo } = getUserInfo() || {};
                    const supplierNo = companyNo;
                    const ds = await getSchedulePlaceImages({ scheduleNo: imageStatusScheduleNo, optionScheduleNo: -1, supplierNo, placeNo: 0 });
                    const rowsPlace = Array.isArray(ds?.table) ? ds.table : [];
                    const rowsSchedule = Array.isArray(ds?.table1) ? ds.table1 : [];
                    const groups = {};
                    for (const row of rowsPlace) {
                      const name = String(row?.장소명 || '').trim() || '장소 미지정';
                      if (!groups[name]) groups[name] = [];
                      groups[name].push(row);
                    }
                    setImageStatusPlaceGroups(groups);
                    setImageStatusScheduleRows(rowsSchedule);
                    // 업로드 후 기본 선택: 새 목록의 모든 사용자 이미지 선택
                    const scheduleNos = rowsSchedule
                      .filter(r => Number(r?.업체번호) !== -1)
                      .map(r => r.이미지번호)
                      .filter(Boolean);
                    const placeNosAll = rowsPlace
                      .filter(r => Number(r?.업체번호) !== -1)
                      .map(r => r.이미지번호)
                      .filter(Boolean);
                    const allSelected = Array.from(new Set([...scheduleNos, ...placeNosAll]));
                    setImageStatusSelected(allSelected);
                  } catch (refreshErr) {
                    console.error('이미지 목록 새로고침 실패', refreshErr);
                  }
                  e.target.value = '';
                } catch (err) {
                  console.error(err);
                  alert('이미지 업로드에 실패했습니다. 다시 시도해주세요.');
                } finally {
                  setImageStatusUploading(false);
                  setImageStatusUploadProgress(null);
                  setImageStatusTargetPlace({ placeName: '', placeNo: 0 });
                }
              }}
              disabled={imageStatusUploading}
            />
            <div className="p-4 overflow-auto">
              {imageStatusError ? (
                <div className="text-xs text-red-600">{imageStatusError}</div>
              ) : imageStatusLoading ? (
                <div className="text-xs text-gray-500">불러오는 중...</div>
              ) : (
                <div className="space-y-6">
                  {/* 일정 이미지 (table1) 그룹 */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-medium">일정 이미지</div>
                      <div className="flex items-center gap-2">
                        <div className="text-[11px] text-gray-500">
                          {imageStatusUploading && imageStatusUploadProgress
                            ? `업로드 중... ${imageStatusUploadProgress.current}/${imageStatusUploadProgress.total}`
                            : `${Array.isArray(imageStatusScheduleRows) ? imageStatusScheduleRows.length : 0}개`}
                        </div>
                      </div>
                    </div>
                    <ul className="grid grid-cols-6 gap-3">
                      {(() => {
                        const rows = Array.isArray(imageStatusScheduleRows) ? imageStatusScheduleRows : [];
                        if (!rows.length) return (<li className="col-span-6 text-[11px] text-gray-500">이미지 없음</li>);
                        // 공용 이미지(업체번호 -1) 타일 우선 노출
                        const common = rows.find(r => Number(r?.업체번호) === -1);
                        const others = rows.filter(r => Number(r?.업체번호) !== -1);
                        const tiles = [];
                        if (common) {
                          tiles.push(
                            <li key={`sched-common`}>
                              <div
                                className="w-full aspect-square rounded border bg-white overflow-hidden flex flex-col hover:bg-gray-50 cursor-pointer"
                                title="일정에 이미지 업로드"
                                onClick={(e) => {
                                  // 메뉴 위치 계산
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setImageUploadMenuPosition({
                                    x: rect.left,
                                    y: rect.bottom + window.scrollY
                                  });
                                  // 타겟 정보 저장
                                  setImageUploadMenuTarget({
                                    type: 'schedule',
                                    placeNo: 0,
                                    placeName: ''
                                  });
                                  // 메뉴 열기
                                  setImageUploadMenuOpen(true);
                                }}
                              >
                                <div className="flex-1 flex items-center justify-center">
                                  <span className="text-4xl text-gray-400 leading-none">+</span>
                                </div>
                                <div className="px-2 py-1 text-center text-[11px] text-gray-600 border-t">이미지 업로드</div>
                              </div>
                            </li>
                          );
                        }
                        tiles.push(...others.map((row) => {
                          const selected = imageStatusSelected.includes(row.이미지번호);
                          return (
                            <li key={`sched-${row.이미지번호}`}>
                              <button
                                type="button"
                                className={`group relative block w-full aspect-square rounded border bg-white overflow-hidden hover:ring-2 hover:ring-emerald-400 ${selected ? 'ring-2 ring-emerald-500' : ''}`}
                                title={(row.이미지명 || '이미지') + ' - 클릭하여 선택/해제'}
                                onClick={() => {
                                  setImageStatusSelected((prev) => {
                                    const exists = prev.includes(row.이미지번호);
                                    if (exists) return prev.filter((n) => n !== row.이미지번호);
                                    return [...prev, row.이미지번호];
                                  });
                                }}
                              >
                                <img src={row.이미지경로2 || row.이미지경로} alt={row.이미지명 || ''} className="w-full h-full object-cover" />
                                {selected && (
                                  <span className="absolute top-1 left-1 inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-600 text-white text-xs shadow">✓</span>
                                )}
                                <button
                                  type="button"
                                  className="absolute top-1 right-1 z-10 inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/90 text-gray-600 border border-gray-300 hover:text-red-600 hover:bg-white shadow transition-colors opacity-0 group-hover:opacity-100"
                                  title="이 이미지 삭제"
                                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDeleteSingleImage(row.이미지번호); }}
                                >
                                  ✕
                                </button>
                              </button>
                            </li>
                          );
                        }));
                        return tiles;
                      })()}
                    </ul>
                  </div>

                  {/* 장소별 그룹 (table) */}
                  {Object.keys(imageStatusPlaceGroups || {}).map((placeName) => {
                    const list = imageStatusPlaceGroups[placeName] || [];
                    const common = list.find(r => Number(r?.업체번호) === -1);
                    const others = list.filter(r => Number(r?.업체번호) !== -1);
                    return (
                      <div key={placeName} className="">
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-sm font-medium">장소: {placeName}</div>
                          <div className="text-[11px] text-gray-500">{list.length}개</div>
                        </div>
                        <ul className="grid grid-cols-6 gap-3">
                          {[
                            ...(common ? [
                              <li key={`${placeName}-common`}>
                                <div
                                  className="w-full aspect-square rounded border bg-white overflow-hidden flex flex-col hover:bg-gray-50 cursor-pointer"
                                  title={`${placeName}에 이미지 업로드`}
                                  onClick={(e) => {
                                    // 장소번호 추정: others 중 첫 항목의 장소번호 사용, 없으면 0
                                    const others = list.filter(r => Number(r?.업체번호) !== -1);
                                    const placeNo = Number(others?.[0]?.장소번호 || 0);

                                    // 메뉴 위치 계산
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    setImageUploadMenuPosition({
                                      x: rect.left,
                                      y: rect.bottom + window.scrollY
                                    });

                                    // 타겟 정보 저장
                                    setImageUploadMenuTarget({
                                      type: 'place',
                                      placeNo,
                                      placeName
                                    });

                                    // 메뉴 열기
                                    setImageUploadMenuOpen(true);
                                  }}
                                >
                                  <div className="flex-1 flex items-center justify-center">
                                    <span className="text-4xl text-gray-400 leading-none">+</span>
                                  </div>
                                  <div className="px-2 py-1 text-center text-[11px] text-gray-600 border-t">이미지 업로드</div>
                                </div>
                              </li>
                            ] : []),
                            ...others.map((row) => {
                              const selected = imageStatusSelected.includes(row.이미지번호);
                              return (
                                <li key={`${placeName}-${row.이미지번호}`}>
                                  <button
                                    type="button"
                                    className={`group relative block w-full aspect-square rounded border bg-white overflow-hidden hover:ring-2 hover:ring-emerald-400 ${selected ? 'ring-2 ring-emerald-500' : ''}`}
                                    title={(row.이미지명 || '이미지') + ' - 클릭하여 선택/해제'}
                                    onClick={() => {
                                      setImageStatusSelected((prev) => {
                                        const exists = prev.includes(row.이미지번호);
                                        if (exists) return prev.filter((n) => n !== row.이미지번호);
                                        return [...prev, row.이미지번호];
                                      });
                                    }}
                                  >
                                    <img src={row.이미지경로2 || row.이미지경로} alt={row.이미지명 || ''} className="w-full h-full object-cover" />
                                    {selected && (
                                      <span className="absolute top-1 left-1 inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-600 text-white text-xs shadow">✓</span>
                                    )}
                                    <button
                                      type="button"
                                      className="absolute top-1 right-1 z-10 inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/90 text-gray-600 border border-gray-300 hover:text-red-600 hover:bg-white shadow transition-colors opacity-0 group-hover:opacity-100"
                                      title="이 이미지 삭제"
                                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDeleteSingleImage(row.이미지번호); }}
                                    >
                                      ✕
                                    </button>
                                  </button>
                                </li>
                              );
                            }),
                          ]}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="px-4 py-3 border-t flex items-center justify-between gap-2">
              <div className="text-xs text-gray-600">선택됨: {imageStatusSelected.length}개</div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="px-3 py-1.5 rounded text-xs bg-emerald-600 text-white disabled:opacity-50"
                  disabled={!imageStatusScheduleNo || imageStatusSelected.length === 0 || imageStatusSaving}
                  onClick={async () => {
                    if (!imageStatusScheduleNo || imageStatusSelected.length === 0) return;
                    try {
                      setImageStatusSaving(true);
                      await saveScheduleSelectedImages({ scheduleNo: imageStatusScheduleNo, imageNos: imageStatusSelected });
                      // 저장 후 새로고침
                      const { companyNo } = getUserInfo() || {};
                      const supplierNo = companyNo;
                      const ds = await getSchedulePlaceImages({ scheduleNo: imageStatusScheduleNo, optionScheduleNo: -1, supplierNo, placeNo: 0 });
                      const rowsPlace = Array.isArray(ds?.table) ? ds.table : [];
                      const rowsSchedule = Array.isArray(ds?.table1) ? ds.table1 : [];
                      const groups = {};
                      for (const row of rowsPlace) {
                        const name = String(row?.장소명 || '').trim() || '장소 미지정';
                        if (!groups[name]) groups[name] = [];
                        groups[name].push(row);
                      }
                      setImageStatusPlaceGroups(groups);
                      setImageStatusScheduleRows(rowsSchedule);
                      // 카드 미리보기 즉시 갱신
                      await refreshScheduleImagesForCard(imageStatusScheduleNo);
                      // 선택 초기화
                      setImageStatusSelected([]);
                      alert('저장되었습니다.');
                      closeImageStatusAndRefresh();
                    } catch (err) {
                      console.error(err);
                      alert('선택 이미지 저장에 실패했습니다.');
                    } finally {
                      setImageStatusSaving(false);
                    }
                  }}
                >저장</button>
                <button
                  type="button"
                  className="px-3 py-1.5 rounded text-xs bg-gray-200 hover:bg-gray-300"
                  onClick={async () => {
                    if (imageStatusScheduleNo) {
                      await refreshScheduleImagesForCard(imageStatusScheduleNo);
                    }
                    closeImageStatusAndRefresh();
                  }}
                >닫기</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 일정 복사 모달 */}
      {copyModalOpen && (
        <div className="fixed inset-0 z-[65] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={closeCopyModal} />
          <div className="relative z-10 w-[360px] max-w-[92vw] bg-white rounded shadow-lg">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <h4 className="text-sm font-semibold">일정 복사</h4>
              <button className="text-sm text-gray-500 hover:text-gray-800" onClick={closeCopyModal}>✕</button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">복사할 여행일차 선택</label>
                <select
                  className="w-full border rounded px-2 py-1 text-sm"
                  value={copyTargetDay}
                  onChange={(e) => setCopyTargetDay(e.target.value)}
                >
                  <option value="">일차 선택</option>
                  {dayNumbers.map((d) => (
                    <option key={d} value={d}>{d}일차</option>
                  ))}
                  {dayNumbers.length > 0 && (
                    <option value={Math.max(...dayNumbers) + 1}>{Math.max(...dayNumbers) + 1}일차 (새로 추가)</option>
                  )}
                </select>
              </div>
            </div>
            <div className="px-4 py-3 border-t flex items-center justify-end gap-2">
              <button className="px-3 py-1 text-sm border rounded bg-white hover:bg-gray-50" onClick={closeCopyModal} disabled={copyLoading}>취소</button>
              <button className="px-3 py-1 text-sm border rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50" onClick={handleConfirmCopy} disabled={copyLoading || !copyTargetDay}>{copyLoading ? '복사 중...' : '복사'}</button>
            </div>
          </div>
        </div>
      )}

      {/* 이미지 업로드 메뉴 */}
      {imageUploadMenuOpen && (
        <div
          ref={imageUploadMenuRef}
          className="fixed z-[80] bg-white rounded shadow-lg border"
          style={{
            left: `${imageUploadMenuPosition.x}px`,
            top: `${imageUploadMenuPosition.y}px`,
          }}
        >
          <div className="py-1">
            <button
              type="button"
              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100"
              onClick={() => {
                setImageUploadMenuOpen(false);
                // 로컬 이미지 업로드 처리
                if (imageStatusFileInputRef.current) {
                  // 현재 타겟 정보 저장
                  setImageStatusTargetPlace({
                    placeName: imageUploadMenuTarget.placeName,
                    placeNo: imageUploadMenuTarget.placeNo
                  });
                  imageStatusFileInputRef.current.click();
                }
              }}
            >
              내 PC에서 업로드
            </button>
            <button
              type="button"
              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100"
              onClick={async () => {
                setImageUploadMenuOpen(false);
                // 현재 타겟 정보 저장
                setImageStatusTargetPlace({
                  placeName: imageUploadMenuTarget.placeName,
                  placeNo: imageUploadMenuTarget.placeNo
                });
                // 공용 이미지 모달 열기
                try {
                  setImageStatusServerImgOpen(true);
                  setImageStatusServerLoading(true);
                  const { companyNo } = getUserInfo() || {};
                  const pc = await getProductCity({ productNo: productId }).catch(() => null);
                  const defaultCityNo = pc?.도시번호 ? String(pc.도시번호) : '';
                  const cityList = await getSupplierCities({ companyNo }).catch(() => []);
                  setImageStatusAllCities(Array.isArray(cityList) ? cityList : []);
                  if (defaultCityNo) {
                    setImageStatusServerCityNo(defaultCityNo);
                    // getSupplierCities returns { 코드, 코드명 } for cities
                    const cityObj = (Array.isArray(cityList) ? cityList : []).find(c => String(c.코드) === defaultCityNo);
                    if (cityObj) setImageStatusServerCityName(cityObj.코드명 || '');
                    const imgs = await getCommonImagesByCity({ cityNo: defaultCityNo });
                    setImageStatusServerImgs(Array.isArray(imgs) ? imgs : []);
                  } else {
                    setImageStatusServerImgs([]);
                  }
                } catch (err) {
                  console.error(err);
                  alert('공용 이미지를 불러오지 못했습니다.');
                } finally {
                  setImageStatusServerLoading(false);
                }
              }}
            >
              공용 이미지에서 선택
            </button>
            <button
              type="button"
              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100"
              onClick={async () => {
                setImageUploadMenuOpen(false);
                // 현재 타겟 정보 저장
                setImageStatusTargetPlace({
                  placeName: imageUploadMenuTarget.placeName,
                  placeNo: imageUploadMenuTarget.placeNo
                });
                try {
                  setImageStatusGalleryOpen(true);
                  setImageStatusGalleryLoading(true);
                  const { companyNo } = getUserInfo() || {};
                  const imgs = await getProductGallery({ companyNo, productNo: productId }).catch(() => []);
                  setImageStatusGalleryImgs(Array.isArray(imgs) ? imgs : []);
                  setImageStatusGallerySelected([]);
                } catch (e) {
                  console.error(e);
                  alert('상품 갤러리 이미지를 불러오지 못했습니다.');
                } finally {
                  setImageStatusGalleryLoading(false);
                }
              }}
            >
              상품 갤러리에서 선택
            </button>
          </div>
        </div>
      )}

      {/* 이미지 설정 모달 - 서버 이미지 선택 모달 */}
      {imageStatusServerImgOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setImageStatusServerImgOpen(false)} />
          <div className="relative z-10 w-[900px] max-w-[96vw] max-h-[88vh] bg-white rounded shadow-lg flex flex-col">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <h4 className="text-sm font-semibold">공용 이미지 선택</h4>
              <button className="text-sm text-gray-500 hover:text-gray-800" onClick={() => setImageStatusServerImgOpen(false)}>닫기</button>
            </div>
            <div className="p-4 flex items-center gap-2">
              <label className="text-xs text-gray-600">도시</label>
              <div className="relative">
                <button
                  type="button"
                  className="px-3 py-1.5 rounded border text-xs flex items-center gap-1 min-w-[120px]"
                  onClick={() => setImageStatusServerCityOpen(!imageStatusServerCityOpen)}
                >
                  <span className="flex-1 text-left">{imageStatusServerCityName || '선택'}</span>
                  <span className="text-gray-400">▼</span>
                </button>
                {imageStatusServerCityOpen && (
                  <div className="absolute top-full left-0 mt-1 w-[240px] max-h-[320px] overflow-auto bg-white border rounded shadow-lg z-10">
                    <div className="sticky top-0 p-2 border-b bg-white">
                      <input
                        type="text"
                        className="w-full px-2 py-1 text-xs border rounded"
                        placeholder="도시명 검색"
                        value={imageStatusServerCityQuery}
                        onChange={(e) => setImageStatusServerCityQuery(e.target.value)}
                      />
                    </div>
                    <div className="p-1">
                      {(() => {
                        const cities = Array.isArray(imageStatusAllCities) ? imageStatusAllCities : [];
                        const query = (imageStatusServerCityQuery || '').toLowerCase();
                        const filtered = query
                          ? cities.filter(c => {
                            const name = String(c?.코드명 || '').toLowerCase();
                            const code = String(c?.코드 || '').toLowerCase();
                            return name.includes(query) || code.includes(query);
                          })
                          : cities;

                        if (!filtered.length) {
                          return <div className="p-2 text-xs text-gray-500">검색 결과 없음</div>;
                        }

                        return filtered.map((city) => {
                          const cityNo = String(city?.코드 || '');
                          const cityName = String(city?.코드명 || '');
                          const selected = cityNo === imageStatusServerCityNo;
                          return (
                            <button
                              key={cityNo}
                              type="button"
                              className={`block w-full px-3 py-1.5 text-xs text-left hover:bg-gray-100 ${selected ? 'bg-blue-50 text-blue-600' : ''}`}
                              onClick={async () => {
                                setImageStatusServerCityNo(cityNo);
                                setImageStatusServerCityName(cityName);
                                setImageStatusServerCityOpen(false);
                                try {
                                  setImageStatusServerLoading(true);
                                  const imgs = await getCommonImagesByCity({ cityNo });
                                  setImageStatusServerImgs(Array.isArray(imgs) ? imgs : []);
                                  setImageStatusServerSelected([]);
                                } catch (err) {
                                  console.error(err);
                                  alert('도시별 공용 이미지를 불러오지 못했습니다.');
                                } finally {
                                  setImageStatusServerLoading(false);
                                }
                              }}
                            >
                              {cityName}
                            </button>
                          );
                        });
                      })()}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex-1 overflow-auto p-4 border-t border-b">
              {imageStatusServerLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-sm text-gray-500">불러오는 중...</div>
                </div>
              ) : (
                <div>
                  {!imageStatusServerImgs.length ? (
                    <div className="text-sm text-gray-500">이미지가 없습니다. 다른 도시를 선택해주세요.</div>
                  ) : (
                    <div className="grid grid-cols-6 gap-3">
                      {imageStatusServerImgs.map((img) => {
                        const selected = imageStatusServerSelected.includes(img.이미지번호);
                        return (
                          <div key={img.이미지번호} className="relative">
                            <button
                              type="button"
                              className={`block w-full aspect-square rounded border overflow-hidden hover:ring-2 hover:ring-blue-400 ${selected ? 'ring-2 ring-blue-500' : ''}`}
                              onClick={() => {
                                setImageStatusServerSelected((prev) => {
                                  const exists = prev.includes(img.이미지번호);
                                  if (exists) return prev.filter(n => n !== img.이미지번호);
                                  return [...prev, img.이미지번호];
                                });
                              }}
                            >
                              <img src={img.이미지경로2 || img.이미지경로} alt={img.이미지명 || ''} className="w-full h-full object-cover" />
                            </button>
                            {selected && (
                              <span className="absolute top-1 left-1 inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs shadow">✓</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="p-4 flex items-center justify-between">
              <div className="text-xs text-gray-600">선택됨: {imageStatusServerSelected.length}개</div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="px-3 py-1.5 rounded text-xs bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                  disabled={imageStatusServerSelected.length === 0 || imageStatusServerLoading}
                  onClick={async () => {
                    if (!imageStatusServerSelected.length || !imageStatusScheduleNo) return;
                    try {
                      setImageStatusServerLoading(true);
                      const { companyNo, userNo } = getUserInfo() || {};
                      // 1) 서버 공용이미지 -> 상품 갤러리로 복사
                      await downloadCommonImagesToProduct({
                        companyNo,
                        userNo,
                        productNo: productId,
                        imageNos: imageStatusServerSelected,
                      });
                      // 2) 복사된 이미지를 일정에 연결
                      const placeNo = Number(imageStatusTargetPlace?.placeNo || 0);
                      for (const imageNo of imageStatusServerSelected) {
                        if (placeNo > 0) {
                          await savePlaceImageFromGalleryToSchedule({ imageNo, placeNo, scheduleNo: imageStatusScheduleNo, userNo });
                        } else {
                          await saveScheduleImageFromGallery({ imageNo, scheduleNo: imageStatusScheduleNo, userNo });
                        }
                      }
                      // 3) 이미지 목록 새로고침
                      try {
                        const { companyNo } = getUserInfo() || {};
                        const supplierNo = companyNo;
                        const ds = await getSchedulePlaceImages({ scheduleNo: imageStatusScheduleNo, optionScheduleNo: -1, supplierNo, placeNo: 0 });
                        const rowsPlace = Array.isArray(ds?.table) ? ds.table : [];
                        const rowsSchedule = Array.isArray(ds?.table1) ? ds.table1 : [];
                        const groups = {};
                        for (const row of rowsPlace) {
                          const name = String(row?.장소명 || '').trim() || '장소 미지정';
                          if (!groups[name]) groups[name] = [];
                          groups[name].push(row);
                        }
                        setImageStatusPlaceGroups(groups);
                        setImageStatusScheduleRows(rowsSchedule);
                        // 업로드 후 기본 선택: 새 목록의 모든 사용자 이미지 선택
                        const scheduleNos = rowsSchedule
                          .filter(r => Number(r?.업체번호) !== -1)
                          .map(r => r.이미지번호)
                          .filter(Boolean);
                        const placeNosAll = rowsPlace
                          .filter(r => Number(r?.업체번호) !== -1)
                          .map(r => r.이미지번호)
                          .filter(Boolean);
                        const allSelected = Array.from(new Set([...scheduleNos, ...placeNosAll]));
                        setImageStatusSelected(allSelected);
                        // 미리보기 새로고침
                        await refreshScheduleImagesForCard(imageStatusScheduleNo);
                        setIframeKey((k) => k + 1);
                        // 모달 닫기
                        setImageStatusServerImgOpen(false);
                        // 선택 초기화
                        setImageStatusServerSelected([]);
                      } catch (refreshErr) {
                        console.error('이미지 목록 새로고침 실패', refreshErr);
                      }
                    } catch (err) {
                      console.error(err);
                      alert('공용 이미지 추가에 실패했습니다.');
                    } finally {
                      setImageStatusServerLoading(false);
                    }
                  }}
                >
                  선택 추가
                </button>
                <button type="button" className="px-3 py-1.5 rounded text-xs bg-gray-200 hover:bg-gray-300" onClick={() => setImageStatusServerImgOpen(false)}>닫기</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 콘텐츠: 공용 이미지 선택 모달 */}
      {contentCommonOpen && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setContentCommonOpen(false)} />
          <div className="relative z-10 w-[900px] max-w-[96vw] max-h-[88vh] bg-white rounded shadow-lg flex flex-col">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <h4 className="text-sm font-semibold">공용 이미지 선택 {contentCommonCityName ? `- ${contentCommonCityName}` : ''}</h4>
              <button className="text-sm text-gray-500 hover:text-gray-800" onClick={() => setContentCommonOpen(false)}>닫기</button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              {contentCommonLoading ? (
                <div className="text-sm text-gray-500">불러오는 중...</div>
              ) : (
                <ul className="grid grid-cols-6 gap-3">
                  {(Array.isArray(contentCommonImgs) ? contentCommonImgs : []).map((img) => {
                    const selected = contentCommonSelected.includes(img.이미지번호);
                    return (
                      <li key={`cc-${img.이미지번호}`}>
                        <button
                          type="button"
                          className={`block w-full aspect-square rounded border overflow-hidden bg-white hover:ring-2 hover:ring-emerald-400 ${selected ? 'ring-2 ring-emerald-500' : ''}`}
                          onClick={() => {
                            setContentCommonSelected(prev => prev.includes(img.이미지번호) ? prev.filter(n => n !== img.이미지번호) : [...prev, img.이미지번호]);
                          }}
                        >
                          <img src={img.이미지경로2 || img.이미지경로} alt={img.이미지명 || ''} className="w-full h-full object-cover" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <div className="px-4 py-3 border-t flex items-center justify-between">
              <div className="text-xs text-gray-600">선택됨: {contentCommonSelected.length}개</div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="px-3 py-1.5 rounded text-xs bg-emerald-600 text-white disabled:opacity-50"
                  disabled={contentCommonSelected.length === 0 || contentCommonLoading}
                  onClick={async () => {
                    if (contentCommonSelected.length === 0 || !previewModalContentNo) return;
                    try {
                      setContentCommonLoading(true);
                      const { companyNo, userNo } = getUserInfo() || {};
                      // 공용 -> 상품 갤러리 복사
                      await downloadCommonImagesToProduct({ companyNo, userNo, productNo: productId, imageNos: contentCommonSelected });
                      // 갤러리 조회 후 경로 매칭으로 이미지번호 추출
                      const gallery = await getProductGallery({ companyNo, productNo: productId });
                      const selectedSet = new Set(contentCommonSelected.map(Number));
                      const selectedPaths = new Set(
                        (contentCommonImgs || [])
                          .filter(si => selectedSet.has(Number(si.이미지번호)))
                          .map(si => si.이미지경로2)
                          .filter(Boolean)
                      );
                      const toMapImageNos = (gallery || [])
                        .filter(g => selectedPaths.has(g.이미지경로2))
                        .map(g => g.이미지번호)
                        .filter(Boolean);
                      if (toMapImageNos.length > 0) {
                        await mapImagesToContent({ contentNo: previewModalContentNo, productNo: productId, userNo, imageNos: toMapImageNos });
                        await refreshContentImages();
                      }
                      setContentCommonOpen(false);
                      setContentCommonSelected([]);
                    } catch (err) {
                      console.error(err);
                      alert('공용 이미지 추가에 실패했습니다.');
                    } finally {
                      setContentCommonLoading(false);
                    }
                  }}
                >선택 추가</button>
                <button type="button" className="px-3 py-1.5 rounded text-xs bg-gray-200 hover:bg-gray-300" onClick={() => setContentCommonOpen(false)}>닫기</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 콘텐츠: 상품 갤러리 선택 모달 */}
      {contentGalleryOpen && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setContentGalleryOpen(false)} />
          <div className="relative z-10 w-[900px] max-w-[96vw] max-height-[88vh] bg-white rounded shadow-lg flex flex-col">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <h4 className="text-sm font-semibold">상품 갤러리에서 선택</h4>
              <button className="text-sm text-gray-500 hover:text-gray-800" onClick={() => setContentGalleryOpen(false)}>닫기</button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              {contentGalleryLoading ? (
                <div className="text-sm text-gray-500">불러오는 중...</div>
              ) : (
                <ul className="grid grid-cols-6 gap-3">
                  {(Array.isArray(contentGalleryImgs) ? contentGalleryImgs : []).map((img) => {
                    const selected = contentGallerySelected.includes(img.이미지번호);
                    return (
                      <li key={`cg-${img.이미지번호}`}>
                        <button
                          type="button"
                          className={`block w-full aspect-square rounded border overflow-hidden bg-white hover:ring-2 hover:ring-emerald-400 ${selected ? 'ring-2 ring-emerald-500' : ''}`}
                          onClick={() => {
                            setContentGallerySelected(prev => prev.includes(img.이미지번호) ? prev.filter(n => n !== img.이미지번호) : [...prev, img.이미지번호]);
                          }}
                        >
                          <img src={img.이미지경로2 || img.이미지경로} alt={img.이미지명 || ''} className="w-full h-full object-cover" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <div className="px-4 py-3 border-t flex items-center justify-between">
              <div className="text-xs text-gray-600">선택됨: {contentGallerySelected.length}개</div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="px-3 py-1.5 rounded text-xs bg-emerald-600 text-white disabled:opacity-50"
                  disabled={contentGallerySelected.length === 0 || contentGalleryLoading}
                  onClick={async () => {
                    if (contentGallerySelected.length === 0 || !previewModalContentNo) return;
                    try {
                      setContentGalleryLoading(true);
                      const { userNo } = getUserInfo() || {};
                      await mapImagesToContent({ contentNo: previewModalContentNo, productNo: productId, userNo, imageNos: contentGallerySelected });
                      await refreshContentImages();
                      setContentGalleryOpen(false);
                      setContentGallerySelected([]);
                    } catch (err) {
                      console.error(err);
                      alert('갤러리 이미지 추가에 실패했습니다.');
                    } finally {
                      setContentGalleryLoading(false);
                    }
                  }}
                >선택 추가</button>
                <button type="button" className="px-3 py-1.5 rounded text-xs bg-gray-200 hover:bg-gray-300" onClick={() => setContentGalleryOpen(false)}>닫기</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Right Iframe Preview (50% width) */}
      {showPreview && (
        <div className="w-1/2 bg-gray-100 border-l shadow-inner">
          <div className="h-10 px-3 border-b flex items-center justify-between bg-white">
            <div className="flex items-center gap-2">
              <button
                type="button"
                className={`text-xs px-2 py-1.5 rounded border ${previewMode === 'pc' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white hover:bg-gray-50'}`}
                onClick={() => setPreviewMode('pc')}
                title="PC 보기"
              >PC 보기</button>
              <button
                type="button"
                className={`text-xs px-2 py-1.5 rounded border ${previewMode === 'mobile' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white hover:bg-gray-50'}`}
                onClick={() => setPreviewMode('mobile')}
                title="모바일 보기"
              >모바일 보기</button>
              <button
                type="button"
                onClick={() => setDetailLinkEmailOpen(true)}
                className="ml-2 text-xs px-2 py-1.5 rounded border bg-white hover:bg-gray-50"
              >
                <span>이메일로 보내기</span>
              </button>
              {previewMode === 'mobile' && (
                <div className="flex items-center gap-2 ml-2">
                  <select
                    className="text-xs px-2 py-1.5 rounded border bg-white"
                    value={previewDeviceId}
                    onChange={(e) => setPreviewDeviceId(e.target.value)}
                    title="디바이스 프리셋"
                  >
                    {devicePresets.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                  {previewDeviceId === 'custom' && (
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-gray-600">W
                        <input
                          type="number"
                          min={240}
                          max={1024}
                          value={customDevice.width}
                          onChange={(e) => setCustomDevice(prev => ({ ...prev, width: Number(e.target.value) || 0 }))}
                          className="w-16 ml-1 px-2 py-1 border rounded text-xs"
                          title="가로(px)"
                        />
                      </label>
                      <label className="text-xs text-gray-600">H
                        <input
                          type="number"
                          min={400}
                          max={2000}
                          value={customDevice.height}
                          onChange={(e) => setCustomDevice(prev => ({ ...prev, height: Number(e.target.value) || 0 }))}
                          className="w-16 ml-1 px-2 py-1 border rounded text-xs"
                          title="세로(px)"
                        />
                      </label>
                      <label className="text-xs text-gray-600 inline-flex items-center gap-1">
                        <input
                          type="checkbox"
                          checked={customDevice.notch}
                          onChange={(e) => setCustomDevice(prev => ({ ...prev, notch: e.target.checked }))}
                        />
                        노치
                      </label>
                      <label className="text-xs text-gray-600">radius
                        <input
                          type="number"
                          min={0}
                          max={40}
                          value={customDevice.radius}
                          onChange={(e) => setCustomDevice(prev => ({ ...prev, radius: Number(e.target.value) || 0 }))}
                          className="w-14 ml-1 px-2 py-1 border rounded text-xs"
                          title="모서리 둥글기(px)"
                        />
                      </label>
                    </div>
                  )}
                </div>
              )}
            </div>
            <a
              href={previewUrl || '#'}
              target="_blank"
              rel="noreferrer"
              className="text-xs px-2 py-1.5 rounded border bg-white hover:bg-gray-50"
            >
              <span>새 탭에서 열기</span>
            </a>
          </div>
          {/* Scale wrapper: keeps height, visually scales iframe content */}
          <div className="h-[calc(100%-2.5rem)] overflow-auto p-3">
            <div className="px-3 h-full w-full bg-white border rounded-lg shadow-sm overflow-hidden">
              {!previewUrl ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-3"></div>
                    <p className="text-sm text-gray-500">미리보기 로딩 중...</p>
                  </div>
                </div>
              ) : previewMode === 'mobile' ? (
                (() => {
                  const cacheBusted = previewUrl ? `${previewUrl}${previewUrl.includes('?') ? '&' : '?'}_=${iframeKey}` : '';
                  const preset = devicePresets.find(p => p.id === previewDeviceId) || devicePresets[0];
                  const deviceWidth = previewDeviceId === 'custom' ? customDevice.width : preset.w;
                  const deviceHeight = previewDeviceId === 'custom' ? customDevice.height : preset.h;
                  const radius = previewDeviceId === 'custom' ? customDevice.radius : (preset.radius ?? 20);
                  const hasNotch = previewDeviceId === 'custom' ? customDevice.notch : !!preset.notch;
                  return (
                    <div className="w-full h-full flex items-start justify-center py-4">
                      <div className="bg-gray-100 border rounded-[28px] p-3 shadow-inner">
                        <div className="relative bg-black rounded-[24px] p-2">
                          <div className="relative bg-white overflow-hidden" style={{ width: deviceWidth, height: deviceHeight, borderRadius: radius }}>
                            {hasNotch && (
                              <div className="absolute left-1/2 -translate-x-1/2 top-0" style={{ width: Math.max(100, deviceWidth * 0.4), height: 24, background: '#000', borderBottomLeftRadius: 12, borderBottomRightRadius: 12 }} />
                            )}
                            <iframe
                              title="detail-preview"
                              key={iframeKey}
                              src={cacheBusted}
                              style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()
              ) : (
                (() => {
                  const scale = 0.6; // 화면 배율 (60%)
                  const style = {
                    transform: `scale(${scale})`,
                    transformOrigin: 'top left',
                    width: `${100 / scale}%`,
                    height: `${100 / scale}%`,
                    border: 'none',
                    display: 'block',
                  };
                  return (
                    <iframe
                      title="detail-preview"
                      key={iframeKey}
                      src={previewUrl}
                      style={style}
                    />
                  );
                })()
              )}
            </div>
          </div>
        </div>
      )}

      <DetailLinkEmailModal
        open={detailLinkEmailOpen}
        onClose={() => setDetailLinkEmailOpen(false)}
        linkUrl={previewUrl}
        productName={productName}
      />
    </div>
  );
};

export default GeneralDetailPageSettings;
